import { ROUTE_LABELS, routeLabelDictEntries } from "@sendly/shared";

const en = {
  // Common
  "common.loading": "Loading...",
  "common.featureNotAvailable": "This feature is not available.",
  "common.cancel": "Cancel",
  "common.save": "✓ Save",
  "common.yes": "Yes",
  "common.no": "No",
  "common.saving": "Saving...",
  "common.saved": "Saved",
  "common.close": "Close",
  "common.mainNavigation": "Main navigation",
  "common.toggleFilters": "Toggle filters",
  "common.clearSearch": "Clear search",
  "common.moveUp": "Move up",
  "common.moveDown": "Move down",
  "common.delete": "Delete",
  "common.confirm": "Confirm",
  "common.ok": "OK",
  "common.typedConfirmLabel": "Type \"{word}\" to confirm",
  "common.error": "Error",
  "common.points": "pts",
  "common.routes": "routes",
  "common.back": "← Back",
  "common.retry": "Try again",
  "common.add": "+ Add",
  "common.addShort": "+ Add",
  "common.archived": "archived",
  "common.fullDescription": "Full description",
  "common.showMore": "Show more ({count})",

  // Login
  "login.title": "Sign in",
  "login.google": "Google",
  "login.apple": "Apple",
  "login.accountLinkPrompt": "An account already exists for this email with a different sign-in method. Sign in with the method you used before (Google, Apple or email) and we'll link your accounts.",
  "login.or": "or",
  "login.emailPlaceholder": "email@example.com",
  "login.send": "Send",
  "login.linkSent": "Link sent!",
  "login.checkEmailPrefix": "Check your ",
  "login.checkEmailSuffix": " inbox and click the link to sign in.",
  "login.emailHint": "You'll receive a sign-in link via email.",
  "login.emailHintPwa": "The sign-in link will open in your browser. Come back to the app after.",
  "login.confirmEmailTitle": "Confirm your email",
  "login.confirmEmailDesc": "For security, please re-enter the email address you signed in with.",
  "login.confirmEmailButton": "Confirm",
  "login.signInFailed": "Sign-in failed. Please try again.",
  "login.sendFailed": "Couldn't send the sign-in link.",
  "login.emailInvalid": "Invalid email address.",
  "login.networkError": "Network error. Check your connection.",
  "login.tooManyRequests": "Too many requests. Please wait a few minutes.",
  "login.linkInvalid": "This link has expired or is invalid. Request a new one.",
  "login.passwordSectionTitle": "Password (test only)",
  "login.passwordPlaceholder": "Password",
  "login.passwordSignIn": "Sign in",
  "login.passwordSignUp": "Sign up",
  "login.passwordTestOnlyHint": "Email + password login is enabled in the test environment only.",
  "login.passwordRequired": "Please enter a password.",
  "login.passwordWeak": "Password is too weak (at least 6 characters).",
  "login.emailInUse": "This email is already registered. Try signing in instead.",
  "login.invalidCredentials": "Invalid email or password.",
  "login.passwordAuthDisabled": "Email + password login is not enabled on this project.",

  // Tabs — `.short` variants are used on narrow viewports (≤480px). For tabs
  // whose full label already fits a 60px column ("Map", "Routes", "Admin"),
  // short equals full so the render path stays uniform.
  "tab.map": "Map",
  "tab.map.short": "Map",
  "tab.routes": "Routes",
  "tab.routes.short": "Routes",
  "tab.setters": "Setters",
  "tab.setters.short": "Setters",
  "tab.spraywall": "Spraywall",
  "tab.spraywall.short": "Spray",
  "tab.admin": "Admin",
  "tab.admin.short": "Admin",

  // Map
  "map.clickToReposition": "📍 Click the map to reposition!",
  "map.clickToPlace": "👆 Click the map: \"{name}\"",
  "map.clickToPlaceSprayWall": "👆 Tap where the wall is: \"{name}\"",
  "map.sprayWallPlaced": "\"{name}\" placed on the map",
  "map.sprayWallSaveFailed": "Something went wrong, the wall could not be placed. Please refresh the app.",
  "map.saveFailed": "Something went wrong, the route could not be saved. Please refresh the app.",
  "map.newRoute": "Add new route",
  "map.defaultName": "Map",
  "map.floors": "Floors",
  "map.searchRoutes": "Search routes",
  "map.searchPlaceholder": "Search routes...",
  "map.sortName": "Name (A→Z)",
  "map.sortDiffAsc": "Difficulty ↑",
  "map.sortDiffDesc": "Difficulty ↓",
  "map.sortRating": "Rating ★",
  "map.sortAscentsDesc": "Most climbers",
  "map.sortClimbsDesc": "Most climbs",
  "map.sortNewest": "Newest",
  "map.statusAll": "All",
  "map.statusUnclimbed": "Unclimbed",
  "map.statusClimbed": "Climbed ✓",
  "map.legendToggle": "Legend",
  "map.legendHide": "Hide legend",
  "map.legendHoldColor": "Hold color",
  "map.legendDifficulty": "Difficulty",
  "map.legendClimbed": "Climbed",
  "map.legendNewRoute": "New route",
  "map.legendComp": "Competition route (dashed orange ring + 🏆)",
  "map.legendSprayWall": "Spraywall — tap to open the wall",
  "map.legendSmall": "Zoomed out only the hold color shows — the center dot marks a climbed route, not the difficulty",
  "map.legendPoints": "Points",
  "map.legendPulsing": "pulsing ring",
  "map.legendHoldExample": "(orange)",
  "map.legendDiffExample": "(blue · 2p)",
  "map.routeCount": "{count} routes",
  "map.routes": "Routes",
  "map.showAll": "All ({count})",
  "map.noResults": "No routes match the current filters.",
  "map.noRoutesOnMap": "No routes on this map yet.",
  "map.noRoutesInGym": "No routes in this gym yet.",
  "map.filterDifficulty": "DIFFICULTY",
  "map.filterStatus": "STATUS",
  "map.filterComp": "Competition routes only",

  // Filters
  "filter.all": "All",
  "filter.clearAll": "Clear all",
  "filter.sheetTitle": "Filters and sort",
  "filter.sortLabel": "Sort",
  "filter.removeChip": "Remove {name} filter",
  "filter.showResults": "Show {count} routes",

  // Toast
  "toast.undo": "Undo",
  "toast.sendLogged": "Send: {name}",
  "toast.attemptLogged": "+{count} attempts · {name} ({today} today)",
  "toast.repeatLogged": "Climbed again: {name}",
  "toast.climbCooldown": "Just logged: {name}",
  "toast.climbFailed": "Could not log that climb. Please try again.",

  // Routes management
  "routes.active": "✅ Active",
  "routes.archived": "📦 Archived",
  "routes.all": "📋 All",
  "routes.empty": "No routes found.",
  "routes.loadMoreArchived": "Load more archived",
  "routes.showOnMap": "Show on map",
  "routes.inCompetition": "competition",
  "routes.watching": "You are watching this route",
  "routes.searchPlaceholder": "Search route...",
  "routes.setterFilter": "Setter",
  "routes.filtersLabel": "Filters",
  "routes.filterRated": "Rated",
  "routes.filterGradeVote": "Grade vote",

  // Leaderboard
  "lb.empty": "No ascents yet.",

  // Setters
  "setters.empty": "No setters yet.",
  "setters.totalRoutes": "routes total",
  "setters.active": "active",
  "setters.activeRoutes": "Active routes",
  "setters.archivedRoutes": "Archived routes",
  "setters.noRating": "no ratings",
  "setters.unknown": "Unknown setter",
  "setters.sprayAuthors.title": "Spraywall authors",
  "setters.sprayAuthors.problems": "{count} routes",
  "setters.sprayAuthors.sends": "{count} sends",
  "setters.sprayAuthors.rating": "Average rating: {avg} ({count} ratings)",
  "setters.sprayAuthors.benchmarks": "{count} benchmarks",
  "setters.sprayAuthors.empty": "No public spray wall routes yet.",

  // Admin
  "admin.branding": "🎨 Branding",
  "admin.replaceLogo": "Replace logo",
  "admin.replaceCover": "Replace cover",
  "admin.coverHint": "Recommended: {w}×{h} px",
  "admin.stats": "📊 Statistics",
  "admin.nav.overview": "Overview",
  "admin.nav.community": "Community",
  "admin.nav.gym": "Gym",
  "admin.nav.account": "Account & data",
  "admin.activeRoutes": "Active routes",
  "admin.users": "Users",
  "admin.climbs": "Climbs",
  "admin.ascents": "Ascents",
  "admin.mau": "MAU",
  "admin.usersTitle": "👥 Users",
  "admin.searchUsers": "Search by name or email...",
  "admin.userRow.role": "Role",
  "admin.search.placeholder": "Search settings…",
  "admin.sidebar.collapse": "Collapse menu",
  "admin.sidebar.expand": "Expand menu",
  "admin.search.noResults": "No results for \"{query}\"",
  "admin.needsSetup": "Set up",
  "onboarding.hero.titleNamed": "Welcome to Sendly, {gymName}! 👋",
  "onboarding.hero.titleGeneric": "Welcome to Sendly! 👋",
  "onboarding.hero.subtitle": "A few steps and your gym is live. Estimated time: ~{minutes} minutes. You can stop anytime — your progress is saved.",
  "onboarding.hero.dismiss": "Hide",
  "onboarding.hero.dismissHint": "Won't appear again for this gym",
  "onboarding.hero.dismissConfirm": "Hide the checklist? You won't see it again for this gym — only the \"Set up\" pills on the rows will keep nudging you about pending steps.",
  "onboarding.hero.foot": "{done}/{total} done — pick up where you left off anytime.",
  "onboarding.cta.continue": "Continue →",
  "onboarding.cta.locked": "Finish the basics first",
  "onboarding.compact.title": "Getting started",
  "onboarding.compact.next": "Continue: {step}",
  "onboarding.minutes": "~{n}m",
  "onboarding.step.created.title": "Gym created",
  "onboarding.step.profile.title": "Basics & gym type",
  "onboarding.step.profile.sub": "Description, address, GPS, email, phone",
  "onboarding.step.branding.title": "Appearance",
  "onboarding.step.branding.sub": "Logo and accent color",
  "onboarding.step.maps.title": "Upload a map",
  "onboarding.step.maps.sub": "At least one floor with an image",
  "onboarding.step.difficulty.title": "Difficulty & hold colors",
  "onboarding.step.difficulty.sub": "Set a grade range for every level",
  "onboarding.step.routes.title": "Add your first routes",
  "onboarding.step.routes.sub": "At least 3 active routes so climbers have something to send",
  "onboarding.step.team.title": "Invite your team",
  "onboarding.step.team.sub": "Setters, coaches, receptionists (optional)",
  "onboarding.step.hours.title": "Set opening hours",
  "onboarding.step.hours.sub": "Specify all 7 days and the timezone",
  "onboarding.step.billing.title": "Billing details",
  "onboarding.step.billing.sub": "Company name and tax number for the quarterly invoice",
  "onboarding.step.publish.title": "Go live & list publicly",
  "onboarding.step.publish.sub": "Listed in the Sendly directory with a shareable link",
  "admin.sub.competitions": "{active} active · {upcoming} upcoming",
  "admin.sub.events": "{active} active · {draft} draft",
  "admin.sub.series": "{active} active · {closed} closed",
  "admin.sub.usersSimple": "{count} members",
  "admin.sub.usersSimpleOne": "{count} member",
  "admin.sub.maps": "{count} floors",
  "admin.sub.mapsOne": "{count} floor",
  "admin.sub.tv": "{count} screens",
  "admin.sub.tvOne": "{count} screen",
  "admin.sub.tvNone": "Not configured",
  "admin.sub.diff": "{levels} grades · {colors} hold colors",
  "admin.sub.stats": "{routes} active routes",
  "admin.sub.audit": "Access changes and bans",
  "admin.sub.profile": "Name, description, contact",
  "admin.sub.branding": "Logo · color · cover",
  "admin.sub.display": "Opening hours, highlight, visibility",
  "admin.sub.pwa": "Share link and QR code",
  "admin.sub.widget": "Embeddable opening hours",
  "admin.sub.export": "Download a JSON snapshot",
  "admin.sub.maintenance": "Delete data (danger zone)",
  "admin.sub.subscription": "{plan} plan · MAU {mau}/{max}",
  "admin.sub.subscriptionPlan": "{plan} plan",
  "admin.usersCount": "{count} users",
  "admin.showMore": "Show more",
  "admin.kickTitle": "Kick",
  "admin.reportName.button": "Report",
  "admin.reportName.title": "Report name for moderation",
  "admin.reportName.confirm": "Report {name}'s display name to Sendly platform moderation? A platform admin makes the decision.",
  "admin.reportName.done": "Name reported for moderation",
  "admin.mapsTitle": "🗺️ Maps / Floors",
  "admin.mapsDesc": "Add more floors or zones. Routes are always placed on the active map.",
  "admin.replaceImage": "🔄 Replace image",
  "admin.uploadImage": "📷 Upload image",
  "admin.hasImage": "✓ Has image",
  "admin.width": "Width",
  "admin.height": "Height",
  "admin.newFloorPlaceholder": "New floor name…",
  "admin.deleteMapConfirm": "Are you sure you want to delete this map?",
  "admin.displayTitle": "⚙️ General",
  "admin.pwa.title": "📱 PWA & QR code",
  "admin.pwa.gymLink": "Gym link",
  "admin.pwa.qrCode": "QR code",
  "admin.pwa.generateQr": "🔲 Generate QR code",
  "admin.pwa.regenerateQr": "🔄 Regenerate",
  "admin.pwa.downloadQr": "⬇️ Download PNG",
  "admin.pwa.qrHint": "Print this QR code and place it in your gym — visitors can scan it to open the app directly.",

  "admin.tvTitle": "📺 TV settings",
  "admin.slideDuration": "Slide interval",
  "admin.seconds": "sec",
  "admin.newRouteHighlight": "New route highlight period",
  "admin.days": "days",
  "admin.tvOledProtection": "OLED burn-in protection",
  "admin.tvOledProtectionHint": "Pixel shift + slight brightness reduction to prevent burn-in. For best results, use Dark theme.",
  "admin.tvTheme": "TV theme",
  "admin.tvLanguage": "TV language",
  "admin.themeDark": "🌙 Dark",
  "admin.themeLight": "☀️ Light",
  "admin.themeSystem": "📱 System",
  "admin.logo": "Logo",
  "admin.uploadLogo": "📷 Upload logo",
  "admin.difficultyLevels": "Difficulty levels",
  "admin.diffPoints": "Points:",
  "admin.diffDeleteTitle": "Delete difficulty level",
  "admin.diffDeletePromptBody": "{count} active routes use this level. Archive them or delete them?\n\nExisting ascents stay (on archived routes too), but their level can no longer be resolved, so that slice counts 0 points in rankings and statistics.",
  "admin.diffRetroNotice": "Points and the grade range apply retroactively: changing them re-evaluates every past ascent in this gym and recalculates every member's total. Colour and label only change how a level looks.",
  "admin.diffReweightConfirmTitle": "Retroactive re-evaluation",
  "admin.diffReweightConfirmBody": "The \"{label}\" level changes from {from} to {to} points.\n\nThis applies retroactively: every past ascent in this gym is re-evaluated and the totals of {members} members are recalculated. The level currently has {routes} active routes.\n\nYou will not be asked again during this edit.",
  "admin.diffGradeRangeConfirmBody": "The grade range of the \"{label}\" level moves the cross-gym normalised score — retroactively, for every past ascent of {members} members. Clearing the range drops their global profile back to raw points for this gym. The level currently has {routes} active routes.\n\nYou will not be asked again during this edit.",
  "admin.diffUnsavedHint": "Unsaved value — press Enter or click elsewhere to save",
  "admin.newDiffPlaceholder": "New level name...",
  "admin.diffDeleteNote": "After deletion, existing routes remain but the removed level won't be shown.",
  "admin.diffNoSetForDiscipline": "This discipline has no difficulty set of its own yet — its routes use the shared levels. Add levels here, or switch the discipline off and back on in the Gym profile section to copy the other discipline's ladder.",
  "admin.holdColors": "Hold colors",
  "admin.holdDeleteTitle": "Delete hold color",
  "admin.holdDeletePromptBody": "{count} active routes use this color. Archive them or delete them?",
  "admin.newHoldPlaceholder": "New color name...",
  "admin.dbTitle": "Database maintenance",
  "admin.dbDesc": "Delete routes, ascents, and ratings. This action cannot be undone!",
  "admin.purge.keepYear.label": "📅 Keep current year",
  "admin.purge.keepYear.desc": "Deletes routes, ascents, and ratings from before {year}.",
  "admin.purge.keep12m.label": "🕐 Keep last 12 months",
  "admin.purge.keep12m.desc": "Deletes routes, ascents, and ratings older than 12 months.",
  "admin.purge.archived.label": "📦 Delete archived routes",
  "admin.purge.archived.desc": "Deletes archived routes and their related ascents/ratings.",
  "admin.purge.ascents.label": "🧗 Delete ascents only",
  "admin.purge.ascents.desc": "Deletes all ascents (routes and ratings remain).",
  "admin.purge.ratings.label": "⭐ Delete ratings only",
  "admin.purge.ratings.desc": "Deletes all ratings (routes and ascents remain).",
  "admin.purge.competitions.label": "🏆 Delete competitions & series",
  "admin.purge.competitions.desc": "Deletes all competitions, series, and their data (groups, sessions, registrations, attempts).",
  "admin.purge.competitionsResult": "Deleted {count} competitions and all related series.",
  "admin.purge.all.label": "💣 Delete everything",
  "admin.purge.all.desc": "Deletes all routes, ascents, and ratings. Clean slate.",
  "admin.purge.confirmAll": "WARNING! This deletes EVERYTHING (routes, ascents, ratings).\n\nType 'DELETE' to confirm.",
  "admin.purge.confirmOther": "Are you sure?\n\n{desc}\n\nType 'DELETE' to confirm.",
  "admin.purge.confirmWord": "DELETE",
  "admin.purge.title": "Database maintenance",
  "admin.purge.confirmButton": "Delete",
  "admin.purge.confirmTypeLabel": "Type \"{word}\" to confirm:",
  "admin.purge.result": "Deleted: {routes} routes, {ascents} ascents, {ratings} ratings.",

  // Settings
  "settings.profile": "Profile",
  "settings.appearance": "Appearance",
  "settings.scoring": "Scoring",
  "settings.dataAndLegal": "Data & Legal",
  "settings.billingSubtitleEmpty": "No billing info saved yet",
  "settings.displayName": "Display name",
  "settings.nameRateLimit": "You've changed your name too often. Please try again later.",
  "settings.nameRevert": "Restore previous",
  "settings.nameNeedsLetters": "A name needs at least 2 letters.",
  "settings.avatarUseMonogram": "Switch to monogram avatar",
  "settings.avatarUsePhoto": "Switch to photo avatar",

  // Sign-in section (profile settings → 🔐 Sign-in)
  "security.title": "Sign-in",
  "security.emailTitle": "Account email address",
  "security.emailPickSub": "Pick which of your addresses this should be. Only addresses you can also sign in with are offered.",
  "security.addressOrphan": "No sign-in method uses it",
  "security.meaningTitle": "What this means:",
  "security.meaningMail": "Sendly's emails arrive here",
  "security.meaningFriends": "Friends find you at this address",
  "security.meaningLink": "Passwordless sign-in links arrive here too",
  "security.methodsSub": "Any of these signs you into the same account.",
  "security.linkRowSub": "always the account address",
  "security.disableAction": "Turn off",
  "security.methodsTitle": "How you can sign in",
  "security.methodGoogle": "Google",
  "security.methodApple": "Apple",
  "security.methodEmailLink": "Email link",
  "security.noAddress": "No address for this method",
  "security.methodsHint": "Any of these signs you into the same account. Google and Apple identify you by their own address, but a magic link ALWAYS arrives at the account email address — it has no separate address of its own. Your ascents, memberships and friends belong to the account, not to a sign-in method.",
  "security.notLinkedSub": "Not connected to your account",
  "security.enableAction": "Enable",
  "security.emailPlaceholder": "new@address.com",
  "security.emailSwitched": "Account address is now {email}",
  "security.makePrimaryAction": "Set",
  "security.makePrimaryConfirmLink": "The account email address becomes {email}. Since magic links always arrive at the account address, your sign-in link will be sent to {email} instead of {current} — you will no longer be able to sign in with {current}.",
  "security.emailInUseHelp": "If that other account is yours and you no longer need it, sign in with it and delete it under Settings → Delete account — that frees up this address. Deletion is permanent: the ascents, memberships and friends on that account are lost.",
  "security.enableFieldLabel": "Which address?",
  "security.enableHint": "We'll send a confirmation link to this address. It's the only way a new address gets onto the account, so it becomes the account email address once enabled — you can switch back in the list below.",
  "security.enableSend": "Send link",
  "security.linkSent": "Confirmation link sent to {email}",
  "security.linkAwaiting": "Awaiting confirmation — open the link in your email.",
  "security.linkAwaitingShort": "Sent",
  "security.linkAwaitingTitle": "Awaiting confirmation: {email}",
  "security.linkAwaitingHint": "Open the link in the email sent to that address, on this same device — passwordless sign-in switches on afterwards.",
  "security.linkAwaitingCancel": "Cancel — use a different address",
  "security.linkAction": "Connect",
  "security.unlinkAction": "Disconnect",
  "security.unlinkConfirm": "Disconnect {method} sign-in? You won't be able to sign in with it anymore, but your account, ascents and memberships stay — and you can reconnect it any time.",
  "security.lastMethodHint": "At least one sign-in method must remain.",
  "security.linked": "Connected",
  "security.unlinked": "Disconnected",
  "security.appleRelayTitle": "Hidden Apple email address",
  "security.appleRelayHint": "This address disappears together with your Apple account. If you're in it for the long run, set up one of your own addresses as a sign-in method too.",

  // Sign-in section — errors surfaced by mapAuthError (utils/authIdentities.js)
  "security.errEmailInUse": "This address already belongs to a Sendly account.",
  "security.errCredentialInUse": "This sign-in method already belongs to another Sendly account.",
  "security.errRecentLogin": "For security, sign in again and then retry.",
  "security.errNotLinked": "This sign-in method isn't linked to your account.",
  "security.errGeneric": "That didn't work. Please try again.",
  "moderation.cat.profanity": "Contains profane or offensive language.",
  "moderation.cat.sexual": "Contains sexual content or innuendo.",
  "moderation.cat.threat": "Contains a threat.",
  "moderation.cat.hate": "Contains hateful content.",
  "moderation.cat.harassment": "Contains harassing or insulting content.",
  "moderation.cat.impersonation": "This looks like it impersonates an official role.",
  "moderation.cat.generic": "Contains content that isn't allowed.",
  "moderation.nameReset.title": "Your display name was changed",
  "moderation.nameReset.body": "Sendly moderation reset your display name to “{name}”. You can set a new one anytime in Settings.",
  "moderation.nameReset.byPlatform": "This was handled by Sendly platform moderation — not your gym.",
  "moderation.nameReset.noteLabel": "Note",
  "settings.birthYear": "Birth year",
  "settings.birthYearPlaceholder": "e.g. 1998",
  "settings.birthYearHint": "Used for age-category competition and event registration",
  "settings.birthYearInvalid": "Enter a valid year ({min}–{max})",
  "settings.gender": "Gender",
  "settings.genderFemale": "Female",
  "settings.genderMale": "Male",
  "settings.genderOther": "Other",
  "settings.genderNoAnswer": "Prefer not to say",
  "settings.genderHint": "Optional — used for anonymized statistics.",
  "settings.theme": "Theme",
  "settings.language": "Language",
  "settings.haptics": "Haptic feedback",
  "settings.hapticsHint": "A short vibration to confirm key actions (where the device supports it — not available on iPhone).",
  "settings.notif.title": "Notifications",
  "settings.notif.device": "On this device",
  "settings.notif.enableDevice": "Enable push notifications",
  "settings.notif.disableDevice": "Disable push notifications",
  "settings.notif.enabled": "Device registered",
  "settings.notif.permissionDenied": "Notifications are blocked in the browser. Allow them in the browser's site settings.",
  "settings.notif.unsupportedHint": "This device/browser doesn't support push notifications. On iOS it works in an installed PWA (Add to Home Screen).",
  "settings.notif.noVapid": "Missing VAPID key (VITE_FB_VAPID_KEY) — push is not configured.",
  "settings.notif.error": "Could not register: {code}",
  "settings.notif.group.community": "User / community",
  "settings.notif.group.gymAdmin": "Gym – admin",
  "settings.notif.group.platform": "Platform",
  "settings.notif.group.moderation": "Moderation",
  "settings.notif.pref.friendRequests": "Friend request (received / accepted)",
  "settings.notif.pref.watchedRoute": "Watched route (ascent, rating)",
  "settings.notif.pref.gymNewRoutes": "New routes in your gym (batched)",
  "settings.notif.pref.gymNewComp": "New competition in your gym",
  "settings.notif.pref.eventWaitlist": "Event: you got off the waitlist",
  "settings.notif.pref.eventReminder": "Event: day-before reminder",
  "settings.notif.pref.gymRouteFreshness": "No new route in a while (3 weeks)",
  "settings.notif.pref.mauBand": "MAU band crossed",
  "settings.notif.pref.gymNewMember": "New member in your gym (join/leave)",
  "settings.notif.pref.newGym": "New gym (created / activated / visibility)",
  "settings.notif.pref.platformMauBand": "Gym MAU band crossed",
  "settings.notif.pref.crashSpike": "Crash alert (crash spike)",
  "settings.notif.pref.nameReview": "Name awaiting review",
  "settings.notif.pref.sprayProblemComments": "Comment on my spray route",
  "settings.notif.pref.sprayReports": "Spray reports",
  "settings.notif.gymScopedElsewhere": "Per-gym notifications (new routes, competitions, watched routes) are configured gym by gym in the My gyms section.",
  "settings.notif.hint": "Turn on the types you want to be notified about.",
  "settings.notif.enableFirst": "Enable push notifications on this device first.",
  "settings.gradePreference": "Grade scale",
  "settings.gradeFont": "Font (6a, 6b+, ...)",
  "settings.gradeV": "V-grade (V3, V4, ...)",
  "settings.weeklyGoal": "Weekly goal",
  "settings.weeklyGoalHint": "Set a weekly target to track your progress. 0 = disabled.",
  "settings.weeklyGoalPoints": "Points",
  "settings.weeklyGoalClimbs": "Climbs",
  "settings.weeklyGoalUnitPoints": "points / week",
  "settings.weeklyGoalUnitClimbs": "climbs / week",
  "settings.weeklyGoalPlaceholder": "e.g. 50",

  // GymSelector menu
  "gymSelector.menuHelp": "Help",
  "gymSelector.menuFeedback": "Write to Sendly",
  "gymSelector.feedbackSubject": "Sendly feedback (v{version})",
  "gymSelector.feedbackBody": "Hi Sendly team,\n\n[Write your feedback here]\n\n\n---\nVersion: {version}\n",
  "gymSelector.menuContactGym": "Write to the gym",
  "gymSelector.contactGymSubject": "Message from the Sendly app – {gym}",
  "gymSelector.contactGymBody": "Hi,\n\n[Write your message here]\n\n\n---\nSent from the Sendly app\n",
  "gymSelector.menuPlatformAdmin": "Platform Admin",
  "gymSelector.menuLogout": "Sign out",

  // Stats tab
  "stats.title": "Statistics",
  "stats.title.short": "Stats",
  "stats.totalPoints": "Total points",
  "stats.climbedRoutes": "Climbed routes",
  "stats.activityCalendar": "Active days",
  "stats.heatmapHint": "The last 12 weeks — every climbing activity counted. Does not follow the period selector.",
  "stats.heatmapClimbs": "climbs",
  "stats.heatmapSpray": "spray",
  "stats.heatmapAttempts": "project",
  "stats.hardest": "Hardest",
  "stats.personalStats": "Personal statistics",
  "stats.gradePyramid": "Grade pyramid",
  "stats.gradePyramid.idealMarker": "Ideal pyramid",
  "stats.activity": "Activity",
  "stats.completion": "Completion",
  "stats.completionHint": "Based on the currently active routes",
  "stats.streak": "{count} week streak!",
  "stats.streakBroken": "Your last streak: {count} weeks",
  "stats.noStreak": "Climb this week to start a streak!",
  // SmartPeriodPill — universal period selector control
  "period.label": "Period",
  "period.prev": "Previous",
  "period.next": "Next",
  "period.notCurrent": "Not the current period — tap to jump back",
  "period.quickPicks": "Quick picks",
  "period.customRange": "Custom range",
  "period.pickEndDate": "Pick the end date ({d})",
  "period.type.weekly": "Weekly",
  "period.type.monthly": "Monthly",
  "period.type.yearly": "Yearly",
  "period.type.all": "All time",
  "period.type.custom": "Custom",
  "period.preset.today": "Today",
  "period.preset.yesterday": "Yesterday",
  "period.preset.thisWeek": "This week",
  "period.preset.lastWeek": "Last week",
  "period.preset.thisMonth": "This month",
  "period.preset.lastMonth": "Last month",
  "period.preset.thisYear": "This year",
  "period.preset.lastYear": "Last year",
  "period.preset.last7": "Last 7 days",
  "period.preset.last30": "Last 30 days",
  "period.preset.last90": "Last 90 days",
  "period.preset.lastNDays": "Last {n} days",
  "period.dow.mon": "Mon",
  "period.dow.tue": "Tue",
  "period.dow.wed": "Wed",
  "period.dow.thu": "Thu",
  "period.dow.fri": "Fri",
  "period.dow.sat": "Sat",
  "period.dow.sun": "Sun",
  "period.sinceJoined": "Since you joined",
  "period.beforeJoinDisabled": "Periods before you joined are not shown",
  "stats.compare": "Compare",
  "stats.compareFriend": "Compare with",
  "stats.commonAscents": "{count} common ascents",
  "stats.flashCount": "Flash",
  "stats.flashInBrackets": "(flash)",
  "stats.flashRate": "Flash rate",
  "stats.climbingLevel": "Climbing level",
  "stats.topNAverage": "avg of {count} hardest",
  "stats.intensityZones": "Intensity zones",
  "stats.zoneWarmup": "Warmup",
  "stats.zoneHard": "Hard",
  "stats.zoneLimit": "Limit",
  "stats.intensityZonesHint": "{count} climbs, measured against your hardest ascent · limit = your top level, hard = 1–2 levels below",
  "stats.intensityZonesHintSends": "{count} ascents, measured against your hardest ascent · limit = your top level, hard = 1–2 levels below",
  "stats.progression": "Progression",
  "stats.progressionPoints": "Points",
  "stats.progressionMaxGrade": "Max grade",
  "stats.progressionClimbs": "Climbs",
  "stats.progressionWeeks12": "12 weeks",
  "stats.progressionWeeks26": "26 weeks",
  "stats.progressionWeeks52": "52 weeks",
  "stats.weeklyGoal": "Weekly goal",
  "stats.goalReached": "Goal reached!",
  "quickLog.toggle": "⚡ Quick log",
  "archiveMode.toggle": "Archive mode",
  "archiveMode.armed": "Archive mode — tap a route",
  "archiveMode.armedCount": "Archived: {count} routes",
  "archiveMode.archived": "Archived: {name}",
  "archiveMode.alreadyArchived": "This route is already archived",
  "archiveMode.failed": "Could not archive: {name}",
  "archiveMode.done": "Done",
  "quickLog.counter": "{climbs} climbs · {sends} new",
  "quickLog.cooldown": "{name} — just logged",
  "quickLog.undo": "↩ Undo",
  "quickLog.done": "Done",
  "quickLog.todayTitle": "Today · {count} climbs",
  "quickLog.removeOne": "Remove one climb",
  "activity.title": "Activity",
  "activity.climbs": "Climbs",
  "activity.sessions": "Sessions",
  "activity.perSession": "Ø {count} / session",
  "activity.newRoutes": "New routes",
  "activity.longestSession": "Longest session",
  "activity.volumeByGrade": "Volume by grade",
  "activity.empty": "No climbs logged in this period yet.",
  "activity.hint": "Counts every climb — first ascents, repeats, spray sends and spray repeats alike.",
  "activity.repeats": "Repeats",
  "activity.perDay": "Ø climbs / day",
  "stats.timeline": "Recent days",
  "stats.timelineEmpty": "Nothing logged yet.",
  "stats.timelineDaySummary": "{climbs} climbs · {sends} new",
  "stats.timelineDaySpray": "incl. {count} spray",
  "stats.timelineDayAttempts": "{count} tries",
  "stats.timelineProject": "P {name} · {count} tries",
  "stats.sprayBenchmarkNote": "Spray: benchmark problems only",
  "stats.timelineSpraySection": "Spraywall",
  "stats.timelineSprayFlash": "flash",
  "stats.timelineSprayRepeat": "repeat",
  "stats.timelineSprayProject": "project, {count} tries",
  "stats.timelineSprayProjectNoCount": "project",
  "stats.timelineSprayDeleted": "deleted route",
  "stats.timelineSprayUnavailable": "unavailable route",
  "stats.leaderboard": "Leaderboard",
  "stats.you": "You",

  // Route modal
  "route.flashBadge": "⚡ Flash",
  "route.hold": "hold",
  "route.setter": "🔧 Set by:",
  "route.addedBy": "Added by:",
  "route.watch": "Watch route",
  "route.unwatch": "Stop watching",
  "route.lastEditedBy": "Last edited by:",
  "route.unknownUser": "unknown",
  "route.climbersExpand": "+{count} more — tap to expand the full list",

  // User stats modal (climber chip click target)
  "userStats.memberSince": "Joined:",
  "userStats.noAscents": "No ascents in this gym yet.",
  "userStats.totalAscents": "Ascents",
  "userStats.totalClimbs": "Climbs",
  "userStats.repeatsHint": "{count} repeats",
  "userStats.totalPoints": "Points",
  "userStats.flashCount": "Flash",
  "userStats.activeDays30": "Active days (30)",
  "userStats.currentStreak": "Current streak",
  "userStats.weeks": "weeks",
  "userStats.bestStreakHint": "Best: {count} weeks",
  "userStats.difficultyBreakdown": "By difficulty",
  "userStats.recentAscents": "Recent ascents",
  "userStats.openProfile": "View profile",
  "userStats.loadError": "Failed to load stats.",
  "route.ascentsCount": "🧗 Ascents:",
  "route.climbAgain": "Again",
  "log.today": "Today",
  "log.empty": "You haven't logged this route today.",
  "log.emptyArchived": "Archived route — it can't be logged.",
  "log.emptyDay": "No entries.",
  "log.flashButton": "Flash — first try",
  "log.sendNth": "✓ Send · try {n}",
  "log.hint.fresh": "⚡ = first try. If you tried but didn't log it: tap Send!, then tap the ? to say which try it was.",
  "log.hint.project": "Wrong tap? Tap the dot.",
  "log.hint.sent": "Tap a dot or a day to fix it.",
  "log.unknownCount.text": "We don't know which try it was.",
  "log.unknownCount.action": "Set it ›",
  "log.dot.try": "Try {n}",
  "log.dot.tryGroup": "{count} tries",
  "log.dot.unknown": "We don't know which try",
  "log.dot.send": "Send",
  "log.dot.flash": "Flash",
  "log.dot.repeat": "Repeat",
  "log.dot.repeatGroup": "{count} repeats",
  "log.sel.try": "Try {n}",
  "log.sel.tryGroup": "{count} tries",
  "log.sel.repeatGroup": "{count} repeats",
  "log.sel.tryNote": "The day's try count goes down by one.",
  "log.sel.tryGroupNote": "Delete takes one try off: the day's try count goes down by one.",
  "log.sel.repeatGroupNote": "Delete takes off the latest repeat ({time}).",
  "log.sel.sendNote": "Its {count} repeats are deleted with it, and the points are taken back.",
  "log.sel.sendNoteNoRepeats": "The points are taken back.",
  "log.sel.delete": "Delete",
  "log.sel.deleteDay": "Whole day ({count})",
  "log.sel.askCount": "Which try?",
  "log.chart.try": "try",
  "log.chart.send": "send",
  "log.chart.repeat": "repeat",
  "log.chart.tapHint": "tap a bar",
  "log.chart.tapBubble": "Tap a bar.",
  "log.chart.older": "Earlier days",
  "log.chart.newer": "Later days",
  "log.journal.title": "Log",
  "log.journal.open": "Log ›",
  "log.journal.sub": "{name} · {entries} entries, {days} days",
  "log.journal.tries": "{count} tries",
  "log.journal.repeats": "{count} repeats",
  "log.journal.deleteAll": "Delete whole log…",
  "log.journal.done": "Done",
  "log.journal.confirmTitle": "Delete the whole log?",
  "log.journal.confirmBody": "All {count} entries of \"{name}\" will be deleted{detail}. As if you had never climbed it. This cannot be undone.",
  "log.journal.confirmSend": "the send",
  "log.count.title": "Which try did you send it on?",
  "log.count.earlier": "{count} tries before it on other days",
  "log.count.flash": "⚡ First try — Flash",
  "log.count.more": "More?",
  "log.count.ok": "OK",
  "log.count.decrease": "Fewer",
  "log.count.increase": "More",
  "log.count.hint": "One tap on a number and it's saved. The tries before the send show as amber dots on the send's day.",
  "log.count.hintUnknown": "If you don't know, close it — the \"?\" stays (unknown).",
  "help.info": "Explanation",
  "help.gotIt": "Got it",
  "help.info.logButtons": "Explanation of the buttons",
  "help.info.opinion": "Explanation of the opinion",
  "help.info.chart": "Explanation of the chart",
  "help.info.count": "Explanation of the tries count",
  "help.info.footRule": "Explanation of the foot rule",
  "help.info.startHold": "Explanation of the start",
  "route.send": "✓ Send!",
  "route.tried": "Tried",
  "route.whatIsSendBody": "Send = climbing the whole route without a fall, from the bottom to the top.",
  "route.gradeOpinion": "How hard do you find it?",
  "route.gradeOpinion.hint": "Only the setters see this. It does not change the points. You can change it later; tap the selected one to take it back.",
  "route.gradeOpinion.easier": "Easier",
  "route.gradeOpinion.fair": "Fair",
  "route.gradeOpinion.harder": "Harder",
  "route.gradeOpinionLean.easier": "Rather easier",
  "route.gradeOpinionLean.harder": "Rather harder",
  "route.gradeOpinionTitle": "Difficulty opinion",
  "route.whatIsSend": "What is a send?",
  "route.attemptsCount": "{count} attempts",
  "route.logRow.repeat": "Repeat",
  "route.deleteProject": "Delete project day",
  "route.deleteProjectTitle": "Delete the project day?",
  "route.deleteProjectConfirm": "The project day of {date} ({attempts} attempts) will be deleted.",
  "route.deleteSend": "Delete send",
  "route.deleteSendTitle": "Delete the send?",
  "route.deleteSendConfirm": "The send of \"{name}\" will be deleted.",
  "route.deleteSendConfirmRepeats": "Its {count} repeats are deleted with it.",
  "route.deleteSendConfirmKeepProject": "The project days stay, and it becomes a project again.",
  "route.deleteRepeat": "Delete repeat",
  "route.deleteRepeatTitle": "Delete the repeat?",
  "route.deleteRepeatConfirm": "One repeat will be deleted: {when}.",
  "route.deleteLogTitle": "Delete the whole log?",
  "route.deleteLogConfirm": "Every entry of \"{name}\" will be deleted ({count} entries). This cannot be undone.",
  "route.rating": "Rating",
  "rating.group": "Rating",
  "rating.star": "{count} of 5 stars",
  "rating.display": "{label}: {value} of 5",
  "route.commentPlaceholder": "Comment (only setters see)...",
  "route.rateLimitError": "You've edited this comment too many times. Please try again later.",
  "route.updateRating": "Update rating",
  "route.saveRating": "Save rating",
  "route.removeRating": "Remove rating",
  "route.stats": "Stats",
  "route.climbedBadge": "Climbed",
  "route.edit": "✏️ Edit",
  "route.name": "Name",
  "route.setterLabel": "Setter",
  "route.difficulty": "Difficulty",
  "route.holdColor": "Hold color",
  "route.saveEdit": "💾 Save",
  "route.reposition": "📍 Reposition",
  "route.repositionArchivedHint": "Archived routes can't be repositioned — activate first.",
  "route.archive": "📦 Archive",
  "route.activate": "Activate",
  "route.activateConfirm": "Are you sure you want to reactivate this route?",
  "route.delete": "Delete",
  "route.deleteConfirm": "Permanently delete this route? This also removes its ascents, attempts and ratings — cannot be undone.",
  "route.difficultyNone": "— Not set —",
  "route.holdColorNone": "— Not set —",
  "route.ungradedNoPoints": "Ungraded routes receive no points",
  "route.ungraded": "Ungraded",
  "route.noHoldColor": "No color",
  "route.ungradedShort": "—",
  "legend.unsetRoute": "Ungraded / uncolored route",

  // Route builder
  "builder.title": "🔧 Add new route",
  "builder.clickMap": "Click the map to place it!",
  "builder.namePlaceholder": "E.g. Yellow lightning (optional)",
  "builder.setterPlaceholder": "Setter name (optional)",
  "builder.holdColorLabel": "Hold color",
  "builder.place": "Place →",

  // TV Dashboard
  "tv.loading": "Loading...",
  "tv.disabled": "TV Dashboard is not available.",
  "tv.noAscents": "No ascents in this period yet.",
  "tv.weeklyLeaderboard": "Weekly leaderboard",
  "tv.monthlyLeaderboard": "Monthly leaderboard",
  "tv.alltimeLeaderboard": "All-time leaderboard",
  "tv.todayAscents": "Today's ascents",
  "tv.map": "🗺️ Map",
  "tv.activeRoutes": "active routes",
  "tv.newRoutes": "new (last {days} days)",
  "tv.new": "NEW",
  "tv.noRatings": "No ratings",
  "tv.timesClimbed": "{count}× climbed",
  "tv.locale": "en-GB",
  "tv.accessDenied": "This dashboard is not available.",
  "tv.accessDeniedHint": "Ask the gym admin for the TV link.",
  "tv.gradeDistribution": "Grade distribution",
  "tv.sprayWalls": "Spray walls",
  "tv.sprayPublicProblems": "Public routes",
  "tv.sprayNewProblems": "New routes ({days} days)",
  "tv.brandingScanTitle": "What did you send today?",
  "tv.brandingScanBody": "Scan the QR to open the gym, log your ascents, and watch your progress over time.",
  "tv.brandingPoweredBy": "Powered by Sendly",
  "tv.climberOfWeek": "Climber of the week",
  "tv.thisWeek": "this week",
  "tv.lastWeek": "last week",
  "tv.topRatedRoutes": "Top rated routes",
  "tv.weeklyComparison": "Weekly comparison",
  "tv.totalAscentsLabel": "Ascents",
  "tv.totalPointsLabel": "Points",
  "tv.activeClimbers": "Active climbers",
  "tv.newRoutesShowcase": "New routes ({days} days)",
  "tv.noNewRoutes": "No new routes.",
  "tv.milestones": "Milestones",
  "tv.milestoneAscents": "{count} ascents reached!",
  "tv.milestoneFirstDiff": "First time on:",
  "tv.upcomingCompetitions": "Upcoming competitions",
  "tv.externalComp": "External",
  "tv.slidePicker": "Slides",
  "tv.groupPicker": "Groups",

  // TV Screen Management
  "tv.screens.title": "TV screen management",
  "tv.screens.nameLabel": "Screen name",
  "tv.screens.modeLabel": "Mode",
  "tv.screens.groupLabel": "Group",
  "tv.screens.copyLink": "Copy link",
  "tv.screens.status.online": "Online",
  "tv.screens.status.slow": "Slow",
  "tv.screens.status.offline": "Offline",
  "tv.screens.pendingLabel": "Pending",
  "tv.screens.registrationCode": "Registration code",
  "tv.screens.registrationHint": "This code appears in Settings → TV Screens. Ask the admin to approve this screen there.",
  "tv.screens.waitingForApproval": "Waiting for admin approval...",
  "tv.screens.approveBtn": "Approve",
  "tv.screens.rejectBtn": "Reject",
  "tv.screens.namePlaceholder": "e.g. Spectator TV",
  "tv.screens.competitionLabel": "Competition",
  "tv.screens.groupAll": "All",
  "tv.screens.seriesLabel": "Series",
  "tv.screens.deleteBtn": "Delete TV",
  "tv.screens.renameBtn": "Rename",
  "tv.screens.tvUrl": "TV dashboard link",
  "admin.tvGymDashboardHint": "These settings only apply to this TV in \"Gym dashboard\" mode.",
  "tv.screens.mapLabel": "Map",
  "tv.screens.mapAll": "All maps",
  "tv.screens.tvUrlHint": "Open this link in the TV's browser (Raspberry Pi, laptop, Android TV). A registration code will appear on screen, which you can approve in the list below.",
  "tv.screens.tvUrlNoPermission": "This gym has no TV link yet. Generating one requires gym admin permission.",
  "tv.screens.emptyState": "No registered TVs. Open the link above on a browser-equipped device and register it here using the displayed code.",
  "tv.screens.quickActions": "Quick actions",
  "tv.screens.allToCompetition": "All TVs to competition",
  "tv.screens.allToGymDashboard": "All TVs to gym dashboard",
  "tv.screens.allOff": "All TVs off",
  "tv.screens.modes.gym-dashboard": "Gym dashboard",
  "tv.screens.modes.competition-leaderboard": "Competition leaderboard",
  "tv.screens.modes.competition-clock": "Session countdown",
  "tv.screens.modes.competition-timer": "Finals timer",
  "tv.screens.modes.competition-startlist": "Competition start list",
  "tv.screens.modes.competition-ceremony": "Award ceremony",
  "tv.screens.modes.series-standings": "Series standings",
  "tv.screens.modes.sendly-branding": "Sendly branding",
  "tv.screens.modes.off": "Off",
  "tv.screens.modes.interval-timer": "Interval timer (Tabata)",
  "tv.screens.clock.noActiveSession": "No active session",
  "tv.screens.clock.nextSession": "Next session",
  "tv.screens.clock.paused": "PAUSED",
  "tv.screens.clock.competitionEnded": "Competition ended",
  "tv.screens.finals.noActiveFinals": "No active finals",
  "tv.screens.finals.observation": "Observation",
  "tv.screens.finals.climbing": "Climbing",
  "tv.screens.finals.rest": "Rest",
  "tv.screens.finals.finished": "Finished",
  "tv.screens.finals.round": "Round {current}/{total}",

  // Admin – TV config
  "admin.tvGradePreference": "TV grade scale",
  "admin.tvLeaderboardMode": "Leaderboard mode",
  "admin.tvLbAuto": "Automatic (alternating weekly and monthly)",
  "admin.tvLbWeekly": "Weekly",
  "admin.tvLbMonthly": "Monthly",
  "admin.tvLbAlltime": "All time",
  "admin.tvEnabledSlides": "Active slides",
  "admin.tvSlideCompetition": "Active competition",
  "admin.tvSlideUpcomingCompetitions": "Upcoming competitions",
  "admin.tvSlideSeriesLeaderboard": "Series leaderboard",
  "admin.tvSlideLeaderboard": "Leaderboard",
  "admin.tvSlideTodayAscents": "Today's ascents",
  "admin.tvSlideMap": "Map",
  "admin.tvSlideBranding": "Branding (QR + intro)",
  "admin.tvSlideSprayWalls": "Spray walls",
  "admin.tvSlideGradeDistribution": "Grade distribution",
  "admin.tvSlideClimberOfWeek": "Climber of the week",
  "admin.tvSlideTopRated": "Top rated routes",
  "admin.tvSlideWeeklyComparison": "Weekly comparison",
  "admin.tvSlideNewRoutes": "New routes showcase",
  "admin.tvSlideMilestones": "Milestones",

  // Admin – Advanced statistics (advancedStats gated)
  "admin.trafficTitle": "Traffic (30 days)",
  "admin.trafficNew": "New routes",
  "admin.trafficRepeat": "Repeats",
  "admin.trafficTooltip": "{date}: {total} climbs — {sends} new, {repeats} repeats",
  "admin.trafficWeekend": "Weekend",
  "admin.gradeDistTitle": "Grade distribution (active routes)",
  "admin.ascentDistTitle": "Climb distribution",
  "admin.ascentDistEmpty": "No climbs in this period.",
  "admin.ascentDistTotal": "Total: {count} climbs",
  "admin.statsCompareTitle": "Period comparison",
  "admin.statsCompareRange": "{cur} vs {prev}",
  "admin.statsCompareNoPrev": "No comparison baseline for this period.",
  "admin.statsCompareVsPrev": "vs {n}",
  "admin.statsCompareAscents": "Ascents",
  "admin.statsCompareClimbs": "Climbs",
  "admin.statsComparePoints": "Points",
  "admin.statsCompareClimbers": "Active climbers",
  "admin.statsCompareNewClimbers": "New climbers",
  "admin.statsGroupCommunity": "Community and activity",
  "admin.statsGroupRoutes": "Routes",
  "admin.topRoutes": "Most popular (Top 10)",
  "admin.bottomRoutes": "Least climbed (Bottom 5)",
  "admin.repeatRatio": "Repeat ratio",
  "admin.repeatRatioLegend": "climbs / climbers · repeats per climber",
  "admin.repeatRatioHint": "Which routes people come back to. Ascent counts saturate after a few weeks; this doesn't — so it's the better signal for timing a reset.",
  "admin.routeClassic": "Classic",
  "admin.routeStrippable": "Strippable",
  "admin.routeAge": "Route age",
  "admin.routeAgeAvg": "avg. days",
  "admin.routeAgeOldest": "oldest (days)",
  "admin.ratingOverview": "Rating by difficulty",
  "admin.userGrowthTitle": "User activity",
  "admin.activeUsers30d": "Active (30d)",
  "admin.activeUsersWeek": "Active (7 days)",
  "admin.retentionRate": "Retention",
  "admin.monthlyJoins": "New members by month",
  "admin.memberChangesTitle": "Member changes (12 months)",
  "admin.memberChangesDailyTitle": "Member changes (30 days)",
  "admin.memberJoinedLabel": "Joined",
  "admin.memberLeftLabel": "Left",
  "admin.memberChangesEmpty": "No joins or leaves in the last 12 months.",
  "admin.memberChangesDailyEmpty": "No joins or leaves in the last 30 days.",

  // Admin – TV link
  "admin.tvLinkGenerate": "Generate TV link",
  "admin.tvRegenerateLink": "Generate new link",
  "admin.tvRegenerateConfirm": "Generate a new link? The previous link will stop working and you'll need to update the TV.",

  // Zoomable map
  "zoomMap.uploadHint": "UPLOAD MAP IMAGE",
  "zoomMap.useTwo": "Use two fingers to move the map",
  "zoomMap.reset": "Reset view",
  "zoomMap.fullscreen": "Fullscreen",
  "zoomMap.exitFullscreen": "Exit fullscreen",

  // Default difficulty / hold colour labels and the "unknown" placeholders come
  // from the shared table (see hu.js).
  ...routeLabelDictEntries(ROUTE_LABELS.en),

  // Friends
  "friends.title": "Friends",
  "friends.tab": "Friends",
  "friends.addPlaceholder": "Email address...",
  "friends.send": "Send",
  "friends.pendingIncoming": "Incoming requests",
  "friends.pendingIncomingCount": "{count} pending request(s)",
  "friends.acceptedCount": "{count} friend(s)",
  "friends.pendingOutgoing": "Outgoing requests",
  "friends.list": "Friends",
  "friends.cancel": "Cancel",
  "friends.accept": "Accept",
  "friends.reject": "Reject",
  "friends.removeTitle": "Remove friend",
  "friends.removeConfirm": "Remove {name} from friends?",
  "friends.requestSent": "Friend request sent!",
  "friends.alreadyFriends": "Already friends",
  "friends.requestPending": "Request already pending",
  "friends.userNotFound": "No user found with this email",
  "friends.rateLimit": "Too many lookups — try again later",
  "friends.cannotAddSelf": "You cannot add yourself",
  "friends.noFriendsYet": "No friends yet",
  "friends.addFriend": "Add friend",
  "friends.sent": "Sent",
  "friends.searchPlaceholder": "Search friends...",

  // Account deletion
  "profile.deleteDescription": "Your account and all associated data will be permanently deleted. This action cannot be undone.",
  "profile.deleteList": "Will be deleted: ascents, ratings, friendships, active competition and event registrations. Results in closed competitions and events are preserved.",
  "profile.deleteConfirmWord": "DELETE",
  "profile.deleteConfirmLabel": "Type \"{word}\" to confirm",
  "profile.deleteButton": "Permanently delete account",
  "profile.deleteShowButton": "Delete account…",
  "profile.deleting": "Deleting...",
  "profile.deleteError": "An error occurred during deletion. Please try again.",
  "profile.deleteErrorPrecondition": "Account cannot be deleted: you are either a platform admin or the last active admin of a gym. Promote another admin or delete the gym first.",

  // Leave gym (single gym, account stays)
  "profile.leaveGymShowButton": "Leave this gym…",
  "profile.leaveGymDescription": "You'll be removed from {gymName}. Your account and memberships in other gyms remain intact.",
  "profile.leaveGymList": "Will be deleted in this gym: ascents, ratings, active competition and event registrations. Results in closed competitions and events are preserved.",
  "profile.leaveGymConfirmWord": "LEAVE",
  "profile.leaveGymConfirmLabel": "Type \"{word}\" to confirm",
  "profile.leaveGymButton": "Leave gym",
  "profile.leavingGym": "Leaving...",
  "profile.leaveGymError": "An error occurred while leaving. Please try again.",
  "profile.leaveGymErrorLastAdmin": "You are the last active admin of this gym. Promote another member to admin first.",
  "profile.leaveGymErrorBanned": "Banned members cannot leave on their own. Contact the gym admin.",
  "profile.leftGym": "You left the gym.",

  // Global profile page (/profile): header, tabs, settings sections
  "profile.pageTitle": "My Sendly profile",
  "profile.menuMyProfile": "My profile",
  "profile.tab.stats": "Statistics",
  "profile.tab.stats.short": "Stats",
  "profile.tab.settings": "Settings",
  "profile.tab.settings.short": "Settings",
  "profile.group.account": "Account",
  "profile.group.community": "Community",
  "profile.group.gyms": "Gyms",
  "profile.group.data": "Data",
  "profile.badgeNew": "New",
  "profile.section.grades.title": "Scoring and goals",
  "profile.section.data.sub": "Data export, legal documents",
  "profile.section.delete.sub": "Permanently delete your account and all data",
  "profile.deleteSectionTitle": "Delete account",
  "profile.myGyms.title": "My gyms",
  "profile.myGyms.sub": "{count} gyms",
  "profile.myGyms.empty": "You haven't joined any gym yet.",
  "profile.myGyms.enter": "Enter →",
  "profile.myGyms.ascentCount": "{count} ascents",
  "profile.myGyms.notifTitle": "Gym notifications",
  "profile.myGyms.noMembership": "You have no active membership in this gym, so notifications can't be configured.",
  "profile.myGyms.pushOffHint": "Push notifications are off on this device — enable them in the Notifications section first to receive gym notifications.",

  // Data export
  "export.title": "📦 Data export",
  "export.userDesc": "Download all your personal data in JSON format (profile, ascents, ratings, friendships).",
  "export.userButton": "Download my data",
  "export.gymTitle": "📦 Data export",
  "export.gymDesc": "Download all gym data in JSON format (routes, ascents, ratings, members, competitions).",
  "export.gymButton": "Download gym data",
  "export.loading": "Exporting...",
  "export.failed": "The export failed. Please try again.",

  // Gym Selector
  // Gym info modal
  "gymInfo.title": "Gym info",
  "gymInfo.backToGyms": "Back to the gyms",
  "gymInfo.copyLink": "Copy link",
  "gymInfo.linkCopied": "Link copied!",

  "gymSelector.title": "Choose your gym",
  "gymSelector.search": "Search gyms...",
  "gymSelector.noResults": "No gyms match your search.",
  "gymSelector.noGyms": "No gyms available yet.",
  "gymSelector.nearbyBtn": "Nearby",
  "gymSelector.routes": "routes",
  "gymSelector.members": "members",
  "gymSelector.openNow": "Open",
  "gymSelector.closingSoon": "Closing soon",
  "gymSelector.closedNow": "Closed",
  "gymSelector.opensAt": "Opens {day} {time}",
  "gymSelector.opensAtToday": "Opens at {time}",
  "gymSelector.today": "Today",
  "gymSelector.moreInfo": "More info",
  "gymSelector.lessInfo": "Less",
  "gymSelector.notFound": "Gym not found",
  "gymSelector.backToList": "Back to gym list",
  "gymSelector.loadFailed": "Couldn't load the gym",
  "gymSelector.loadFailedBody": "The connection to the server dropped. Check your internet connection and try again.",
  "notFound.title": "Oops, this page doesn't exist",
  "notFound.body": "The link you opened might be mistyped or out of date. An old QR code or bookmark could have led you here. You can head back to the gym list from here.",
  "notFound.cta": "Back to gym list",
  "gymSelector.signIn": "Sign in",
  "gymSelector.signInToEnter": "Sign in to enter the gym",
  "gymSelector.signInToContinue": "Sign in to open the page you followed",
  "gymSelector.mapToggle": "Map",
  "gymSelector.calendarBtn": "Calendar",
  "gymSelector.mapEnter": "Enter",
  "gymSelector.mapLoadError": "The map could not be loaded. Please try again later.",
  "gymSelector.myLocation": "My location",
  "gymSelector.showAll": "Show all gyms ({count})",
  "gymSelector.recent": "Visited",
  "gymSelector.notPublic": "Not public",

  // Gym Profile Editor
  "gymEditor.title": "Gym Profile",
  "gymEditor.name": "Gym name",
  "gymEditor.slug": "URL slug (read-only)",
  "gymEditor.description": "Description",
  "gymEditor.address": "Address",
  "gymEditor.city": "City",
  "gymEditor.country": "Country",
  "gymEditor.contactEmail": "Contact email",
  "gymEditor.contactPhone": "Contact phone",
  "gymEditor.website": "Website",
  "gymEditor.facebook": "Facebook",
  "gymEditor.instagram": "Instagram",
  "gymEditor.accentColor": "Accent color",
  "gymEditor.coverImage": "Cover image",
  "gymEditor.uploadCover": "Upload cover",
  "gymEditor.isPublic": "Public listing",
  "gymEditor.isPublicDesc": "Show this gym in the public gym selector.",
  "gymEditor.location": "GPS Location",
  "gymEditor.latitude": "Latitude",
  "gymEditor.longitude": "Longitude",
  "gymEditor.detectLocation": "Use my current position",
  "gymEditor.clearLocation": "Clear",
  "gymEditor.locationHint": "Enter coordinates manually, or press the button while at the gym to auto-detect. Used for distance sorting.",
  "gymEditor.geoNotSupported": "Geolocation is not supported by your browser.",
  "gymEditor.openingHours": "Opening hours",
  "gymEditor.timezone": "Timezone",
  "gymEditor.opensAt": "Opens",
  "gymEditor.closesAt": "Closes",
  "gymEditor.timezoneAuto": "Browser default (auto)",
  "gymEditor.timezoneSuggest": "Use {tz}",
  "gymEditor.timezoneHint": "Opening hours are evaluated in this timezone so visitors from other timezones see the correct status.",
  "gymEditor.dayClosed": "+ Set hours",
  "gymEditor.dayUnset": "Not set",
  "gymEditor.openingHoursIncomplete": "Not every day is filled in — set the hours or mark each day as closed.",
  "gymEditor.openingHoursAllClosed": "Every day can't be closed — the gym must be open on at least one day.",
  "gymEditor.mon": "Mon",
  "gymEditor.tue": "Tue",
  "gymEditor.wed": "Wed",
  "gymEditor.thu": "Thu",
  "gymEditor.fri": "Fri",
  "gymEditor.sat": "Sat",
  "gymEditor.sun": "Sun",
  "gymEditor.monFull": "Monday",
  "gymEditor.tueFull": "Tuesday",
  "gymEditor.wedFull": "Wednesday",
  "gymEditor.thuFull": "Thursday",
  "gymEditor.friFull": "Friday",
  "gymEditor.satFull": "Saturday",
  "gymEditor.sunFull": "Sunday",
  "gymEditor.specialHours": "Special opening hours",
  "gymEditor.specialHoursHint": "Different hours or a closed day for specific dates — e.g. public holidays. Overrides the weekly schedule on that date.",
  "gymEditor.addSpecial": "+ Add special day",
  "gymEditor.specialLabel": "Label (optional)",
  "gymEditor.specialOpen": "Open",
  "gymEditor.specialClosed": "Closed",
  "gymEditor.specialNone": "No special days set.",
  "gymEditor.specialDuplicate": "Multiple entries with the same date — only the first one takes effect.",

  // Admin section rename
  "admin.difficultyAndColors": "Difficulty & Colors",

  // Platform Admin
  "platformAdmin.title": "Sendly Admin",
  "platformAdmin.gyms": "Gyms",
  "platformAdmin.stats": "Statistics",
  "platformAdmin.createGym": "Create gym",
  "platformAdmin.searchGyms": "Search gyms...",
  "platformAdmin.allPlans": "All plans",
  "platformAdmin.planFilter": "Filter by plan",
  "platformAdmin.gymCount": "Gyms",
  "platformAdmin.totalUsers": "Total users",
  "platformAdmin.totalAscents": "Total ascents",
  "platformAdmin.totalClimbs": "Total climbs",
  "platformAdmin.demoBadge": "Demo",
  "platformAdmin.gymStatusActive": "Active",
  "platformAdmin.gymStatusInactive": "Inactive",
  "platformAdmin.billingMissingBadge": "Billing details missing",
  "platformAdmin.billingMissingNote": "{count} gym(s) will be invoiced this quarter but have incomplete billing details, so the invoice cannot be issued.",
  "platformAdmin.users": "Users",
  "platformAdmin.usersSearch": "Search by name or email…",
  "platformAdmin.moderation.title": "Moderation",
  "platformAdmin.moderation.sub": "Name review and banlist",
  "platformAdmin.moderation.queueTitle": "Names awaiting review",
  "platformAdmin.moderation.queueEmpty": "No names awaiting review",
  "platformAdmin.moderation.banlistTitle": "Banlist",
  "platformAdmin.moderation.banlistEmpty": "The banlist is empty",
  "platformAdmin.moderation.approve": "Approve",
  "platformAdmin.moderation.reject": "Reject",
  "platformAdmin.moderation.approved": "Name approved",
  "platformAdmin.moderation.resetDone": "Name reset",
  "platformAdmin.moderation.unbanned": "Removed from banlist",
  "platformAdmin.moderation.unban": "Remove",
  "platformAdmin.moderation.actionError": "The action failed",
  "platformAdmin.moderation.sourceAi": "AI-flagged",
  "platformAdmin.moderation.sourceGym": "Reported by {gym}",
  "platformAdmin.moderation.resetTitle": "Reset name",
  "platformAdmin.moderation.currentName": "Current name",
  "platformAdmin.moderation.restoreOriginal": "Restore original",
  "platformAdmin.moderation.noOriginal": "No original",
  "platformAdmin.moderation.generate": "Generate name",
  "platformAdmin.moderation.regenerate": "Regenerate",
  "platformAdmin.moderation.newName": "New name",
  "platformAdmin.moderation.confirmReset": "Reset and ban",
  "platformAdmin.moderation.confirmResetOnly": "Reset without ban",
  "platformAdmin.moderation.commentPlaceholder": "Message to the user (optional)…",
  "platformAdmin.moderation.commentHint": "If filled, the user sees it on their name-change notice.",
  "platformAdmin.moderation.resetNameAction": "Moderate name",
  "platformAdmin.moderation.mode.original": "original",
  "platformAdmin.moderation.mode.generated": "generated",
  "platformAdmin.usersEmpty": "No users found",
  "platformAdmin.usersShowMore": "Show more ({count})",
  "platformAdmin.userGymsStat": "Gyms",
  "platformAdmin.userAdminBadge": "Admin",
  "platformAdmin.userLastLogin": "Last login",
  "platformAdmin.userModalMemberships": "Gym memberships",
  "platformAdmin.userModalNoGyms": "Not a member of any gym",
  "platformAdmin.userModalLoadError": "Failed to load user data",
  "platformAdmin.userBannedBadge": "Banned",
  "platformAdmin.userBan": "Ban",
  "platformAdmin.userUnban": "Unban",
  "platformAdmin.userPlatformBanned": "Platform-banned",
  "platformAdmin.userModalBannedFrom": "Banned from",
  "platformAdmin.totalRoutes": "Total routes",
  "platformAdmin.statsSummary": "Summary",
  "platformAdmin.emailPrefsTitle": "User email preferences",
  "platformAdmin.topGymsByUsers": "Top gyms by users",
  "platformAdmin.topGymsByAscents": "Top gyms by ascents",
  "platformAdmin.planDistribution": "Plan distribution",
  "platformAdmin.enterGym": "Enter gym",
  "platformAdmin.accessDenied": "Access denied",
  "platformAdmin.backToGyms": "Back to gym list",
  "platformAdmin.buildInfo": "Version & build info",
  "platformAdmin.deleteConfirm": "Type DELETE to permanently delete {name}:",
  "platformAdmin.settingsTab": "Settings",
  "platformAdmin.showMap": "Map on gym selector",
  "platformAdmin.googleMapsKey": "Google Maps API key",
  "platformAdmin.googleMapsKeyHint": "Obtain from Google Cloud Console. Enable the Maps JavaScript API.",
  "platformAdmin.mapSaved": "Saved!",
  "platformAdmin.accentColor": "Platform color",
  "platformAdmin.accentColorHint": "Accent color for the platform admin UI. Does not affect individual gym colors.",
  "platformAdmin.accentColorSaved": "Saved!",
  "platformAdmin.accentColorHex": "Colour hex code",
  "platformAdmin.gradeScale": "Grade scale",
  "platformAdmin.gradeScaleHint": "Normalized points and grade names for cross-gym comparison. Save after making changes.",
  "platformAdmin.gradeFont": "Font",
  "platformAdmin.gradeV": "V-scale",
  "platformAdmin.gradeFrench": "French",
  "platformAdmin.gradeUiaa": "UIAA",
  "platformAdmin.gradePoints": "Points",
  "platformAdmin.gradeAdd": "Add grade",
  "platformAdmin.gradeScaleSaved": "Saved!",
  "platformAdmin.gradeScaleReset": "Reset to default",
  "platformAdmin.gradeRow": "Grade {row}",
  "platformAdmin.gradeRemove": "Remove grade",
  "platformAdmin.adminsTitle": "Platform admins",
  "platformAdmin.adminsHint": "Everyone who holds any platform permission. A full platform admin manages every gym, setting and statistic; a user marked “Partial” holds only the permissions ticked under 🔑.",
  "platformAdmin.adminsPartialBadge": "Partial",
  "platformAdmin.adminsAdd": "Add",
  "platformAdmin.adminsRemove": "Remove",
  "platformAdmin.adminsEmailPlaceholder": "User email address...",
  "platformAdmin.adminsEmailLabel": "New platform admin's email",
  "platformAdmin.adminsNotFound": "No user found with this email",
  "platformAdmin.adminsAlready": "User is already a platform admin",
  "platformAdmin.adminsLastAdmin": "Last admin",
  "platformAdmin.crashLogsTitle": "Crash logs",
  "platformAdmin.crashLogsEmpty": "No crash logs",
  "platformAdmin.crashLogsStack": "Stack trace",
  "platformAdmin.crashLogsDetails": "Details",
  "platformAdmin.crashLogsShowAll": "Show all ({count})",
  "platformAdmin.crashDigestTitle": "Daily error digest email",
  "platformAdmin.crashDigestHint": "Send the previous day's crash logs to the specified email address every morning at 7:00 AM (CET).",
  "platformAdmin.crashDigestEnable": "Enable daily crash digest",
  "platformAdmin.crashDigestEmailPlaceholder": "recipient@example.com",
  "platformAdmin.crashDigestEmailLabel": "Recipient's email",
  "platformAdmin.crashDigestSaved": "Saved!",
  "platformAdmin.marketData": "Market data",
  "platformAdmin.marketDataSub": "Monthly anonymized snapshots",
  "platformAdmin.marketMap": "Gym map",
  "platformAdmin.marketMapSub": "Gyms, activity and shared climbers on a map",
  // Spraywall hold-detection platform page (PlatformHoldDetector)
  "platformAdmin.holdDetector.title": "Hold detection",
  "platformAdmin.holdDetector.sub": "Active model and real quality",
  "platformAdmin.holdDetector.activeTitle": "Active model",
  "platformAdmin.holdDetector.optionLabel": "{id} · {date} · P {p} / R {r}",
  "platformAdmin.holdDetector.activeMark": "active",
  "platformAdmin.holdDetector.activate": "Activate",
  "platformAdmin.holdDetector.rollback": "Roll back to {id}",
  "platformAdmin.holdDetector.confirmTitle": "Activate model",
  "platformAdmin.holdDetector.confirmBody": "Activate {id}? Every gym's detection and smart marking uses it from the next call.",
  "platformAdmin.holdDetector.switched": "Active model: {id}",
  "platformAdmin.holdDetector.readOnlyHint": "Switching the model needs the platform.settings.edit permission.",
  "platformAdmin.holdDetector.noModels": "No model has been uploaded yet.",
  "platformAdmin.holdDetector.noActive": "No active model — detection and smart marking are unavailable.",
  "platformAdmin.holdDetector.notEligible": "{id} did not meet the activation criteria (precision or recall below 85% on the test set, or weaker than the model active at upload). You can still choose it — your call.",
  "platformAdmin.holdDetector.compareTitle": "Model versions compared",
  "platformAdmin.holdDetector.compareSub": "last 365 days · from real use",
  "platformAdmin.holdDetector.modeAll": "All",
  "platformAdmin.holdDetector.modeFresh": "New photo",
  "platformAdmin.holdDetector.modeRefresh": "Refresh",
  "platformAdmin.holdDetector.colVersion": "Version",
  "platformAdmin.holdDetector.colRuns": "Runs",
  "platformAdmin.holdDetector.colPrecision": "Real precision",
  "platformAdmin.holdDetector.colRecall": "Real recall",
  "platformAdmin.holdDetector.colCorrected": "Hand-corrected",
  "platformAdmin.holdDetector.colDuration": "Avg. time",
  "platformAdmin.holdDetector.durationSec": "{s} s",
  "platformAdmin.holdDetector.formula": "Real precision = accepted / proposed · real recall = accepted / (accepted + added + kept).",
  "platformAdmin.holdDetector.worstTitle": "Weakest runs",
  "platformAdmin.holdDetector.worstSub": "last 90 days",
  "platformAdmin.holdDetector.colGym": "Gym",
  "platformAdmin.holdDetector.colWall": "Wall",
  "platformAdmin.holdDetector.colModel": "Model",
  "platformAdmin.holdDetector.colRunPrecision": "Precision",
  "platformAdmin.holdDetector.colRunRecall": "Recall",
  "platformAdmin.holdDetector.deletedWall": "deleted wall",
  "platformAdmin.holdDetector.noRuns": "No runs in this period.",
  "platformAdmin.holdDetector.consentsTitle": "Training consent",
  "platformAdmin.holdDetector.consentsCount": "{n} gyms consented",
  "platformAdmin.holdDetector.consentsSince": "since {date}",
  "platformAdmin.holdDetector.loadError": "Could not load.",

  // Market data viewer (snapshot-only reads)
  "marketData.empty": "No market snapshot yet. The first one is generated automatically on the 1st of next month, or can be backfilled with the backfillMarketSnapshots function.",
  "marketData.platformSummary": "Platform summary",
  "marketData.benchmark": "Benchmark: {n}/{min} gyms",
  "marketData.benchmarkOk": "Benchmark eligible ({n} gyms)",
  "marketData.benchmarkTitle": "Gym anonymity: market benchmarks may only be built from at least 5 gyms, so a single gym can't be reverse-identified from the average.",
  "marketData.prevMonth": "Previous month",
  "marketData.nextMonth": "Next month",
  "marketData.monthSelect": "Month",
  "marketData.gymSelect": "Gym",
  "marketData.totalAscents": "Ascents",
  "marketData.newRegistrations": "New registrations",
  "marketData.multiGymUsers": "Active in multiple gyms",
  "marketData.errors": "Failed gyms",
  "marketData.trendAscents": "Trend — ascents",
  "marketData.trendMau": "Trend — MAU",
  "marketData.activity": "Activity",
  "marketData.newMembers": "New members",
  "marketData.heatmap": "Heatmap (day × hour, unique climbers)",
  "marketData.retention": "Returning members (cohort)",
  "marketData.cohortSize": "Cohort (new members)",
  "marketData.weekN": "Week {n}",
  "marketData.retentionHint": "Members returning within 1/4/12 weeks of joining (the join day itself doesn't count). A weekly value only appears once the whole cohort's window has elapsed — until then it shows \"—\" and later monthly runs fill it in. In multi-month view the percentage denominator is the matured months' cohort only — the card then shows \"returned/matured cohort\" (e.g. 31/31).",
  "marketData.grades": "Grade distribution",
  "marketData.routesByGrade": "Active routes by grade",
  "marketData.ascentsByGrade": "Ascents by grade",
  "marketData.climberLevel": "Climber level (hardest send of the month)",
  "marketData.flashRate": "Flash rate",
  "marketData.unknown": "Unknown",
  "marketData.noData": "No data",
  "marketData.lifecycle": "Route lifecycle (routes archived this month)",
  "marketData.archivedCount": "Archived routes",
  "marketData.avgLifetimeDays": "Avg lifetime (days)",
  "marketData.avgAscentsPerRoute": "Avg ascents / route",
  "marketData.decayCurve": "Decay curve (avg ascents / route / life-week)",
  "marketData.decayHint": "Traffic drops below a quarter of the initial peak around life-week {w} — a good time to schedule the reset.",
  "marketData.decayNoDrop": "Traffic did not drop below a quarter of the initial peak in the observed window.",
  "marketData.decayNoData": "Not enough data for the decay curve yet — a longer window (12 months / All) accumulates more life-weeks.",
  "marketData.demographics": "Demographics",
  "marketData.ageBuckets": "Age (birth decade)",
  "marketData.gender": "Gender",
  "marketData.language": "Language",
  "marketData.langHu": "Hungarian",
  "marketData.langEn": "English",
  "marketData.competitions": "Competitions",
  "marketData.compCount": "Competition count",
  "marketData.totalParticipants": "Participants",
  "marketData.externalRatio": "External entrant ratio",
  "marketData.kAnonNote": "K-anonymity: every published group is backed by at least 10 unique users; smaller groups are folded into \"Unknown\".",
  "marketData.window1": "1 month",
  "marketData.window3": "3 months",
  "marketData.window12": "12 months",
  "marketData.windowAll": "All",
  "marketData.windowSelect": "Window",
  "marketData.mauAvg": "MAU (monthly avg)",
  "marketData.monthlyBreakdown": "Monthly breakdown",
  "marketData.latestMonthHint": "In multi-month view, routes by grade, climber level, flash rate and demographics show the last month of the window — they count unique users and can't be summed across months.",
  "marketData.activation": "Activation funnel",
  "marketData.actJoined": "Joined",
  "marketData.actFirstAscent": "Climbed in week 1",
  "marketData.actWeek2": "Active in week 2",
  "marketData.churn": "Churn (last active week)",
  "marketData.churn_never": "Never returned",
  "marketData.churn_w1": "Week 1",
  "marketData.churn_w2_4": "Weeks 2–4",
  "marketData.churn_w5_8": "Weeks 5–8",
  "marketData.churn_w9_12": "Weeks 9–12",
  "marketData.churn_unknown": "Unknown (folded below K)",
  "marketData.gymPairs": "Gym pairs (shared active members)",
  "marketData.gymPairsEmpty": "No gym pair with at least 10 shared active members this month.",
  "marketData.refreshCurrentMonth": "Refresh current month",
  "marketData.refreshing": "Refreshing…",
  "marketData.refreshHint": "Recompute the current month (month-to-date) — no need to wait for month end.",
  "marketData.refreshDone": "Market data for {month} refreshed.",
  "marketData.refreshError": "Refresh failed",
  "marketData.exportPdf": "Export PDF",
  "marketData.exporting": "Exporting…",
  "marketData.exportError": "Export failed",
  "marketData.reportTitle": "Market Data",
  "marketData.pdfGeneratedAt": "Generated: {date}",

  // Market map (Gym map)
  "marketMap.country": "Country",
  "marketMap.allCountries": "🌍 All countries",
  "marketMap.metric": "Metric",
  "marketMap.minShared": "Min. shared climbers",
  "marketMap.edges": "Connections",
  "marketMap.heat": "Heatmap",
  "marketMap.summary": "Summary",
  "marketMap.gyms": "Gyms",
  "marketMap.connections": "Connections",
  "marketMap.topConnections": "Most shared climbers",
  "marketMap.sharedClimbers": "Shared climbers",
  "marketMap.noConnections": "No connection above the threshold.",
  "marketMap.noApiKey": "The map needs a Google Maps API key — set it under Settings → Map.",
  "marketMap.loadError": "Could not load the map",
  "marketMap.legendTitle": "Legend",
  "marketMap.legendSize": "Marker size = {metric}",
  "marketMap.legendColor": "Color = activity intensity (where people climb most)",
  "marketMap.legendEdge": "Line thickness = shared active climbers",
  "marketMap.coordsNote": "{withCoords}/{total} gyms have coordinates set — gyms without coordinates are not shown on the map.",
  "marketMap.export": "Export CSV",
  "marketMap.csvName": "Gym",
  "marketMap.csvLat": "Latitude",
  "marketMap.csvLng": "Longitude",

  // Platform Gym Editor
  "platformGymEditor.title": "Edit Gym",
  "platformGymEditor.createTitle": "Create New Gym",
  "platformGymEditor.name": "Gym name",
  "platformGymEditor.slug": "URL slug",
  "platformGymEditor.slugTaken": "This slug is already taken.",
  "platformGymEditor.city": "City",
  "platformGymEditor.country": "Country",
  "platformGymEditor.plan": "Plan",
  "platformGymEditor.planFree": "Free",
  "platformGymEditor.planPro": "Pro",
  "platformGymEditor.planCompetition": "Competition",
  "platformGymEditor.features": "Feature overrides",
  "platformGymEditor.friendsSprayHint": "When off, friends-level Spraywall problems and lists behave as private too.",
  "platformGymEditor.initialAdminEmail": "Initial admin (email)",
  "platformGymEditor.adminRequired": "An initial admin is required",
  "platformGymEditor.userNotFound": "No user found with this email.",
  "platformGymEditor.isActive": "Active",
  "platformGymEditor.activate": "Gym is active and visible.",
  "platformGymEditor.deactivate": "Gym is deactivated and hidden.",
  "platformGymEditor.isDemo": "Demo gym",
  "platformGymEditor.isDemoHint": "Not a real gym — excluded from real-gym counts (e.g. public gym tally, benchmarks).",
  "platformGymEditor.deleteGym": "Delete gym",
  "platformGymEditor.limits": "Limits",
  "platformGymEditor.limitsHint": "Empty = unlimited",
  "platformGymEditor.gymAdmins": "Gym admins",
  "platformGymEditor.addAdminPlaceholder": "Add admin by email",
  "platformGymEditor.removeAdminConfirm": "Remove this admin?",
  "platformGymEditor.noAdmins": "No admins yet.",
  "platformGymEditor.create": "Create gym",
  "platformGymEditor.sectionOverview": "Overview",
  "platformGymEditor.sectionPlan": "Plan and features",
  "platformGymEditor.sectionStatus": "Status",
  "platformGymEditor.sectionDanger": "Danger zone",

  // Add-on framework — PlatformGymEditor "Add-ons" block, docs/spec/15-platform-admin.md
  // ("addons.title" is defined in the AdminSubscription block below — shared key)
  "addons.mcp.fallbackName": "Sendly MCP Server",
  "addons.mcp.badge": "MCP",
  "addons.sprayWall.fallbackName": "Spraywall",
  "addons.sprayWall.badge": "Spray",
  "addons.readOnlyHint": "The platform.gym.editAddons capability is required to switch add-ons on or off",
  "addons.contractRefTitle": "Turn on add-on",
  "addons.contractRefBody": "To turn on the “{name}” add-on, enter the framework-contract annex reference.",
  "addons.contractRefLabel": "Framework-contract annex reference",
  "addons.contractRefPlaceholder": "e.g. Annex 1 v1, signed 2026-10-01",
  "addons.contractRefRequired": "A framework-contract annex reference is required",
  "addons.logTitle": "Log (last 10)",
  "addons.logEmpty": "No log entries yet.",
  "addons.logOn": "Turned on",
  "addons.logOff": "Turned off",
  "addons.logByUnknown": "Platform admin",
  // Spraywall training consent toggle (PlatformGymEditor, sprayWall add-on card)
  "addons.sprayTraining.title": "Use walls for model training",
  "addons.sprayTraining.hint": "Only with a signed consent. Once revoked, the gym is left out of the next export; it cannot be removed from a model already trained.",
  "addons.sprayTraining.promptTitle": "Training consent",
  "addons.sprayTraining.promptBody": "Enter the reference of the signed consent (framework-contract annex). It goes into the platform audit log.",
  "addons.sprayTraining.contractRefLabel": "Consent reference",
  "addons.sprayTraining.contractRefPlaceholder": "e.g. Annex 2, point 5 — signed 2026-10-01",
  "addons.sprayTraining.contractRefRequired": "The consent reference is required",
  "addons.sprayTraining.since": "Granted since {date}",

  // Feature enforcement / limits
  "limits.routesReached": "Plan limit reached ({count}/{max} routes).",

  // Global Profile
  "globalProfile.memberSince": "Member since",
  "globalProfile.totalPoints": "Total points",
  "globalProfile.totalAscents": "Total ascents",
  "globalProfile.totalGyms": "Gyms",
  "globalProfile.timeline": "Recent days",
  "globalProfile.timelineEmpty": "Nothing logged yet.",
  "globalProfile.showMore": "Show more",
  "globalProfile.perGym": "Per-gym breakdown",
  "globalProfile.points": "pts",
  "globalProfile.ascents": "ascents",
  "globalProfile.climbs": "climbs",
  "globalProfile.ofWhichNew": "{count} new",
  "globalProfile.sectionAscents": "Ascents",
  "globalProfile.sectionActivity": "Activity",
  "globalProfile.totalAscentsN": "{count} routes climbed in total",
  "globalProfile.totalClimbsN": "{count} climbs in total",
  "globalProfile.viewGym": "Open gym",
  "globalProfile.noGyms": "You haven't joined any gyms yet.",
  "globalProfile.loading": "Loading profile...",
  "globalProfile.thisWeek": "This week",
  "globalProfile.thisMonth": "This month",
  "globalProfile.diffBreakdown": "Difficulty breakdown",

  // Upload validation
  "upload.invalidType": "Unsupported file type. Please use JPG, PNG, or WebP.",
  "upload.tooLarge": "File is too large. Maximum size: {max} MB.",
  "upload.hintLogo": "Max 1.5 MB · JPG, PNG, WebP",
  "upload.hintImage": "Max 3 MB · JPG, PNG, WebP · Recommended: high resolution",

  // PWA install
  "pwa.installHint": "Add {name} to your home screen!",
  "pwa.install": "Install",
  "pwa.iosHint": "Tap Share ⎋ → Add to Home Screen",

  // App update banner — shown when a new service worker version is ready
  "update.available": "A new version is available",
  "update.refresh": "Refresh",
  // Client–backend compatibility (docs/spec/19d-client-backend-compat.md):
  // the build is below the live level — the update modal, the standing screen
  // after the loop brake, the callable refusal text and the /version rows.
  "compat.clientOutdated": "Please update the app and try again",
  "compat.modal.title": "Here's the new Sendly",
  "compat.modal.body": "You need to update to continue. A few seconds, and you're on the new version.",
  "compat.modal.update": "Update",
  "compat.modal.updating": "Updating…",
  "compat.stuck.title": "The app could not update.",
  "compat.stuck.body": "Close it completely and open it again.",
  "compat.stuck.retrying": "Trying again…",
  "compat.stuck.ownLevel": "This build: level {level} · build {build}",
  "compat.stuck.liveLevel": "Live: level {level} · build {build}",
  "compat.version.level": "compatibility {own} (live: {live})",
  "compat.version.buildId": "build id",
  "compat.version.liveUnknown": "unknown",
  "offline.banner": "Offline mode — changes are saved on this device and will sync automatically once the connection returns",
  "offline.blockedBanner": "{n} attempt(s) couldn't be saved — the competition is closed or attempt registration is locked. Ask the organizer to re-open, or discard with X.",
  // "Connected but nothing gets through" — the phone is associated, so the
  // offline banner stays silent while saves pile up in the queue.
  "offline.pendingBanner": "{n} save(s) waiting to sync — the connection is slow. Your data is safe on this device.",
  "offline.blockedDiscardTitle": "Discard unsaved attempts",
  "offline.blockedDiscardConfirm": "Discard these {n} unsaved attempt(s)? This action cannot be undone.",
  "offline.blockedDiscardConfirmLabel": "Discard",
  "offline.blockedDiscardAria": "Discard unsaved attempts",

  // Grade normalization
  "grade.gradeRange": "Grade range",
  "grade.noMapping": "This gym has not mapped its difficulty levels to standard grades yet.",
  "grade.clear": "Clear grade range",
  "grade.unmappedWarning": "{count} gym(s) not using standard grade mapping",
  "globalProfile.scoreInfoNorm": "Total points are calculated using the normalized grading system. Each ascent receives points based on the standard grade mapping of its difficulty level, making scores comparable across different gyms.",
  "globalProfile.scoreInfoOpen": "How total points are calculated",
  "globalProfile.scoreInfoRaw": "Total points are based on each gym's own scoring system. Once your gyms map their difficulty levels to standard grades, the total will automatically switch to normalized points, enabling cross-gym comparison.",
  "globalProfile.noAdvancedStats": "No advanced statistics available. Statistics are only available for gyms with this feature enabled.",
  "globalProfile.advancedStatsNotAvailable": "Advanced statistics not available for this gym.",
  "globalProfile.activeDays": "Active days",
  "globalProfile.activityHint": "Activity counts every climb, spray sends and repeats included, in every discipline — the discipline filter does not apply here.",

  // Platform analytics (Phase 6)
  "platform.growthTitle": "Platform growth (12 months)",
  "platform.totalGyms": "Total gyms",
  "platform.totalUsersAll": "Total users",
  "platform.newGymsMonthly": "New gyms per month",
  "platform.userChangesMonthly": "User changes per month",
  "platform.userChangesDaily": "User changes per day (30 days)",
  "platform.newUsersLabel": "New",
  "platform.deletionsLabel": "Deleted",
  "platform.revenueTitle": "Revenue (12 months)",
  "platform.revenueEmpty": "No billed revenue in the last 12 months — either every quarter produced only zero-amount gyms, or the most recent run sits outside the 12-month window.",
  "platform.revenueNoRuns": "The chart reads snapshots of what `sendQuarterlyBilling` actually invoiced (platform/quarterlyBilling/runs/{quarter}). No quarterly run has fired yet — values appear after the first scheduled run (Jan 1 / Apr 1 / Jul 1 / Oct 1, 08:00). Retroactive computation is deliberately skipped: plan, MAU, discount and pricing can all change between quarters.",
  "platform.revenueLoadError": "Failed to load billing snapshots.",
  "platform.revenueTotal12m": "12-month total ({currency})",
  "platform.revenueMonthly": "Monthly breakdown — {currency}",
  "platform.revenueSourceNote": "The chart reflects the snapshots of quarterly invoices actually sent (platform/quarterlyBilling/runs/{quarter}), in net (VAT-exclusive) amounts. A gym's older quarters keep showing the plan / price / discount in effect at billing time even if those have changed since — that's how the numbers stay in sync with bookkeeping.",
  "platform.gymHealthTitle": "Gym health",
  "platform.sortName": "Name",
  "platform.sortUsers": "Users",
  "platform.sortRoutes": "Routes",
  "platform.sortBilling": "Billing",
  "platform.sortBy": "Sort by",
  "platform.colBillingTooltip": "Billing details on file",
  "platform.colBillingComplete": "Billing details on file",
  "platform.colBillingMissing": "Billing details missing",
  "platform.colName": "Name",
  "platform.colPlan": "Plan",
  "platform.colRoutes": "Routes",
  "platform.colUsers": "Users",
  "platform.colAscents": "Asc.",
  "platform.upsellTitle": "Upsell candidates",
  "platform.upsellLimits": "Free gyms near limits (80%+)",
  "platform.upsellAdvanced": "High activity without advancedStats",
  "platform.feature.tvDashboard": "TV dashboard",
  "platform.feature.friends": "Friends",
  "platform.feature.competitions": "Competitions",
  "platform.feature.customBranding": "Custom branding",
  "platform.feature.dbMaintenance": "Database maintenance",
  "platform.feature.setterStats": "Setter stats",
  "platform.feature.gymPwa": "Gym PWA",
  "platform.feature.advancedStats": "Advanced stats",
  "platform.feature.gradeMapping": "Grade mapping",
  "platform.noUpsellCandidates": "No upsell candidates at this time.",
  "platform.featureAdoptionTitle": "Feature adoption",

  // GlobalStats
  "globalStats.normalAscent": "Normal",
  "globalStats.friendRanking": "Friends ranking",

  // Activity feed
  "feed.title": "Friends' activity",
  "feed.empty": "No recent activity in the last 48 hours.",
  "feed.climbSummary": "{climbs} climbs · {sends} new",
  "feed.justNow": "Just now",
  "feed.hoursAgo": "{count}h ago",
  "feed.yesterday": "Yesterday",

  // Activity heatmap labels
  "heatmap.mon": "Mon",
  "heatmap.wed": "Wed",
  "heatmap.fri": "Fri",
  "heatmap.sun": "Sun",
  "heatmap.less": "Less",
  "heatmap.more": "More",

  // Admin heatmap
  "admin.heatmapTitle": "Route heatmap",
  "admin.heatmapHint": "The number is every climb, repeats included — that is what shows which routes people come back to.",
  "admin.heatmapLegendNone": "0",
  "admin.heatmapLegendLow": "Low",
  "admin.heatmapLegendMed": "Med",
  "admin.heatmapLegendHigh": "High",
  "admin.heatmapLegendHot": "Hot",

  // Competition
  "tab.comp": "Competition",
  "tab.comp.short": "Comp.",
  "comp.title": "Competitions",
  "comp.backToGym": "Back to gym",
  "comp.create": "+ New competition",

  // Calendar (shared: gym Events tab + platform /calendar)
  "calendar.viewList": "List",
  "calendar.viewCalendar": "Calendar",
  "calendar.viewToggle": "List or calendar view",
  "calendar.period": "Period",
  "calendar.filterKinds": "Filter by type",
  "calendar.kind.all": "All",
  "calendar.kind.comp": "Competitions",
  "calendar.kind.event": "Events",
  "calendar.status.live": "Live",
  "calendar.status.upcoming": "Upcoming",
  "calendar.status.draft": "Draft",
  "calendar.status.closed": "Closed",
  "calendar.status.past": "Past",
  "calendar.reg.open": "Reg. open",
  "calendar.reg.soon": "Reg. soon",
  "calendar.reg.closed": "Reg. closed",
  "calendar.reg.toggle": "Registration window",
  "calendar.reg.legend": "Registration window",
  "calendar.reg.legendUrgent": "Closes within {n} days",
  "calendar.reg.spanLabel": "{name} · registration",
  "calendar.reg.closesAt": "Registration closes: {date}",
  "calendar.reg.closesToday": "Registration closes today at {time}",
  "calendar.reg.closesTodayDate": "Registration closes today",
  "calendar.reg.opensAt": "Registration opens: {date}",
  "calendar.reg.opensIn": "in {n} days",
  "calendar.reg.daysLeft": "{n} days",
  "calendar.reg.lastDays": "Last {n} days!",
  "calendar.reg.untilStart": "Registration until the start date",
  "calendar.reg.closedOn": "Registration closed — {date}",
  "calendar.reg.closedManual": "Registration closed",
  "calendar.external": "External",
  "calendar.open": "Open",
  "calendar.openMonth": "Open month",
  "calendar.openDay": "Open day",
  "calendar.openInGym": "Open in gym",
  "calendar.modal.about": "Description",
  "calendar.modal.groups": "Groups",
  "calendar.modal.sessions": "Sessions",
  "calendar.modal.program": "Program",
  "calendar.modal.friends": "Friends",
  "calendar.modal.feePerGroup": "Varies by group",
  "calendar.modal.free": "Free",
  "calendar.modal.cap": "max {n}",
  "calendar.backToGyms": "Gyms",
  "calendar.platformTitle": "Calendar",
  "calendar.platformSubtitle": "Every Sendly gym's public competitions and events in one place.",
  "calendar.platformEmpty": "No competitions or events are currently announced at any gym.",
  "calendar.platformFooter": "Tap a competition for details and registration on its gym page.",
  "calendar.today": "Today",
  "calendar.undated": "No date",
  "calendar.noDate": "no date",
  "calendar.empty": "No competitions or events to show in this period.",
  "calendar.emptyButReg": "No competitions or events in this period — but registration is open on the marked days.",
  "calendar.more": "+{n} more",
  "calendar.dow.0": "Mon",
  "calendar.dow.1": "Tue",
  "calendar.dow.2": "Wed",
  "calendar.dow.3": "Thu",
  "calendar.dow.4": "Fri",
  "calendar.dow.5": "Sat",
  "calendar.dow.6": "Sun",
  "comp.import.excel.btnLabel": "📊 Import from Excel",
  "comp.import.excel.title": "Import competition from Excel",
  "comp.import.results.btnLabel": "Import results from Excel",
  "comp.import.results.title": "Import results from Excel",
  "comp.import.results.fileLabel": "Excel file (.xlsx)",
  "comp.import.results.dropzoneHint": "Click or drag file here",
  "comp.import.results.formatHelpTitle": "Expected format",
  "comp.import.results.formatHelpBody": "Column B is the competitor name. Header row 1 has paired columns named like 'R1Z', 'R1T', 'R2Z', 'R2T' — the prefix is the route name. R1Z = number of zone attempts on route R1, R1T = number of top attempts. Empty or 0 means no zone/top reached. A competitor with a top count > 0 is treated as having reached the zone too. Existing attempts in the system are overwritten when a match is found.",
  "comp.import.results.previewTitle": "Preview",
  "comp.import.results.previewMatchedRoutes": "Routes matched: {matched} / {total}",
  "comp.import.results.previewMatchedRows": "Competitors matched: {matched} / {total}",
  "comp.import.results.previewTotalWrites": "Attempts to write: {n}",
  "comp.import.results.previewSkippedUnchanged": "Unchanged (skipped): {n}",
  "comp.import.results.warningsTitle": "Warnings",
  "comp.import.results.warnUnmatchedRoutes": "Routes not found in this competition: {names}",
  "comp.import.results.warnUnmatchedRows": "Competitors not registered: {names}",
  "comp.import.results.warnDuplicateProblemName": "Two picked problems share the name {name} — the column cannot be matched unambiguously.",
  "comp.import.results.warnUnknownHeaders": "Header columns ignored (don't match {{routeName}}Z / {{routeName}}T): {headers}",
  "comp.import.results.nextBtn": "Next",
  "comp.import.results.importBtn": "Import",
  "comp.import.results.importing": "Importing... ({n} / {total})",
  "comp.import.results.doneTitle": "Results imported successfully",
  "comp.import.results.doneSummary": "{n} attempts saved.",
  "comp.import.results.errorInvalidFile": "Only .xlsx files are accepted.",
  "comp.import.results.errorParseFailure": "File is not readable. Make sure it's a valid .xlsx format.",
  "comp.import.results.errorRow": "Import stopped at entry {n} ({name}): {message}. You can safely import the same file again — rows that already went through won't be duplicated.",
  "comp.import.results.errorEmptyFile": "The file contains no usable data (need column B + Rn{Z|T} headers).",
  "comp.import.results.errorImportFailed": "Error during import: {message}",
  "comp.import.excel.competitionName": "Competition name",
  "comp.import.excel.fileLabel": "Excel file (.xlsx)",
  "comp.import.excel.dropzoneHint": "Click or drag file here",
  "comp.import.excel.formatHelpTitle": "Expected format",
  "comp.import.excel.formatHelpBody": "Column A is the session name, B the competitor name. Columns C/D are optional and ignored. Each tab becomes two groups: '{sheet} Kezdő' and '{sheet} Haladó' — every competitor from the tab is assigned to Haladó by default and can be moved to Kezdő later from the admin editor.",
  "comp.import.excel.previewTitle": "Preview",
  "comp.import.excel.previewSessionsCount": "Sessions: {n}",
  "comp.import.excel.previewGroupsCount": "Groups: {n}",
  "comp.import.excel.previewCompetitorsCount": "Total competitors: {n}",
  "comp.import.excel.warningsTitle": "Warnings ({n})",
  "comp.import.excel.warningRow": "{sheet}, row {row}: {reason}",
  "comp.import.excel.warningEmptyCell": "Empty cell",
  "comp.import.excel.warningInvalidGender": "Invalid gender value: '{value}'",
  "comp.import.excel.warningEmptySheet": "Empty sheet (no data rows)",
  "comp.import.excel.nextBtn": "Next",
  "comp.import.excel.importBtn": "Create",
  "comp.import.excel.importing": "Importing...",
  "comp.import.excel.stepCreatingComp": "Creating competition...",
  "comp.import.excel.stepCreatingGroups": "Creating groups ({n}/{total})...",
  "comp.import.excel.stepCreatingSessions": "Creating sessions ({n}/{total})...",
  "comp.import.excel.stepCreatingGuests": "Registering guest competitors ({n}/{total})...",
  "comp.import.excel.doneTitle": "Competition created successfully!",
  "comp.import.excel.doneSummary": "{sessions} sessions, {groups} groups, {competitors} guest competitors",
  "comp.import.excel.openEditor": "Open editor",
  "comp.import.excel.errorInvalidFile": "Only .xlsx files are accepted.",
  "comp.import.excel.errorParseFailure": "File is not readable. Make sure it's a valid .xlsx format.",
  "comp.import.excel.errorEmptyFile": "The file contains no importable data.",
  "comp.import.excel.errorImportFailed": "Error during import: {message}",
  "comp.import.excel.errorPartialImport": "The competition was partially created. You can find it in drafts.",

  // Competition template — clipboard copy and paste
  "comp.template.copy.btnLabel": "Copy as template",
  "comp.template.copy.downloadLabel": "Download as .json",
  "comp.template.copy.copied": "Competition template copied to clipboard",
  "comp.template.copy.failed": "Could not copy to the clipboard.",
  "comp.template.copy.unsupported": "This competition type cannot be turned into a template.",
  "comp.template.paste.btnLabel": "📋 Paste from template",
  "comp.template.paste.title": "Create competition from template",
  "comp.template.paste.intro": "Paste a template copied from another competition. Settings, groups and sessions carry over — routes, dates, participants and results do not.",
  "comp.template.paste.nameLabel": "Name of the new competition",
  "comp.template.paste.previewTitle": "What the template contains",
  "comp.template.paste.previewCounts": "{groups} groups · {sessions} sessions",
  "comp.template.paste.previewSource": "Source: {gym}",
  "comp.template.paste.previewExcluded": "Not included: routes, dates, participants, organizers, judges, results. The competition is created as a draft.",
  "comp.template.paste.previewExcluded_external": "Not included: dates, results link. The competition is created as a draft.",
  "comp.template.paste.previewExcluded_challenge": "Not included: dates, participants, exclusions, results. The challenge is created as a draft.",
  "comp.template.paste.warningsTitle": "Warnings ({n})",
  "comp.template.paste.warnUnknownGroupKey": "Session “{session}” references an unknown group: {key} — skipped.",
  "comp.template.paste.warnDuplicateGroupKey": "Duplicate group key: {key} — the second one was re-keyed.",
  "comp.template.paste.warnGroupNoName": "Group #{n} has no name — skipped.",
  "comp.template.paste.warnUnknownDifficulty": "Group {group} has a hardest level this gym does not have — it is created without an upper limit.",
  "comp.template.paste.warnSprayNoAddon": "The template uses the spray source, but this gym has no Spraywall — switch the draft's source to Routes.",
  "comp.template.paste.warnSessionNoName": "Session #{n} has no name — skipped.",
  "comp.template.paste.warnGroupsTruncated": "{n} groups over the limit — skipped.",
  "comp.template.paste.warnSessionsTruncated": "{n} sessions over the limit — skipped.",
  "comp.template.paste.createBtn": "Create",
  "comp.template.paste.importing": "Creating…",
  "comp.template.paste.stepCreatingComp": "Creating competition…",
  "comp.template.paste.stepCreatingGroups": "Creating groups… ({n}/{total})",
  "comp.template.paste.stepCreatingSessions": "Creating sessions… ({n}/{total})",
  "comp.template.paste.doneTitle": "Competition created",
  "comp.template.paste.doneSummary": "Created {groups} groups and {sessions} sessions.",
  "comp.template.paste.doneSummarySimple": "The settings came across from the template.",
  "comp.template.paste.doneNextSteps": "The competition is a draft. Set the dates and the routes before activating it.",
  "comp.template.paste.doneNextSteps_external": "The competition is a draft. Set the dates and check the link before activating it.",
  "comp.template.paste.doneNextSteps_challenge": "The challenge is a draft. Set the dates before activating it.",
  "comp.template.paste.openEditor": "Open",
  "comp.template.paste.error_empty": "The field is empty — paste a template.",
  "comp.template.paste.error_notJson": "The pasted text is not valid JSON.",
  "comp.template.paste.error_notTemplate": "The pasted text is not a Sendly competition template.",
  "comp.template.paste.error_wrongKind": "This is not a competition template.",
  "comp.template.paste.error_futureVersion": "This template was made with a newer version. Please update the app.",
  "comp.template.paste.error_noContent": "The template has no competition name, groups or sessions.",
  "comp.template.paste.errorImportFailed": "Error while creating: {message}",
  "comp.template.paste.errorPartialImport": "The competition was partially created. You can find it in drafts.",
  // Shared by every "create from template" dialog (competition, event).
  "template.textareaLabel": "Template (JSON)",
  "template.textareaPlaceholder": "Paste the template here (Ctrl+V), or drop a .json file",
  "template.clipboardHint": "If your browser blocked clipboard access, paste it manually into the field (Ctrl+V) or pick a previously saved .json file.",
  "template.pickFile": "Choose a file…",
  "template.clear": "Clear",
  "template.errorFileTooLarge": "The file is too large (max 2 MB).",
  "template.errorFileRead": "The file could not be read.",

  "comp.activate": "Activate",
  "comp.close": "Close",
  "comp.closing": "Closing…",
  "comp.closeFailed": "Could not close",
  "comp.delete": "Delete",
  "comp.status.draft": "Draft",
  "comp.status.active": "Active",
  "comp.status.closed": "Closed",
  "comp.type.standard": "Standard",
  "comp.type.external": "External",
  "comp.type.challenge": "Challenge",
  "comp.filter.active": "Active",
  "comp.filter.closed": "Closed",
  "comp.filter.draft": "Draft",
  "comp.filter.all": "All",
  "comp.participants": "{count} participants",
  "comp.noCompetitions": "No matching competitions",
  "comp.noActive": "No competition is scheduled at this gym right now.",
  "comp.deleteConfirm": "Delete competition \"{name}\" and all related data?",
  "comp.deleteFailed": "Failed to delete the competition",
  "comp.activateConfirm": "Activate \"{name}\"? It will become available to competitors.",
  "comp.activateConfirmExternal": "Activate external competition \"{name}\"?",
  "comp.closeConfirm": "Close \"{name}\"? Final results will be recorded.\n\nTops will also be added to the competitors' personal statistics: they earn a climb, points and an active day. Not for guests.",
  "comp.closeConfirmChallenge": "Close the \"{name}\" challenge? Final results will be recorded.",
  "comp.closeConfirmExternal": "Close external competition \"{name}\"?",
  "comp.closeWithRoutesBody": "Close \"{name}\"? Final results will be recorded.\n\nTops will also be added to the competitors' personal statistics: they earn a climb, points and an active day. Not for guests.\n\n{count} routes are assigned to this competition. They'll reappear on the public map once it closes — do you want to archive them at the same time?",
  "comp.closeWithArchive": "Close + archive",
  "comp.closeOnly": "Close only",
  "toast.compClosedAndArchived": "Competition closed, {count} routes archived",
  "comp.alreadyActiveExists": "Another competition is already active. Are you sure you want to start \"{name}\" alongside it? Two competitions will run in parallel.",
  "comp.notCompDay": "Sessions can only be started on competition days",
  "comp.sessionControl": "Session control",
  "comp.lockAttempts": "Lock recording",
  "comp.unlockAttempts": "Unlock recording",
  "comp.lockAttemptsHint": "Lock attempt recording — judges and competitors can't record new attempts (admin/organizer corrections still allowed).",
  "comp.unlockAttemptsHint": "Unlock attempt recording.",
  "comp.attemptsLockedBanner": "Attempt recording is locked for this competition.",
  "comp.attemptsLockedToast": "Attempt recording is locked.",
  "comp.sessionStart": "Start",
  "comp.sessionStop": "Stop",
  "comp.sessionPause": "Pause",
  "comp.sessionResume": "Resume",
  "comp.sessionFinish": "Finish",
  "comp.sessionRestart": "Restart",
  "comp.sessionRunning": "Running",
  "comp.sessionPaused": "Paused",
  "comp.sessionScheduled": "Scheduled",
  "comp.sessionFinished": "Finished",
  "comp.sessionStartTime": "Start: {time}",
  "comp.sessionDuration": "{minutes} min",
  "comp.sessionParticipants": "{count} participants",
  "comp.sessionAutoStopped": "Session ended automatically",
  "comp.sessionCantStart": "Only one session may run at a time — finish the current one first",
  "comp.sessionAdjustRemaining": "Adjust remaining time",

  // Competition editor
  "comp.editor.title": "Edit competition",
  "comp.editor.titleNew": "Create new competition",
  "comp.editor.tabRoutes": "Routes",
  "comp.editor.tabRoutes.short": "Routes",
  "comp.editor.tabGroups": "Groups",
  "comp.editor.tabGroups.short": "Groups",
  "comp.editor.tabSessions": "Sessions",
  "comp.editor.tabSessions.short": "Sess.",
  "comp.editor.tabSettings": "Settings",
  "comp.editor.tabSettings.short": "Settings",
  "comp.editor.tabJudges": "Judges",
  "comp.editor.tabJudges.short": "Judges",
  "comp.editor.tabParticipants": "Participants",
  "comp.editor.tabParticipants.short": "Particip.",
  "comp.editor.tabEntryFees": "Entry Fees",
  "comp.editor.tabEntryFees.short": "Fees",
  "comp.editor.name": "Competition name",
  "comp.editor.description": "Short description",
  "comp.editor.descriptionHint": "One or two sentences — always visible on cards and at registration",
  "comp.editor.longDescription": "Full description",
  "comp.editor.longDescriptionHint": "The complete announcement — shown collapsed by default on cards and the registration page",
  "comp.editor.rules": "Rules",
  "comp.editor.rulesHint": "Shown during registration, competitor must accept",
  "localizedText.label": "Text language",
  "localizedText.base": "default",
  "localizedText.filled": "Has text in this language",
  "localizedText.empty": "No text in this language yet",
  "localizedText.hintBase": "The gym's default language. Where a field is empty in another language, viewers see this text.",
  "localizedText.hintTranslation.hu": "Translation. Anything left empty is shown to viewers in Hungarian.",
  "localizedText.hintTranslation.en": "Translation. Anything left empty is shown to viewers in English.",
  "localizedText.fallback.hu": "If left empty, the Hungarian text is shown.",
  "localizedText.fallback.en": "If left empty, the English text is shown.",
  "comp.editor.type": "Type",
  "comp.editor.startDate": "Start date",
  "comp.editor.endDate": "End date",
  "comp.editor.nameRequired": "Competition name is required",
  "comp.editor.groupNameRequired": "Every group must have a name",
  "comp.editor.startNumbersBackfilled": "Assigned start numbers to {count} earlier registrations",
  "comp.editor.startNumbersBackfillFailed": "Could not assign start numbers to earlier registrations",
  "comp.editor.invalidDateRange": "Valid start and end dates are required (YYYY-MM-DD)",
  "comp.editor.endBeforeStart": "End date cannot be earlier than start date",
  "comp.editor.coverImage": "Cover image",
  "comp.editor.coverHint": "Recommended: 1200×400 px · Max 5 MB · JPG, PNG, WebP",
  "comp.editor.externalUrl": "External link",
  "comp.editor.resultsUrl": "Results link",
  "comp.editor.routeCount": "{count} routes selected",
  "comp.editor.problemCount": "{count} problems selected",
  "comp.editor.source": "Source",
  "comp.editor.sourceRoutes": "Routes",
  "comp.editor.sourceSpray": "Spray",
  "comp.editor.sourceSprayHint": "Only benchmark problems can be picked for a spray competition. A problem's points are those of the difficulty level its Font grade falls in; they are fixed when you save.",
  "comp.editor.sourceSwitchConfirm": "Switching the source clears the selected routes and their points.",
  "comp.editor.sprayScoringForced": "A spray competition is scored Classic Redpoint.",
  "comp.editor.sprayNoBand": "no band",
  "comp.editor.sprayNoFinals": "A spray competition has no finals — delete the finals session before saving.",
  "comp.editor.sprayNoBenchmarks": "There is no benchmark problem on the Spraywall. A moderator can mark benchmarks.",
  "comp.editor.noGroupsForRoutes": "Create groups first to assign routes",
  "comp.editor.multiGroupHint": "Ctrl / ⌘ / Shift + click to select multiple groups and edit them at once",
  "comp.editor.selectAll": "Select all",
  "comp.editor.deselectAll": "Deselect all",
  "comp.editor.bulkAddLabel": "New route:",
  "comp.editor.bulkAddRoutes": "+ Create",
  "comp.editor.bulkAddHint": "Create empty routes (no difficulty or hold color) and assign them immediately to the selected group(s). Names are numbered — the start number, a prefix and a suffix can all be set. They can be repositioned / graded on the map later.",
  "comp.editor.bulkAdvanced": "Advanced generation",
  "comp.editor.bulkPrefix": "Prefix",
  "comp.editor.bulkStart": "Start number",
  "comp.editor.bulkCount": "Count",
  "comp.editor.bulkSuffix": "Suffix",
  "comp.editor.bulkPrefixPlaceholder": "e.g. A",
  "comp.editor.bulkSuffixPlaceholder": "e.g. -F",
  "comp.editor.bulkPreview": "They will be named",
  "comp.editor.groupName": "Group name",
  "comp.editor.groupMode": "Mode",
  "comp.editor.groupColor": "Color",
  "comp.editor.sessionName": "Session name",
  "comp.editor.sessionStart": "Start",
  "comp.editor.sessionDuration": "Duration (min)",
  "comp.editor.durationUnit": "min",
  "comp.editor.sessionMaxParticipants": "Max capacity",
  "comp.editor.sessionGroups": "Groups",
  "comp.editor.hasZones": "Zone system",
  "comp.editor.scoringSystem": "Scoring system",
  "comp.editor.pointPool": "Point pool (per route)",
  "comp.editor.sectionScoring": "Scoring",
  "comp.editor.sectionRegistration": "Registration",
  "comp.editor.countBest": "Count best routes",
  "comp.editor.countBestHint": "How many best topped routes count toward the total score",
  "comp.editor.countBestSpray": "Count best problems",
  "comp.editor.countBestHintSpray": "How many best topped problems count toward the total score",
  "comp.editor.flashBonusHintSpray": "Extra points for problems topped on first attempt (empty = no bonus)",
  "challenge.editor.countAllSpray": "Count all problems",
  "challenge.editor.countAllHintSpray": "When off, only the best N problems count.",
  "comp.editor.noGroupsForProblems": "Create groups first to assign problems",
  "comp.editor.filterAllMaps": "All maps",
  "comp.editor.filterAllWalls": "All walls",
  "comp.editor.filterAllLevels": "All levels",
  "comp.closeConfirmSpray": "Close \"{name}\"? Final results will be recorded.\n\nThe tops go into the competitors' Spraywall log: they count as climbs and active days, not points. Guests' tops do not.",
  "comp.scorecard.problemName": "Problem",
  "comp.editor.flashBonus": "Flash bonus",
  "comp.editor.flashBonusHint": "Extra points for routes topped on first attempt (empty = no bonus)",
  // ── Challenge ──
  "challenge.editor.countAll": "Count all routes",
  "challenge.editor.countAllHint": "When off, only the best N routes count.",
  "challenge.editor.participation": "Participation",
  "challenge.editor.participationAuto": "Automatic",
  "challenge.editor.participationOptIn": "Opt-in",
  "challenge.editor.participationAutoHint": "Everyone who climbs in the window is on the board (can leave anytime).",
  "challenge.editor.participationOptInHint": "Only climbers who joined are shown.",
  "challenge.editor.saved": "Saved",
  "challenge.editor.saveFailed": "Save failed",
  "challenge.editor.source": "Source",
  "challenge.editor.sourceRoutes": "Routes",
  "challenge.editor.sourceSpray": "Spray",
  "challenge.editor.sourceSprayHint": "Only benchmark problems count in a spray challenge. A problem scores the points of the difficulty level its Font grade falls in.",
  "challenge.editor.sourceSprayNoBands": "The spray source needs a grade range on at least one difficulty level (Difficulty levels).",
  "challenge.editor.sourceSprayNoAddon": "The Spraywall add-on is off — members cannot log spray climbs now.",
  "challenge.dash.sourceNote": "Only benchmark spray problems count.",
  "challenge.unit.problems": "{n} problems",
  "challenge.dash.climber": "Climber",
  "challenge.dash.points": "Points",
  "challenge.dash.empty": "Nobody has scored points in this period yet.",
  "challenge.dash.noRank": "no rank",
  "challenge.dash.showAll": "Show all {count}",
  "challenge.dash.showLess": "Show less",
  "challenge.dash.partLabel": "I'm taking part",
  "challenge.dash.partHintOn": "Your ascents earn points in this competition.",
  "challenge.dash.partHintOff": "Turn on so your ascents earn points.",
  "challenge.exclude.label": "Exclusion",
  "challenge.exclude.normal": "Counts on the board",
  "challenge.exclude.noRank": "Visible, no rank",
  "challenge.exclude.hidden": "Hidden from board",
  "challenge.exclude.saveFailed": "Could not save the exclusion — try again.",
  "challenge.dash.excludedRoutes": "{count} routes do not count: {names}",
  "challenge.routes.help": "Selected routes score nothing in this challenge.",
  "challenge.routes.count": "{count} routes excluded",
  "challenge.routes.save": "Save exclusions",
  "challenge.routes.saved": "Saved, the board is being recalculated.",
  "challenge.routes.saveFailed": "Could not save the exclusions — try again.",
  "challenge.routes.closedConfirm": "The closed board is recalculated; ranks and points may change.",
  "challenge.routes.archived": "archived",
  "challenge.routes.archivedIncomplete": "The archived routes could not be fully loaded; the list may be incomplete.",
  "challenge.routes.empty": "No such route.",
  "challenge.participants.hint": "Climbers on the board (derived from the window). Set who should get no rank, or be hidden from the board entirely.",
  "challenge.dash.excludedBanner": "An admin has removed you from this challenge.",
  "challenge.dash.noRankBanner": "You appear on the board but receive no rank.",
  "comp.editor.fillPoints": "Fill points",
  "comp.editor.fillPresetGym": "Copy gym points",
  "comp.editor.fillPresetLinear": "Linear (100–1000)",
  "comp.editor.fillPresetCustom": "Custom",
  "comp.editor.fillConfirm": "Overwrite existing points?",
  "comp.editor.maxAttempts": "Max attempts (per route)",
  "comp.editor.maxAttemptsHint": "Empty = unlimited",
  "comp.editor.maxAttemptsPlaceholder": "Unlimited",
  "comp.editor.ageRange": "Age category (from / to)",
  "comp.editor.ageMin": "from",
  "comp.editor.ageMax": "to",
  "comp.editor.ageRangeHint": "Empty = no age limit. The category is the age reached in the competition year.",
  "comp.editor.groupDescription": "Category description",
  "comp.editor.groupDescriptionHint": "Readable without signing in: shown on the registration page and on the card. Do not put personal data in it.",
  "comp.editor.groupMaxDifficulty": "Hardest level",
  "comp.editor.groupMaxDifficultyNone": "No upper limit",
  "comp.editor.groupMaxDifficultyHint": "A recommendation, not a limit. At registration this group is recommended to climbers whose level reaches up to here. A stronger climber may still enter, but gets flagged on the Participants list.",
  "comp.editor.groupMaxDifficultyDeleted": "This difficulty level no longer exists in the gym, so the group has no upper limit right now. Pick another level.",
  "comp.editor.groupMaxDifficultyDeletedOption": "(deleted level)",
  "comp.editor.ageRangeInvalid": "Minimum cannot be greater than maximum",
  "comp.editor.announcements": "Audio announcements",
  "comp.editor.warningMinutes": "Warning minutes",
  "comp.editor.tts": "Text-to-speech (TTS)",
  "comp.editor.useStartNumbers": "Start number system",
  "comp.editor.showRegCounts": "Show entry counts",
  "comp.editor.showRegCountsHint": "When off, entrants cannot see how many people are already in a session. The session capacity, \"Full\" and fewer than {n} spots left are always shown regardless.",
  "comp.editor.registrationStart": "Registration opens",
  "comp.editor.registrationDeadline": "Registration closes",
  "comp.editor.addJudge": "Add judge",
  "comp.editor.removeJudge": "Remove judge",
  "comp.editor.judgeSessions": "Sessions",
  "comp.editor.noJudges": "No judges assigned yet.",
  "comp.editor.removeOrganizer": "Remove organizer",
  "comp.editor.addParticipant": "Add participant",
  "comp.editor.removeParticipant": "Remove",
  "comp.editor.noParticipants": "No registered participants yet.",
  "comp.editor.saved": "Saved!",
  "comp.mode.jam": "JAM",
  "comp.mode.judged": "Judged",
  "comp.scoring.ifscClassic": "IFSC Classic",
  "comp.scoring.ifscClassicHint": "Rank by tops → zones → fewest attempts. Top automatically counts as zone.",
  "comp.scoring.ifsc2024": "IFSC 2024",
  "comp.scoring.ifsc2024Hint": "Top = 25 pts, Zone = 10 pts, −0.1 per attempt. Only highest hold counts.",
  "comp.scoring.ifsc2024NoZoneHint": "Without zones: Top = 25 pts, −0.1 per attempt. Zone data gives neither points nor a tiebreak.",
  "comp.scoring.topsAndZones": "Tops + Zones",
  "comp.scoring.topsAndZonesHint": "Score = tops × 25 + zones × 5. Tiebreak by fewer attempts.",
  "comp.scoring.points": "Point distribution",
  "comp.scoring.pointsHint": "Point pool per route divided equally among all who topped it.",
  "comp.scoring.classicRedpoint": "Classic Redpoint",
  "comp.scoring.classicRedpointHint": "Fixed point routes, best N tops counted toward total score.",

  // Competition user tab
  "comp.past": "Past competitions",
  "comp.register": "Register",
  "comp.registering": "Registering…",
  "comp.loadFailed": "Couldn't load the competitions — the connection appears to have stalled.",
  // Entries are committed one at a time (that is what stops a session
  // overfilling), so during a rush the back of the queue waits ten-plus seconds.
  "comp.registerBusyHint": "Many people are registering at once — your entry is queued, please wait.",
  "comp.acceptRules": "I accept the competition rules and regulations",
  "comp.unregister": "Unregister",
  "comp.selectGroup": "Select group",
  "comp.selectSession": "Select session",
  "comp.registrationClosed": "Registration closed",
  "comp.registrationOpens": "Registration opens: {date}",
  "comp.registrationDeadline": "Registration closes: {date}",
  "comp.registrationNotYetOpen": "Registration is not yet open",

  // Public /register landing — the shareable link for posters and Facebook
  "compReg.notFound": "This competition was not found, or is not published yet",
  "compReg.feeRange": "{min} – {max} {currency}",
  "compReg.factDeadline": "Registration closes",
  "compReg.factOpens": "Registration opens",
  "compReg.factEntered": "Registered so far",
  "compReg.competitorCount": "{n} competitors",
  "compReg.sessionFree": "{free} spots left",
  "compReg.ctaChange": "Change my registration",
  "compReg.viewDashboard": "Live results",
  "compReg.hintNeedsAccount": "You need a Sendly account to register — the button lets you sign in or create one, and takes you straight to the registration form.",
  "compReg.hintGuestAllowed": "You can register a guest too — someone without a Sendly account.",
  "compReg.hintRegistered": "Your guests and the entry fee status are on the competition card in the gym.",
  "compReg.hintClosedRegistered": "Your registration is confirmed. The deadline has passed, so it can no longer be changed — contact the organizers if you need to.",
  "compReg.hintStaffRole": "You don't need to register separately for this competition.",
  "compReg.hintOpensOn": "Registration opens on {date}.",
  "compReg.hintSignInEarly": "Sign in or create an account now, so entering is one click when registration opens.",
  "compReg.ctaSignIn": "Sign in or create an account",
  "compReg.ctaSignInAndRegister": "Sign in and register",
  "compReg.hintClosed": "The registration deadline has passed. Ask the gym about on-site registration.",
  "compReg.alreadyRole.competitor": "You are already registered for this competition",
  "compReg.alreadyRole.judge": "You are taking part in this competition as a judge",
  "compReg.alreadyRole.organizer": "You are taking part in this competition as an organizer",
  // Public event sign-up landing (/gym/:slug/event/:id/register)
  "eventReg.notFound": "This event could not be found or has not been announced yet",
  "eventReg.factDeadline": "Sign-up closes",
  "eventReg.factOpens": "Sign-up opens",
  "eventReg.factSpots": "Places",
  "eventReg.ctaChange": "Change my sign-up",
  "eventReg.ctaSignInAndRegister": "Sign in and sign up",
  "eventReg.ctaFull": "Full",
  "eventReg.already.participant": "You are signed up for this event",
  "eventReg.already.waitlist": "You are on the waitlist",
  "eventReg.guestCount": "Guests you signed up: {n}",
  "eventReg.hintCancelled": "This event has been cancelled. The organiser can tell you more.",
  "eventReg.hintEventClosed": "This event is over.",
  "eventReg.hintRegistered": "Your guests and the fee status are on the event card in the gym.",
  "eventReg.hintClosedRegistered": "Your sign-up is recorded. The deadline has passed, so it can no longer be changed — contact the organiser if you need a change.",
  "eventReg.hintWaitlisted": "If a place frees up, you move in in waitlist order. The event card in the gym shows when you are in.",
  "eventReg.hintNeedsAccount": "Signing up needs a Sendly account — the button lets you sign in or create one, and then takes you straight to the sign-up form.",
  "eventReg.hintGuestAllowed": "You can also sign up a guest — someone without a Sendly account, such as your child.",
  "eventReg.hintWaitlist": "All places are taken, but you can join the waitlist.",
  "eventReg.hintFull": "All places are taken, and this event has no waitlist.",
  "eventReg.hintOpensOn": "Sign-up opens on {date}.",
  "eventReg.hintSignInEarly": "Sign in or create an account now, so that signing up is a single click once it opens.",
  "eventReg.hintClosed": "The sign-up deadline has passed. The organiser or the gym can tell you more.",
  "eventReg.ageRange": "Ages {min}–{max}",
  "eventReg.ageMin": "Ages {min}+",
  "eventReg.ageMax": "Up to age {max}",
  "comp.rules": "Rules",
  "comp.enter": "Enter",
  "comp.viewResults": "Results",
  "comp.externalLink": "Details (external link)",
  "comp.resultsLink": "Results",
  "comp.noActiveSession": "No active session",

  // Registration flow — step accordion + missing-field feedback
  "comp.reg.step.group": "Group",
  "comp.reg.step.session": "Session",
  "comp.reg.step.groupHint": "Which category are you entering?",
  "comp.reg.step.groupHintOther": "Which category are they entering?",
  "comp.reg.step.sessionHint": "When do you climb?",
  "comp.reg.step.sessionHintOther": "When do they climb?",
  "comp.reg.step.billingHint": "Who should the invoice be made out to?",
  "comp.reg.step.billingOptionalHint": "Optional — the competitor can add it themselves later",
  "comp.reg.step.rulesHint": "Read and accept",
  "comp.reg.chip.required": "Required",
  "comp.reg.chip.done": "Done",
  "comp.reg.chip.missing": "Missing",
  "comp.reg.chip.optional": "Optional",
  "comp.reg.missing": "Missing: {field}",
  "comp.reg.jump": "Go there",
  "comp.reg.rulesAcceptedShort": "Accepted",
  "comp.reg.err.required": "Required field",
  "comp.reg.step.guest": "Guest details",
  "comp.reg.step.guestHint": "Who are you entering?",
  "comp.reg.step.consent": "Consent",
  "comp.reg.step.consentHint": "Declaration on the guest's behalf",
  "comp.reg.consentGiven": "Given",
  "comp.reg.billingSharedHint": "One billing record per competition — it covers your guests too, no need to enter it again.",
  "comp.reg.billingGuestOwnHint": "This guest pays their own entry fee — these details go on their invoice.",

  // Registration refused by the server (the form went stale: session filled up, deadline passed)
  "comp.reg.err.sessionFull": "This session filled up in the meantime. Pick another one.",
  "comp.reg.err.notActive": "This competition is not accepting registrations.",
  "comp.reg.err.closed": "Registration has been closed.",
  "comp.reg.err.notStarted": "Registration has not opened yet.",
  "comp.reg.err.deadline": "The registration deadline has passed.",
  // Contention, not a rejection. The second sentence matters because the client
  // can time out while the server still commits the entry.
  "comp.reg.err.busy": "Too many people are registering at once — try again in a moment. Your entry may have gone through in the meantime: check the competition card.",

  // Competition view (JAM)
  // Everything captured offline has to be uploaded before handing in — the server accepts no attempts afterwards
  "comp.view.countdown": "Time remaining",
  "comp.view.routes": "Competition routes",
  "comp.view.timeline": "Timeline",

  // Session timeline — "what did I climb, in what order, with how many tries"
  "comp.timeline.title": "Timeline",
  "comp.timeline.sessionTitle": "Your session timeline",
  "comp.timeline.myTimeline": "My timeline",
  "comp.timeline.climber": "Climber",
  "comp.timeline.me": "Me",
  "comp.timeline.noOtherClimbers": "No other climber to look at",
  "comp.timeline.now": "now",
  "comp.timeline.chartAlt": "Session timeline: which route when, and with how many attempts",
  "comp.timeline.axis.difficulty": "Difficulty",
  "comp.timeline.axis.points": "Points",
  "comp.timeline.axis.pointsShort": "pts",
  "comp.timeline.axis.label": "Vertical axis",
  "comp.timeline.pill.routes": "routes",
  "comp.timeline.pill.problems": "problems",
  "comp.timeline.pill.attempts": "attempts",
  "comp.timeline.pill.tops": "{count} top",
  "comp.timeline.pill.zones": "{count} zone",
  "comp.timeline.pill.active": "active",
  "comp.timeline.legend.top": "Top",
  "comp.timeline.legend.zone": "Zone",
  "comp.timeline.legend.none": "Not sent",
  "comp.timeline.legend.number": "number = attempts",
  "comp.timeline.row.topAt": "Top on attempt {n}",
  "comp.timeline.row.zone": "Zone",
  "comp.timeline.row.attempts": "{count} attempts",
  "comp.timeline.row.sendOfAttempts": "from {count} tries",
  "comp.timeline.row.notYet": "not sent yet",
  "comp.timeline.row.flash": "flash",
  "comp.timeline.dur.min": "{n} min",
  "comp.timeline.dur.hour": "{n} h",
  "comp.timeline.dur.day": "{n} days",
  // First and last save on different days is not time on the route — it is
  // a scorecard the climber reopened later
  "comp.timeline.row.laterEdit": "added to later",
  "comp.timeline.row.backfilled": "recorded afterwards",
  "comp.timeline.row.noDifficulty": "no difficulty",
  "comp.timeline.row.pointsUnit": "pts",
  "comp.timeline.row.send": "Sent",
  "comp.timeline.row.repeat": "Repeat",
  "comp.timeline.event.fall": "fall",
  "comp.timeline.event.zone": "zone",
  "comp.timeline.event.top": "top",
  "comp.timeline.backfilledHint": "A result recorded afterwards (Excel import or an admin edit) has no real climbing time, so it stays off the timeline.",
  "comp.timeline.empty.notStarted": "Your session hasn't started yet.",
  "comp.timeline.empty.notStartedHint": "Once it starts, you'll see here — in order — which route you went for and with how many attempts.",
  "comp.timeline.empty.noAttempts": "No attempt recorded yet.",
  "comp.timeline.empty.noAttemptsHint": "As you save your results, the routes appear here in order.",
  "comp.timeline.empty.notEntered": "You didn't compete in this competition.",
  "comp.timeline.empty.notEnteredOther": "This climber didn't compete in this competition.",
  "comp.timeline.day.title": "The day's timeline",
  "comp.timeline.day.competition": "Competition",
  "comp.timeline.day.noAttemptData": "Attempt counts appear only where you entered them; other climbs have no count — the order and the difficulty arc are complete.",
  "comp.view.noRoutes": "No competition routes yet",
  "comp.view.noRoutesHint": "As soon as the organizer assigns the competition routes, they show up here — and you'll find them on the Map tab too.",
  // The filter name comes from the map ({filter}) so the two labels can't drift apart
  "comp.view.routesOnMap": "You can also find these routes on the Map tab — the 🏆 {filter} switch narrows the map down to them.",
  "comp.view.routesOnMapLater": "The competition routes also appear on the Map tab once your session starts.",

  // Classic Redpoint summary
  "comp.redpoint.total": "Total points",
  "comp.redpoint.flashBonusTotal": "incl. {count}⚡ flash bonus: +{points}",
  "comp.redpoint.notCounted": "not counted",
  "comp.redpoint.notTopped": "not topped",

  // Competition attempt modal
  "comp.attempt.zoneAttempts": "Zone attempts",
  "comp.attempt.zoneReached": "Zone reached",
  "comp.attempt.topAttempts": "Top attempts",
  "comp.attempt.topped": "TOP",
  "comp.attempt.zone": "Zone",
  "comp.attempt.top": "Top",
  "comp.attempt.attempts": "Attempts",
  "comp.attempt.fewer": "One attempt fewer",
  "comp.attempt.more": "One attempt more",
  "comp.attempt.reachedAtAttempt": "Reached on attempt {n}",
  "comp.attempt.notReached": "Not reached",
  "comp.attempt.maxLabel": "max",
  "comp.attempt.openDetail": "Open details",
  "comp.attempt.compPoints": "pts",
  "comp.attempt.flash": "⚡ Flash!",
  "comp.attempt.flashBonus": "+{n} flash bonus",
  "comp.attempt.save": "Save",
  "comp.attempt.saveFailed": "Save failed — the row now shows the value stored on the server. Check your connection and try again.",
  "comp.attempt.notRecordedYet": "Not recorded yet",
  "comp.attempt.triedNotReached": "{n} attempts — not done yet",
  "comp.attempt.topImpliesZone": "A top counts as a zone too",
  "comp.attempt.offlineNote": "You're offline — your save is queued and uploads automatically.",
  "comp.attempt.saveOfflineSub": "Queued · uploads when back online",
  "comp.attempt.readOnly.notStarted": "Your session hasn't started yet — view only.",
  "comp.attempt.readOnly.locked": "The organizer has locked attempt recording.",
  "comp.attempt.readOnly.judged": "In this group the judge records the result.",
  "comp.attempt.readOnly.closed": "The competition is closed, results are final.",
  "comp.attempt.readOnly.notRegistered": "You're not entered in this competition — view only.",
  "comp.attempt.pending": "Pending",
  "comp.attempt.savedToast": "{route} — saved",
  "comp.attempt.queuedToast": "{route} — queued, will upload",
  "comp.attempt.undo": "Undo",
  "comp.attempt.competitor": "Competitor",
  "comp.attempt.registeredBy": "Registered by",

  // Competition judge
  "comp.judge.title": "Judge mode",
  "comp.judge.selectClimber": "Select climber",
  "comp.judge.searchClimber": "Search by name or bib number...",
  "comp.judge.eventFall": "Fall",
  "comp.judge.eventZone": "Zone",
  "comp.judge.eventTop": "Top",
  "comp.judge.currentState": "So far",
  "comp.judge.attemptsCount": "{n} attempts",
  "comp.judge.zoneStatus": "Zone",
  "comp.judge.topStatus": "Top",
  "comp.judge.zoneAtAttempt": "attempt {n}",
  "comp.judge.notReached": "none",
  "comp.judge.myEventsRecent": "Your recent events (30s)",
  "comp.judge.undo": "Undo",
  "comp.judge.recordError": "Failed to record event",
  "comp.judge.undoError": "Undo failed",
  "comp.judge.maxAttemptsReached": "Max attempts reached",
  "comp.judge.backToCompetitors": "Back",
  "comp.judge.maxAttemptsLabel": "Max {n} attempts",
  "comp.judge.noAssignedSessions": "No judged session is assigned to you. Ask the competition admin to assign you at least one session on the Judges tab.",
  "comp.judge.noCompetitorsInSession": "No competitors in the judged groups assigned to you.",
  "comp.judge.noSearchResults": "No competitors match your search.",
  "comp.judge.offline": "Offline",
  "comp.judge.pendingSync": "{n} event(s) waiting to sync",
  "comp.judge.syncing": "Syncing {n} event(s)…",
  "comp.judge.resync": "Retry sync",
  "comp.judge.pendingTooltip": "Your events are safe on this device and will be sent automatically once the connection returns.",

  // Competition leaderboard
  "comp.leaderboard.title": "Leaderboard",
  "comp.leaderboard.rank": "Rank",
  "comp.leaderboard.name": "Name",
  "comp.leaderboard.startNumber": "#",
  "comp.leaderboard.tops": "Tops",
  "comp.leaderboard.zones": "Zones",
  "comp.leaderboard.top": "Top",
  "comp.leaderboard.zone": "Zone",
  "comp.competitor.noRoutes": "No routes assigned to this group.",
  "comp.dashboard.viewCompetitorDetail": "Show per-route breakdown",
  "comp.scorecard.topZoneAtt": "T / z",
  "comp.scorecard.attempts": "Att",
  "comp.leaderboard.attempts": "Att.",
  "comp.leaderboard.score": "Score",
  "comp.leaderboard.empty": "No results yet.",

  // Competition countdown
  "comp.countdown.warning": "{minutes} minutes left!",
  "comp.countdown.lastSeconds": "Last {seconds} seconds!",
  "comp.countdown.ended": "TIME!",

  // Competition dashboard
  "comp.dashboard.live": "LIVE",
  "comp.dashboard.groupLabel": "Group:",
  "comp.dashboard.draftBanner": "Draft — this competition is not announced yet. Only its organizers see this preview; for everyone else the page does not exist.",
  "comp.dashboard.finalsCutoff": "Finals — {n} advance",
  "comp.dashboard.viewLabel": "View:",
  "comp.dashboard.viewList": "Ranking",
  "comp.dashboard.viewMatrix": "Scorecard",
  "comp.matrix.flash": "Flash",
  "comp.matrix.top": "Top",
  "comp.matrix.zone": "Zone",
  "comp.matrix.attemptsHint": "Numbers = attempts",
  "comp.matrix.attemptsWord": "attempts",
  "common.previous": "Previous",
  "common.next": "Next",

  // Competition TV dashboard
  "comp.tv.enableAudio": "Enable audio",

  // Competition export
  "comp.export.csv": "Results (CSV)",
  "comp.export.pdf": "Results (PDF)",

  // Competition QR
  "comp.qr.generate": "QR code",
  "comp.deactivate": "Deactivate",
  "comp.deactivateConfirm": "Deactivate this competition? It will return to draft status.",
  "comp.changeRegistration": "Change",
  "comp.friends.label": "Your friends:",
  "comp.updateRegistration": "Update registration",
  "comp.sessionFull": "Full",
  "comp.details": "Details",
  "comp.overview": "Overview",
  "comp.alreadyCompetitor": "Already registered as competitor",
  "comp.alreadyJudge": "Already registered as judge",
  "comp.alreadyOrganizer": "Already registered as organizer",
  "comp.editor.tabOrganizers": "Organizers",
  "comp.editor.tabOrganizers.short": "Organiz.",
  "comp.editor.addOrganizer": "Add organizer",
  "comp.editor.noOrganizers": "No organizers assigned yet.",
  "comp.editor.deleteSessionWithRegs": "This session has {count} competitors assigned. Deleting will unassign them. Are you sure?",
  "comp.editor.deleteSessionConfirm": "Are you sure you want to delete this session?",
  "comp.editor.deleteGroupWithRegs": "This group has {count} competitors assigned. Deleting will unassign them. Are you sure?",
  "comp.editor.deleteGroupConfirm": "Are you sure you want to delete this group?",
  "comp.closeEditor": "Close",
  "comp.openDashboard": "Dashboard",
  // Card section headers & actions
  "comp.card.regSection": "Registration",
  // Label of the filled header bar when the user has an entry or a guest here.
  "comp.card.regState.mine": "✓ Registered",
  "comp.card.regState.guestOnly": "✓ Guest entered",
  // Right end of the bar: who the entry covers. Alone it is the group · session,
  // with guests a head count, and for a single guest the name itself.
  "comp.card.regWho.mePlusOne": "You + {name}",
  "comp.card.regWho.mePlusN": "You + {n} guests",
  "comp.card.regWho.guests": "{n} guests",
  "comp.regRowActions": "Actions for {name}'s registration",
  "comp.card.kicker.competition": "Competition",
  "comp.card.kicker.challenge": "Challenge",
  "comp.card.kicker.external": "External competition",
  "comp.card.kicker.series": "Series",
  "comp.card.organizerBar": "Organizer",
  "comp.card.expand": "Expand card",
  "comp.card.collapse": "Collapse card",
  "comp.card.row.when": "When",
  "comp.card.row.registration": "Registration",
  "comp.card.row.you": "You",
  "comp.card.row.friends": "Friends",
  "comp.card.row.entrants": "Entrants",
  "comp.card.row.field": "Field",
  "comp.card.row.rounds": "Rounds",
  "comp.card.row.recording": "Recording",
  "comp.card.row.createdBy": "Created by",
  "comp.card.reg.until": "until {date}",
  "comp.card.reg.opensAt": "from {date}",
  "comp.card.reg.untilStart": "until the start day",
  "comp.card.reg.atOrganizer": "On the organizer's site",
  "comp.card.reg.guestHint": "You can enter a guest too ({n}/{max})",
  "comp.card.recording.open": "Open",
  "comp.card.recording.locked": "Locked",
  "comp.card.groupSummary.jam": "{n} JAM",
  "comp.card.groupSummary.judged": "{n} judged",
  "comp.card.sessionsCount": "{n} sessions",
  "comp.card.noSessionsYet": "no sessions yet",
  "comp.card.participantCount": "{n} participants",
  "comp.card.expander.age": "Age group",
  "comp.card.expander.attempts": "Attempts",
  "comp.card.expander.mode": "Mode",
  "comp.card.expander.maxDifficulty": "Hardest level",
  "comp.card.expander.noAgeLimit": "no limit",
  "comp.card.expander.unlimited": "unlimited",
  "comp.card.expander.open": "Details of {name}",
  "comp.control": "Control",
  "comp.manage": "Edit",
  "comp.registerOwn": "+ Register myself",
  "comp.unregisterShort": "Withdraw registration",
  "comp.role.judge": "Judge",
  "comp.role.organizer": "Organizer",
  "comp.role.judgeSessions": "Session: {sessions}",
  "comp.feeStatus.paid": "{paid} ✓ paid",
  "comp.feeStatus.partial": "{paid} / {total}",
  "comp.feeStatus.unpaid": "{amount} — payment due",
  "comp.unregister.paidBlock": "You cannot withdraw because there is a paid amount on record. Please contact the organizers!",
  "comp.entryFees.filterAll": "All",
  "comp.entryFees.filterUnpaid": "Unpaid",
  "comp.entryFees.filterPartial": "Partial",
  "comp.entryFees.filterPaid": "Paid",
  "comp.entryFees.totalExpected": "Expected",
  "comp.entryFees.totalPaid": "Paid",
  "comp.entryFees.outstanding": "Outstanding",
  "comp.entryFees.noEntries": "No payment entries",
  "comp.entryFees.searchPlaceholder": "Search by name, email, group",
  "comp.entryFees.searchNoMatch": "No match",
  "comp.entryFees.methodLabel": "Payment method",
  "comp.entryFees.methodNone": "— method",
  "comp.entryFees.method.card": "Card",
  "comp.entryFees.method.transfer": "Transfer",
  "comp.entryFees.method.cash": "Cash",
  "comp.entryFees.unregisteredParent": "Guests only",
  "comp.guest.addBtn": "+ Register guest",
  "comp.guest.addBtnCounter": "+ Register guest ({n}/{max})",
  "common.more": "Other",
  "comp.menu.printable": "Printable",
  "comp.menu.export": "Export",
  "comp.menu.other": "Tools",
  "common.qrCode": "QR code",
  "comp.overview.competitors": "Competitors",
  "comp.overview.judges": "Judges",
  "comp.overview.groups": "Groups",
  "comp.overview.sessions": "Sessions",
  "comp.overview.unassigned": "Unassigned",
  "comp.overview.headcount": "Capacity",
  "comp.qr.download": "Download QR code",
  "comp.qr.copyLink": "Copy link",
  "comp.qr.linkCopied": "Link copied to clipboard",
  "comp.qr.linkCopyFailed": "Could not copy to clipboard — select the link manually.",
  "comp.qr.targetLabel": "Link target",
  "comp.qr.targetRegister": "Registration",
  "comp.qr.targetDashboard": "Live results",
  "comp.copyRegLink": "Copy registration link",
  "comp.editAttempts": "Edit attempts",

  // Entry fee
  "comp.entryFee.label": "Entry fee",
  "comp.entryFee.enabled": "Has entry fee",
  "comp.entryFee.amount": "Amount",
  "comp.entryFee.currency": "Currency",
  "comp.entryFee.note": "Note",
  "comp.entryFee.notePlaceholder": "e.g. Payable on site in cash",
  "comp.entryFee.paid": "Paid",
  "comp.entryFee.overviewBadge": "{paid}/{total} paid",
  "comp.entryFee.unpaidWarning": "Entry fee not paid. Your results will not appear in the rankings until payment is confirmed.",
  "comp.entryFee.unpaidBadge": "Not paid",
  "comp.entryFee.groupAmount": "Entry fee (group)",
  "comp.entryFee.groupAmountHint": "If empty: {amount} {currency} (competition default)",

  // Billing
  "settings.billingInfo": "Billing Information",
  "billing.individual": "Individual",
  "billing.company": "Company",
  "billing.namePlaceholder.individual": "Full name",
  "billing.namePlaceholder.company": "Company name",
  "billing.taxNumber": "Tax number",
  "billing.zip": "ZIP code",
  "billing.city": "City",
  "billing.address": "Street, number",
  "billing.country": "Country",
  "billing.saved": "Saved!",
  "billing.saveToProfile": "Save to my profile",
  "billing.saveToProfileHint": "Next time these details will already be filled in.",
  "billing.err.name": "Enter the billing name",
  "billing.err.companyName": "Enter the company name",
  "billing.err.taxNumber": "Enter the tax number",
  "billing.editSection": "Billing details",
  "billing.vatId": "EU VAT ID",
  "billing.vatIdPlaceholder": "HU12345678",
  "billing.contactName": "Contact name",
  "billing.invoiceEmail": "Invoice email",
  "billing.companyName": "Company / sole-trader name",
  "billing.gymSectionTitle": "Billing details",
  "billing.gymSectionHint": "We use these fields to issue your quarterly invoice. The platform operator sends a billing summary and invoice every quarter.",
  "billing.save": "Save billing details",
  "billing.savedToast": "Billing details saved.",
  "billing.required": "Company name and tax number are required for billing.",
  "billing.complete": "Complete",
  "billing.incomplete": "Missing",
  "billing.invoiceCurrencyNotice": "💱 You will be invoiced in this currency: {currency} (change above under the Currency picker).",
  "billing.invoiceCurrencyNoticeLocked": "💱 You will be invoiced in this currency: {currency}. The currency has been locked by the platform operator — contact us to change it.",
  "billing.currencyLockedNotice": "The billing currency has been locked by the platform operator.",
  "billing.currencyNoCapNotice": "You don't have permission to change the currency.",
  "billing.vatIdRequiredEu": "EU-based companies need an EU VAT ID for reverse-charge invoicing. Until it is provided, {rate}% VAT is charged.",
  // Dynamic keys: billing.vatStatusName.{domestic|reverse_charge|out_of_scope|eu_missing_vat_id}
  "billing.vatStatusName.domestic": "VAT applies ({rate}% VAT)",
  "billing.vatStatusName.reverse_charge": "reverse charge (0% VAT)",
  "billing.vatStatusName.out_of_scope": "outside the scope of VAT (0% VAT)",
  "billing.vatStatusName.eu_missing_vat_id": "EU VAT ID missing — {rate}% VAT",
  "comp.billing.guestSummary": "Your guests: {count} — you are paying their fees too.",
  "comp.billing.exportExcel": "Billing list (Excel)",
  "comp.billing.pdfTitle": "Billing List",
  "comp.billing.pdfTotal": "Total",
  "comp.billing.pdfCompetitor": "Competitor",
  "comp.billing.pdfBillingName": "Billing name",
  "comp.billing.pdfAddress": "Address",
  "comp.billing.pdfTaxNumber": "Tax number",
  "comp.billing.pdfCount": "Persons",
  "comp.billing.xlsExpected": "Expected",
  "comp.billing.xlsPaid": "Paid amount",
  "comp.billing.xlsPaidFlag": "Paid (X)",
  "comp.billing.xlsOutstanding": "Outstanding",
  "comp.billing.xlsSummary": "--- Summary ---",
  "comp.billing.xlsPayingUsers": "Paying users",
  "comp.billing.xlsFullyPaid": "Fully paid",
  "comp.entryFee.paidAmountPrompt": "Amount paid ({currency}):",

  // Organiser contact (organizerContact) — shared by competitions, series and events.
  // NOT the "organizer" ROLE (a gym member with write access): this is contact info.
  "organizerContact.title": "Organiser contact",
  "organizerContact.name": "Name",
  "organizerContact.email": "Email",
  "organizerContact.phone": "Phone",
  "organizerContact.gymFallback": "The gym's contact",
  "organizerContact.publicHint": "Visible without logging in.",
  "organizerContact.fallbackPreview": "Left empty, the gym's contact is shown:",
  "organizerContact.noGymContact": "The gym has no contact email or phone either, so entrants will find no way to get in touch. Fill in the organiser here, or add the gym's contact details on the gym profile.",

  // Guest competitors
  "comp.settings.allowGuests": "Guest competitors",
  "comp.settings.allowGuestsHint": "Register competitors without a Sendly account (e.g. kids competition)",
  "comp.settings.guestMode.off": "Off",
  "comp.settings.guestMode.organizer": "Organizers only",
  "comp.settings.guestMode.anyone": "Anyone",
  "comp.settings.guestMode.offHint": "No guest competitors.",
  "comp.settings.guestMode.organizerHint": "Only you and the organizers can add guests on the Participants tab. Competitors see no guest button.",
  // The number itself lives in the field below, not here — one place on screen.
  "comp.settings.guestMode.anyoneHint": "Competitors may also register guests, into judged groups.",
  "comp.settings.proxyGuestLimit": "Guests one person may enter",
  "comp.settings.proxyGuestLimitHint": "Applies to everyone — you and the organizers included. If you need more than this, the Participants tab has no limit. ({min}–{max})",
  "comp.guest.addTitle": "Add guest competitor",
  "comp.guest.name": "Competitor name",
  "comp.guest.namePlaceholder": "Name",
  "comp.guest.birthYear": "Birth year",
  "comp.guest.birthYearPlaceholder": "e.g. 2015",
  "comp.guest.email": "E-mail address",
  "comp.guest.emailPlaceholder": "e.g. parent@example.com",
  "comp.guest.emailHint": "Optional. Notifications and the invoice go here — for a guest this is often the parent's address.",
  "comp.guest.emailInvalid": "Invalid e-mail address",
  "comp.age.tooYoung": "Too young for this age category",
  "comp.age.tooOld": "Too old for this age category",
  "comp.level.recommended": "Recommended",
  "comp.level.yours": "Your climbing level in this gym: {level}",
  "comp.level.above": "Your climbing level ({level}) is above this group's limit ({cap}). You can still enter, but the organizers will see it.",
  "comp.level.aboveOther": "The climber's level ({level}) is above this group's limit ({cap}). They can still be entered, but get flagged on the Participants list.",
  "comp.level.theirs": "The climber's level in this gym: {level}",
  "comp.level.approx": "approx. {level}",
  "comp.level.yoursGlobal": "Your climbing level, from your level in other gyms: {level}",
  "comp.level.theirsGlobal": "The climber's level, from their level in other gyms: {level}",
  "comp.level.theirsNone": "The climber has no climbing level yet: fewer than {min} first ascents in this gym, and none that can be carried over from other gyms.",
  "comp.age.birthYearRequired": "Birth year is required for this age category",
  "comp.age.birthYearMissingHint": "Set your birth year in settings to register for age-restricted categories",
  "comp.age.birthYearMissingOther": "The competitor has no birth year on file — they cannot be entered in an age-restricted category",
  "comp.guest.club": "Club / team",
  "comp.guest.clubPlaceholder": "Club name",
  "comp.guest.notes": "Notes",
  "comp.guest.notesPlaceholder": "Internal note (organizers only)",
  "comp.guest.add": "Add",
  "comp.guest.addAndNext": "Add and next",
  "comp.guest.added": "{name} added",
  "comp.guest.addedCount": "{count} guests added in this session",
  "comp.guest.onlyJudged": "Guest competitors can only be registered in judged groups.",
  "comp.guest.noJudgedGroups": "No judged groups. Create a judged group to register guest competitors.",
  "comp.guest.noGroups": "No groups. Create at least one group to register guest competitors.",
  "comp.guest.jamGroupHint": "JAM group: the guest cannot record their own results — you enter their attempts under \"Edit attempts\".",
  "comp.guest.proxyTitle": "Register a guest",
  "comp.guest.proxyButton": "Register guest",
  "comp.guest.badge": "Guest",
  "comp.guest.deleteConfirm": "Are you sure you want to remove {name}'s registration?",
  "comp.guest.editTitle": "Edit guest",
  "comp.guest.consent": "I confirm that the guest (or their legal guardian) has consented to the registration",
  "comp.guest.proxyLimit": "You have reached the maximum of {max} guest registrations.",
  "comp.participants.addGuest": "+ Add guest",
  "comp.participants.addUserTitle": "Register competitor",
  "comp.participants.noEmail": "No e-mail address",
  "comp.participants.noGroups": "No groups yet. Create at least one group to register competitors.",
  "comp.participants.ageCheckedHint": "Age-restricted group — the entry is checked against the competitor's birth year.",
  "comp.participants.deleteConfirm": "Are you sure you want to remove {name}'s registration?",
  "comp.participants.searchPlaceholder": "Search by name, email, start number, club",
  "comp.participants.searchNoMatch": "No participant matches this search.",
  "comp.participants.excludedBadge": "excluded",
  "comp.participants.excludeBtn": "Exclude",
  "comp.participants.excludedTitleOn": "Excluded from official ranking — click to undo",
  "comp.participants.excludedTitleOff": "Exclude from official ranking (points stay visible)",
  "comp.participants.levelAboveBadge": "above level",
  "comp.participants.levelAboveTitle": "Climbing level at entry: {level} · group limit: {cap}",
  "comp.participants.levelAboveTitleGlobal": "Estimated climbing level at entry (from other gyms): {level} · group limit: {cap}",
  "comp.participants.levelAboveCount": "{count} above level",
  "comp.ranking.excludedNote": "Out of competition — not counted in official ranking. Would-be position in parens.",

  // Scorecard
  "comp.scorecard.btnLabel": "Scorecards (PDF)",
  "comp.scorecard.dialogTitle": "Print scorecards",
  "comp.scorecard.layout": "Layout",
  "comp.scorecard.layoutStandard": "Standard (1/page)",
  "comp.scorecard.layoutCompact": "Compact (2/page)",
  "comp.scorecard.sortBy": "Sort by",
  "comp.scorecard.groupBy": "Group by",
  "comp.scorecard.groupByNone": "No grouping",
  "comp.scorecard.groupBySession": "Session",
  "comp.scorecard.groupByGroup": "Group",
  "comp.scorecard.groupBySessionGroup": "Session, then group",
  "comp.scorecard.groupByGroupSession": "Group, then session",
  "comp.scorecard.finalsSection": "Finals",
  "comp.scorecard.sortByStartNumber": "Start number",
  "comp.scorecard.sortByName": "Name",
  "comp.scorecard.filterGroup": "Group filter",
  "comp.scorecard.allGroups": "All",
  "comp.scorecard.generate": "Generate",
  "comp.scorecard.zoneReached": "Zone",
  "comp.scorecard.topped": "Top",
  "comp.scorecard.attemptMark": "Attempt",
  "comp.scorecard.routeName": "Route",
  "comp.scorecard.compPointsCol": "Pts",
  "comp.scorecard.redpointFooter": "Best {n} routes count",
  "comp.scorecard.redpointFlashFooter": "+ {n} bonus per flash",
  "comp.scorecard.attemptCol": "Att. {n}",
  "comp.scorecard.tallyBoxesNote": "Leave the boxes at the end of each row ({boxes}) empty — the scorers fill them in when tallying.",
  "comp.scorecard.mode": "What to print",
  "comp.scorecard.modeAll": "Everyone",
  "comp.scorecard.modeSelected": "Selected",
  "comp.scorecard.modeBlank": "Blank sheets",
  "comp.scorecard.selectedCount": "{n} selected",
  "comp.scorecard.selectAll": "Select all",
  "comp.scorecard.selectNone": "Clear",
  "comp.scorecard.searchCompetitor": "Search by name or start number",
  "comp.scorecard.searchCompetitorEmail": "Search by name, start number or email",
  "comp.scorecard.noCompetitors": "No match",
  "comp.scorecard.everyGroup": "Every group",
  "comp.scorecard.blankPerGroup": "Sheets per group",
  "comp.scorecard.blankTotal": "{n} sheets in total ({groups} groups)",
  "comp.scorecard.blankHint": "the name line at the top is left blank",
  "comp.scorecard.blankNoRoutes": "No group has any route assigned, so there is nothing to print.",
  "comp.scorecard.blankNameLabel": "Name",
  "comp.scorecard.nothingToPrint": "This setting produced no scorecard to print.",

  // Action log
  "comp.actionLog.title": "Judge action log",
  "comp.actionLog.btnLabel": "Action log",
  "comp.syncClimbs.btnLabel": "Add climbs to statistics",
  "comp.syncClimbs.running": "Adding climbs to statistics…",
  "comp.syncClimbs.done": "{count} climb(s) added to the competitors' statistics.",
  "comp.syncClimbs.noCompetitors": "Only guests competed here — there is nobody to write statistics for.",
  "comp.syncClimbs.failed": "Could not add the climbs",
  "comp.actionLog.entryCount": "{n} judge actions",

  // Audit log
  "comp.audit.filterCompetitor": "Competitor",
  "comp.audit.filterRoute": "Route",
  "comp.audit.typeJudgeEvent": "Judge event",
  "comp.audit.typeJamSave": "JAM (self-recorded)",
  "comp.audit.typeAdminEdit": "Admin edit",
  "comp.audit.typeAdminImport": "Excel import",
  "comp.audit.refresh": "Refresh",
  "comp.audit.loadMore": "Show more",
  "comp.audit.empty": "No log entries yet",
  "comp.audit.snapshotLabel": "Snapshot",
  "comp.audit.judgeRole": "Judge",
  "comp.audit.adminRole": "Admin",
  "comp.audit.organizerRole": "Organizer",
  "comp.audit.competitorRole": "Competitor",
  "comp.audit.attemptN": "attempt {n}",
  "comp.audit.zoneShort": "Zone",
  "comp.audit.topShort": "Top",
  "comp.audit.noTop": "–",
  "comp.audit.allFilter": "All",
  "comp.audit.jamSelfNote": "(JAM self-recorded)",
  "comp.audit.revoked": "revoked",

  // Bib number export
  "comp.bibExport.btnLabel": "Bib numbers (PDF)",
  "comp.finalsRotation.btnLabel": "Finals rotation (PDF)",
  "comp.finalsRotation.round": "Round",
  "comp.finalsRotation.startOrder": "Start order",
  "comp.finalsRotation.routesOrder": "Route order",
  "comp.finalsRotation.gapNote": "Gap between groups: {rounds} rounds",
  "comp.finalsRotation.noFinals": "This competition has no finals with finalists assigned.",

  // Participant list
  "comp.participantList.btnLabel": "Participant list (PDF)",
  "comp.participantList.generatedAt": "Generated: {date}",
  "comp.participantList.appeared": "Appeared",
  "comp.participantList.competitorCount": "Competitors: {count}",
  "comp.participantList.judgeCount": "Judges: {count}",
  "comp.participantList.groupCount": "Groups: {count}",
  "comp.participantList.sessionCount": "Sessions: {count}",
  "comp.participantList.printOverview": "Print",
  "comp.participantList.session": "Session",
  "comp.participantList.startNumber": "Start number",
  "comp.participantList.judgeSessions": "Sessions",
  "comp.participantList.sessionDuration": "Duration",
  "comp.participantList.sessionMax": "Max participants",
  "comp.participantList.sessionGroups": "Groups",
  "comp.participantList.sessionCompetitors": "Competitors",

  // Finals session
  "comp.sessionType.standard": "Standard",
  "comp.sessionType.finals": "Finals",
  "comp.newFinalsSession": "New finals session",
  "comp.finals.topN": "Number of finalists",
  "comp.finals.maxAttempts": "Max attempts (finals)",
  "comp.finals.topNHint": "How many top climbers advance to finals",
  "comp.finals.loadFromRanking": "Load from ranking",
  "comp.finals.noFinalists": "Select a group and load from ranking",
  "comp.finals.sourceSession": "Source",
  "comp.finals.sourceAll": "Entire competition",
  "comp.finals.timePerBoulder": "Time per boulder",
  "comp.finals.multiGroupHint": "With multiple groups climbers don't mix — groups run one after another on a shared timeline (see the gap).",
  "comp.finals.groupGap": "Gap between groups",
  "comp.finals.groupGapHint": "rounds (boulder-time) between groups",
  "comp.finals.selectGroupsFirst": "Select at least one group first.",
  "comp.finals.moveGroupUp": "Move group up",
  "comp.finals.moveGroupDown": "Move group down",
  "comp.finals.restBetween": "Rest between rounds",
  "comp.finals.restRounds": "Rest rounds",
  "comp.finals.restRoundsHint": "rounds a climber rests between boulders (IFSC: 1, sometimes 2)",
  "comp.finals.observationTime": "Observation time",
  "comp.finals.routes": "Finals routes",
  "comp.finals.routesHint": "Finals routes (can differ from qualifying)",
  "comp.finals.finalists": "Finalists",
  "comp.finals.reverseOrderHint": "Reverse qualifying order (worst goes first)",
  "comp.finals.totalRounds": "{n} rounds total",
  "comp.finals.autoDuration": "Estimated duration: {n} min",
  "comp.finals.control": "Finals control",
  "comp.finals.round": "Round: {current} / {total}",
  "comp.finals.phaseObservation": "Observation",
  "comp.finals.phaseClimbing": "Climbing",
  "comp.finals.phaseRest": "Rotation / rest",
  "comp.finals.phaseFinished": "Finished",
  "comp.finals.startObservation": "Start observation",
  "comp.finals.startClimbing": "Start climbing (round 1)",
  "comp.finals.nextRound": "Next round",
  "comp.finals.finish": "Finish finals",
  "comp.finals.waiting": "Waiting",
  "comp.finals.allDone": "All done",
  "comp.finals.isolation": "Isolation",
  "comp.finals.isolationWait": "Please wait for your turn!",
  "comp.finals.isolationPosition": "You are climber #{n}",
  "comp.finals.isolationEntryRound": "Your entry in round {n}",
  "comp.finals.isolationCurrentRound": "Current round: {current} / {total}",
  "comp.finals.selectRoutes": "Select finals routes",
  "comp.finals.badge": "FINALS",
  "comp.finals.addManually": "Add competitor",
  "comp.finals.searchCompetitor": "Search competitor...",
  "comp.finals.noCompetitorsAvailable": "No more competitors available",

  // Series
  "series.title": "Competition Series",
  "series.new": "New Series",
  "series.badge": "Series",
  "series.roundsProgress": "{done}/{total} rounds completed",
  "series.viewStandings": "View Standings",
  "series.totalPoints": "Total Points",
  "series.round": "Round",
  "series.noResults": "No results yet",
  "series.noSeries": "No matching series",
  "series.notFound": "This series was not found",
  "series.notClosed": "Competition not yet closed",
  "series.status.active": "Active",
  "series.status.closed": "Closed",
  // Series Editor
  "series.editor.settings": "Settings",
  "series.editor.competitions": "Competitions",
  "series.editor.groups": "Groups",
  "series.editor.name": "Series name",
  "series.editor.description": "Description",
  "series.editor.scoring": "Scoring",
  "series.editor.scoringType": "Scoring mode",
  "series.editor.scoringRawPoints": "Sum points",
  "series.editor.scoringRawPointsHint": "Directly sum competition scores",
  "series.editor.scoringPlacement": "Placement-based",
  "series.editor.scoringPlacementHint": "Points assigned by finishing position (configurable)",
  "series.editor.pointMap": "Points table",
  "series.editor.place": "Place",
  "series.editor.points": "Points",
  "series.editor.addRow": "Add row",
  "series.editor.defaultPoints": "Default points",
  "series.editor.defaultPointsHint": "Points for places outside the table",
  "series.editor.countBest": "Best results count",
  "series.editor.countBestHint": "Empty = all rounds count",
  "series.editor.addCompetition": "Add competition",
  "series.editor.noClosedComps": "No closed competitions",
  "series.editor.addGroup": "Add group",
  "series.editor.groupName": "Group name",
  "series.editor.groupMapping": "Competition group mapping",
  "series.editor.noMapping": "No matching group",
  "series.editor.close": "Close series",
  "series.editor.closeConfirm": "Are you sure you want to close this series?",
  "series.editor.delete": "Delete series",
  "series.editor.deleteConfirm": "Are you sure you want to delete this series? This action cannot be undone.",
  "series.editor.saved": "Saved!",

  // Boulder name generator
  "names.adjectives": "Dynamic,Static,Grippy,Chalky,Bold,Precise,Flying,Tireless,Sweaty,Rubber-fingered,Panting,Trembling,Fearless,Eager,Desperate,Acrobatic,Weightless,Groaning,Crafty,Rested,Cracked,Cramped,Nimble,Thoughtful,Flexible,Heroic,Lucky,Slippery,Dusty,Determined,Relentless,Mighty",
  "names.nouns": "Gecko,Mountain Goat,Sloper King,Crimp Hustler,Beta Slayer,Dyno Knight,Send Machine,Chalk Monster,Climbing Shoe,Lichen,Wall Rat,Crag Warrior,Pinch Master,Edge Dweller,Dyno King,Sloper Wizard,Beta Hunter,Chalk Cloud,Cave Troll,Wall Dancer,Boulder Gnome,Power Machine,Hang Master,Flying Squirrel,Claw Master,Hold Seeker,Wall Acrobat,Crimp Lord,Heel Hooker,Toehook Pro,Mantle King,Campus Beast",
  "names.route.adjectives": "Midnight,Early,Slippery,Sneaky,Cheeky,Silent,Wild,Sleepy,Crooked,Wicked,Gentle,Cruel,Hidden,Lost,Twisted,Secret,Crazy,Burning,Frozen,Dizzy,Angry,Lazy,Endless,Tiny,Rusty,Black,Silver,Golden,Last,First,Devious,Devilish,Winding,Restless",
  "names.route.nouns": "Edge,Dance,Trap,Path,Staircase,Storm,Dream,Bend,Wave,Ladder,Vortex,Snake,Spider,Eagle,Leap,Swing,Riddle,Crossing,Temptation,Crack,Ledge,Dragon,Cat,Swallow,Tower,Trick,Ballad,Moment,Adventure,Maze,Ridge,Dead End,Spell,Abyss",

  // Pricing & billing
  "pricing.title": "Pricing",
  "pricing.tiers": "Plans",
  "pricing.mauBands": "MAU Bands",
  "pricing.tierName": "Plan name",
  "pricing.quarterly": "quarter",
  "pricing.perQuarter": "/ quarter",
  "pricing.addBand": "+ Add Band",
  "pricing.minMau": "Min MAU",
  "pricing.maxMau": "Max MAU",
  "pricing.unlimited": "Unlimited",
  "pricing.save": "Save Pricing",
  "pricing.saved": "Pricing saved",
  "pricing.setupFee": "Setup Fee (one-time)",
  "pricing.netNotice": "All prices are net, excluding VAT.",
  "pricing.vatRate": "VAT rate",
  "pricing.addons.title": "Add-ons",
  "pricing.addons.nameHu": "Name (HU)",
  "pricing.addons.nameEn": "Name (EN)",

  // Gym admin — subscription section
  "subscription.title": "Subscription & Billing",
  "subscription.currentTier": "Current Plan",
  "subscription.currency": "Currency",
  "subscription.mauQuarterMax": "Peak MAU ({quarter})",
  "subscription.mauQuarterMaxHint": "this is what we'll bill at quarter close",
  "subscription.mauBand": "MAU Band",
  "subscription.mauTrend": "MAU Trend (3 months)",
  "subscription.tierPrice": "Plan price",
  "subscription.mauPrice": "MAU band price",
  "subscription.totalPrice": "Total (net)",
  "subscription.vat": "VAT ({rate}%)",
  "subscription.grossTotal": "Gross total",
  "subscription.vatReverseCharge": "Reverse charge — the invoice carries no VAT; the customer self-accounts for VAT in their member state.",
  "subscription.vatOutOfScope": "Outside the territorial scope of VAT — the invoice carries no VAT.",
  "subscription.discount": "Discount",
  "subscription.addonDiscountTag": "−{pct}%",
  "subscription.nextQuarter": "Next quarter starts",
  "subscription.bandWarning": "MAU is approaching the band upper limit",

  // Platform admin — gym card
  "platform.gym.mau": "MAU",
  "platform.gym.band": "Band",
  "platform.gym.currency": "Currency",
  "platform.gym.billing": "Billing",
  "platform.gym.invoiceDetailsEmpty": "The gym has not entered any billing details yet.",
  "platform.gym.invoiceDetailsUpdated": "Last updated: {date}",
  "platform.gym.invoiceDetailsHint": "Filled in by the gym admin under Admin → Subscription & Billing.",
  "platform.gym.quarterlyTotal": "Quarterly total (net)",
  "platform.gym.mauHistory": "MAU History",
  "platform.gym.discount": "Discount",
  "platform.gym.addonDiscounts": "Add-on discounts",
  "platform.gym.discountsSection": "Discounts",
  "platform.gym.vatCurrencySection": "VAT and currency",
  "platform.gym.quarterlySection": "Quarterly fee",
  "platform.gym.discountReadOnly": "Editing the discount requires the “edit billing” capability.",
  "platform.gym.vatStatus": "VAT status",
  "platform.gym.vatAuto": "Auto",
  "platform.gym.vatDomestic": "VAT applies",
  "platform.gym.vatReverseCharge": "Reverse charge",
  "platform.gym.vatOutOfScope": "Out of scope",
  "platform.gym.vatEffective": "Effective status: {status}",
  "platform.gym.vatStatusHint": "Auto decides from the gym's billing country and EU VAT ID; a manual choice overrides it (in quarterly billing too).",
  "platform.gym.currencyLock": "Lock",
  "platform.gym.currencyLockHint": "When locked, the gym admin can't switch currency (the server rejects it too) — you can still change it here.",
  "platform.gym.vatEuMissingWarn": "EU address without an EU VAT ID — reverse charge cannot be applied, VAT is charged until resolved. Request the EU VAT ID or override the status.",
  "platform.gym.regenerateButton": "Generate billing CSV",
  "platform.gym.regenerateRunning": "Generating…",
  "platform.gym.regenerateConfirm": "Generate a billing CSV for {name}? Computes the previous quarter, updates the quarterlyBilling snapshot, and downloads the CSV. No email is sent to the gym — you deliver the invoice yourself.",
  "platform.gym.regenerateSuccess": "Generated ({quarter}) — {total} {currency}. Snapshot updated, CSV downloaded.",

  // Cookie consent
  "cookie.message": "This website uses cookies to improve user experience. Analytical cookies help us understand how the Platform is used.",
  "cookie.accept": "Accept",
  "cookie.decline": "Decline",
  "cookie.details": "Details",

  // Legal pages
  "legal.back": "Back",
  "legal.lastUpdated": "Last updated",
  "legal.contact": "Contact",
  "legal.terms.short": "Terms of Service",
  "legal.privacy.short": "Privacy Policy",

  // Consent modal
  "consent.welcome": "Welcome to Sendly!",
  "consent.updated": "Terms have been updated",
  "consent.description": "To use the Platform, you must accept the following documents:",
  "consent.acceptTerms": "I have read and accept the Terms of Service",
  "consent.acceptPrivacy": "I have read and accept the Privacy Policy",
  "consent.submit": "I accept & continue",
  "consent.cannotUse": "You cannot use the Platform without accepting.",
  "consent.profileTitle": "Your profile",
  "consent.profileHint": "Check your details — you can change them anytime in Settings.",
  "settings.legal": "Legal",
  "settings.legal.acceptedAt": "Accepted on",

  // Disciplines
  "discipline.boulder": "Boulder",
  "discipline.lead": "Lead",
  "discipline.label": "Discipline",
  "discipline.filterAll": "All",

  // Admin — disciplines
  "admin.disciplines": "Disciplines",
  "admin.disciplinesHint": "What types of routes does the gym manage?",
  "admin.diffTabBoulder": "Boulder difficulties",
  "admin.diffTabLead": "Lead difficulties",

  // Admin — TV discipline
  "admin.tvDisciplineMode": "Discipline filter",
  "admin.tvDisciplineAll": "Both (alternating rounds)",

  // Settings — lead grade preference
  "settings.gradePreferenceBoulder": "Boulder grade scale",
  "settings.gradePreferenceLead": "Lead grade scale",
  "settings.gradeFrench": "French (6a, 6b+, ...)",
  "settings.gradeUiaa": "UIAA (VI, VII+, ...)",

  // Settings — Monthly recap
  "settings.monthlyRecapTitle": "Monthly recap",
  "settings.monthlyRecapDesc": "Receive a monthly email summary of your climbing performance.",
  "settings.monthlyRecapLevel": "Recap type",
  "settings.recapFull": "Full (personal + gym + competition)",
  "settings.recapPersonal": "Personal (own stats only)",
  "settings.recapMinimal": "Minimal (points and ranking)",
  "settings.recapOff": "Off",
  "settings.gymAdminReport": "Gym admin report emails",
  "settings.gymAdminReportDesc": "Monthly business report for the gyms you admin — MAU, popular routes, new members. Toggle per gym, independent of your climber recap setting.",

  // Platform Admin — Recap Management
  "platformAdmin.recapTitle": "Monthly recap",
  "platformAdmin.recapEnabled": "Email sending enabled",
  "platformAdmin.recapFromEmail": "From",
  "platformAdmin.recapDailyLimit": "Daily limit",
  "platformAdmin.recapDailyLimitUnit": "emails",
  "platformAdmin.recapScheduleDay": "Send day",
  "platformAdmin.recapScheduleDayUnit": "day of month",
  "platformAdmin.recapScheduleHour": "Send hour (UTC)",
  "platformAdmin.recapSentThisMonth": "Sent this month",
  "platformAdmin.recapLastSent": "Last sent",
  "platformAdmin.recapSuccess": "Success",
  "platformAdmin.recapFailed": "Failed",
  "platformAdmin.recapTestTitle": "Send test email",
  "platformAdmin.recapTestType": "Type",
  "platformAdmin.recapTestTypeRecap": "Monthly recap (climber)",
  "platformAdmin.recapTestTypeAdmin": "Gym admin report",
  "platformAdmin.recapTestTypeInactive": "Inactive mode (come climb)",
  "platformAdmin.recapTestTypeWrapped": "Yearly Wrapped (previous year)",
  "platformAdmin.recapTestSend": "Send test to myself",
  "platformAdmin.recapTestSending": "Sending...",
  "platformAdmin.recapTestSent": "Sent!",
  "platformAdmin.recapTestError": "Send failed",
  "platformAdmin.recapManualTrigger": "Trigger manual send",
  "platformAdmin.recapManualWarning": "This will immediately send to ALL active users. Normally the scheduled function handles this automatically.",
  "platformAdmin.recapManualConfirm": "Are you sure you want to trigger a manual send?",
  "platformAdmin.recapManualStarted": "Send triggered",
  "platformAdmin.recapPerGym": "Per-gym statistics",
  "platformAdmin.recapGym": "Gym",
  "platformAdmin.recapSent": "Sent",
  "platformAdmin.recapErrors": "Errors",
  "platformAdmin.recapSaved": "Saved!",
  "platformAdmin.recapNeverSent": "No sends yet",
  "platformAdmin.recapTestLevel": "Level",

  // Wrapped (yearly summary)
  "wrapped.title": "SENDLY WRAPPED {year}",
  "wrapped.subtitle": "{name}'s yearly summary",
  "wrapped.previewBanner": "Live preview — numbers are computed in real-time, the year hasn't closed yet.",
  "wrapped.totalPoints": "Total points",
  "wrapped.totalRoutes": "Routes climbed",
  "wrapped.activeDays": "Active days",
  "wrapped.hardestClimb": "Hardest climb",
  "wrapped.longestStreak": "Longest streak",
  "wrapped.weeks": "weeks",
  "wrapped.bestWeek": "Best week",
  "wrapped.week": "Week {n}",
  "wrapped.bestFlashMonth": "Best flash month",
  "wrapped.flashes": "flashes",
  "wrapped.monthlyChart": "Your year in climbing",
  "wrapped.firstAscent": "Your first climb",
  "wrapped.lastAscent": "Your last climb",
  "wrapped.gyms": "Your gyms",
  "wrapped.activeDaysIn": "active days",
  "wrapped.funFacts": "Did you know?",
  "wrapped.mostClimbed": "Your most climbed route",
  "wrapped.times": "{n}×",
  "wrapped.gymCount": "You climbed in {n} gyms",
  "wrapped.sprayTitle": "Spraywall",
  "wrapped.sprayHardest": "Hardest send",
  "wrapped.sprayDays": "Spray days",
  "wrapped.spraySends": "Sends",
  "wrapped.sprayProblemsCreated": "Routes created",
  "wrapped.noData": "No Wrapped data available for this year yet.",
  "wrapped.backToGyms": "Back to the gym list",
  "wrapped.gymPoints": "{n} pts",
  "wrapped.loading": "Loading Wrapped...",
  "wrapped.monthNames": "Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec",

  // ── Interval Timer (Tabata) ──
  "intervalTimer.waitingForWorkout": "Interval Timer",
  "intervalTimer.scanToControl": "Scan the QR code with your phone to start a workout",
  "intervalTimer.pinUnavailable": "The PIN is unavailable. Ask staff to re-pair this screen from the admin panel.",
  "intervalTimer.pin": "PIN",
  "intervalTimer.work": "WORK",
  "intervalTimer.rest": "REST",
  "intervalTimer.prepare": "GET READY",
  "intervalTimer.paused": "PAUSED",
  "intervalTimer.restBetweenSetsLabel": "REST (BETWEEN SETS)",
  "intervalTimer.setRoundLabel": "Set {currentSet}/{totalSets} · Round {currentRound}/{totalRounds}",
  "intervalTimer.workoutComplete": "Workout complete! 💪",
  "intervalTimer.totalTime": "Total",
  "intervalTimer.enterPin": "Enter PIN",
  "intervalTimer.invalidPin": "Invalid code, please try again",
  "intervalTimer.tooManyAttempts": "Too many attempts, please wait a few minutes",
  "intervalTimer.myPresets": "Mine",
  "intervalTimer.gymPresets": "Gym",
  "intervalTimer.noPresetsYet": "No presets yet. Create one below.",
  "intervalTimer.loginForMyPresets": "Log in to manage your personal presets",
  "intervalTimer.newPreset": "New preset",
  "intervalTimer.totalDuration": "Total: {time}",
  "intervalTimer.presetSummary": "{rounds} × ({work}s work / {rest}s rest) · {sets} set(s)",
  "intervalTimer.presetName": "Preset name",
  "intervalTimer.addDescription": "Add description",
  "intervalTimer.addPhase": "+ Add phase",
  "intervalTimer.totalRounds": "Number of rounds",
  "intervalTimer.sets": "Sets",
  "intervalTimer.restBetweenSets": "Rest between sets",
  "intervalTimer.prepareTime": "Prepare",
  "intervalTimer.save": "Save",
  "intervalTimer.cancel": "Cancel",
  "intervalTimer.confirmDelete": "Are you sure you want to delete this preset?",
  "intervalTimer.editPreset": "Edit preset",
  "intervalTimer.removePhase": "Remove phase",
  "intervalTimer.pauseBtn": "Pause",
  "intervalTimer.resumeBtn": "Resume",
  "intervalTimer.stopBtn": "Stop",
  "intervalTimer.skipPhase": "Skip phase",
  "intervalTimer.volume": "Volume",
  "intervalTimer.backToPresets": "Back to presets",
  "intervalTimer.controlTakenOver": "Another user took over the control.",
  "intervalTimer.ok": "OK",
  "intervalTimer.errorNameRequired": "Name is required",
  "intervalTimer.errorNameTooLong": "Name must be at most 60 characters",
  "intervalTimer.errorRoundsRequired": "At least one phase is required",
  "intervalTimer.errorDurationRange": "Duration must be between 1 and 3600 seconds",
  "intervalTimer.errorTotalRoundsRange": "Number of rounds must be between 1 and 100",
  "intervalTimer.errorSetsRange": "Sets must be between 1 and 20",
  "intervalTimer.errorRestBetweenSetsRange": "Rest must be between 0 and 3600 seconds",
  "intervalTimer.errorPrepareTimeRange": "Prepare time must be between 0 and 60 seconds",
  "intervalTimer.adminPin": "PIN: {pin}",
  "intervalTimer.adminRepair": "Re-pair screen",
  "intervalTimer.adminRepairConfirm": "Drop this screen's device pairing? The TV asks for a new PIN on its next load — only use this when no PIN shows on the wall.",
  "intervalTimer.adminControlActive": "Controlled: active",
  "intervalTimer.adminControlFree": "No controller",
  "intervalTimer.adminReleaseControl": "Release control",
  "intervalTimer.adminAllToIntervalTimer": "All TVs to Interval timer",
  "intervalTimer.typeWork": "Work",
  "intervalTimer.typeRest": "Rest",
  "intervalTimer.cycles": "Cycles",
  "intervalTimer.switchToRest": "Switch to rest",
  "intervalTimer.switchToWork": "Switch to work",
  "intervalTimer.decrease": "Decrease {label}",
  "intervalTimer.increase": "Increase {label}",
  "intervalTimer.selectPreset": "Select a preset",
  "intervalTimer.startBtn": "Start",
  "intervalTimer.scanQr": "Scan the QR code on the TV or enter the PIN",
  "intervalTimer.autoResetIn": "Auto reset in {seconds}s",

  // Public gym widget (embeddable iframe)
  "widget.summary.activeRoutes": "active routes",
  "widget.summary.members": "climbers",
  "widget.summary.totalAscents": "ascents",
  "widget.summary.totalClimbs": "climbs",
  "widget.grades.title": "Grade distribution",
  "widget.grades.boulder": "Boulder",
  "widget.grades.lead": "Lead",
  "widget.grades.noData": "Not enough data yet",
  "widget.competition.title": "Competition",
  "widget.competition.viewLeaderboard": "Live leaderboard →",
  "widget.competition.viewExternal": "Details →",
  "widget.competition.viewResults": "Results →",
  "widget.upgrade.required": "This widget requires the Pro plan",
  "widget.powered": "Powered by Sendly",
  "widget.error.notFound": "Gym not found",
  "widget.error.noSnapshot": "Widget currently unavailable",
  "widget.occupancy.title": "Popular times",

  // Occupancy chart (shared across widget, admin panel, gym info)
  "occupancy.title": "Popular times",
  "occupancy.quiet": "Quiet",
  "occupancy.busy": "Busy",
  "occupancy.level.quiet": "Quiet",
  "occupancy.level.moderate": "Moderate",
  "occupancy.level.busy": "Busy",
  "occupancy.level.veryBusy": "Very busy",
  "occupancy.noData": "Not enough data yet",
  "occupancy.closed": "Closed",
  "occupancy.basedOn": "Based on the last {weeks} weeks",

  // Admin panel — widget settings
  "admin.widget.title": "Widget / API",
  "admin.widget.description": "Show your gym's live stats on your own website via a simple embed, or query them through the JSON API.",
  "admin.widget.sectionHeader.widget": "Widget",
  "admin.widget.sectionHeader.api": "JSON API",
  "admin.widget.enable": "Enable widget",
  "admin.widget.enableWarning": "When enabled, gym name, city, active route count and member count become publicly visible.",
  "admin.widget.enableConfirm": "Are you sure you want to make your gym stats public?",
  "admin.widget.apiKey.title": "API key",
  "admin.widget.apiKey.generate": "Generate API key",
  "admin.widget.apiKey.regenerate": "Regenerate (old key will be invalidated)",
  "admin.widget.apiKey.regenerateConfirm": "The old key will stop working immediately. Continue?",
  "admin.widget.apiKey.copy": "Copy",
  "admin.widget.apiKey.copied": "Copied",
  "admin.widget.apiKey.show": "Show",
  "admin.widget.apiKey.hide": "Hide",
  "admin.widget.apiKey.none": "No key generated yet",
  "admin.widget.preview.title": "Preview",
  "admin.widget.preview.summary": "Summary",
  "admin.widget.preview.grades": "Grade distribution",
  "admin.widget.preview.competition": "Active competition",
  "admin.widget.preview.occupancy": "Popular times",
  "admin.widget.preview.full": "All",
  "admin.widget.snippet.title": "Embed code",
  "admin.widget.snippet.copy": "Copy code",
  "admin.widget.snippet.copied": "Code copied",
  "admin.widget.api.description": "For developers: query the JSON API from your own application.",
  "admin.widget.api.endpoint": "Endpoint",
  "admin.widget.api.example": "Example (curl)",
  "admin.widget.api.usage": "API requests",
  "admin.widget.api.usageToday": "Today",
  "admin.widget.api.usage7d": "Last 7 days",
  "admin.widget.api.usage30d": "Last 30 days",
  "admin.widget.api.usageTotal": "All time",
  "admin.widget.api.usageNever": "No requests yet",
  "admin.widget.rebuild": "Refresh now",
  "admin.widget.rebuilding": "Refreshing…",
  "admin.widget.rebuildSuccess": "Widget data refreshed",
  "admin.widget.rebuildError": "Refresh failed",
  "admin.widget.planRequired": "Pro plan required",
  "admin.widget.disabled": "The widget is currently disabled",

  // ── Permission system: roles ──
  "roles.user.label": "User",
  "roles.user.desc": "Basic gym member features",
  "roles.setter.label": "Setter",
  "roles.setter.desc": "Route creation and management",
  "roles.coach.label": "Coach",
  "roles.coach.desc": "Training plans and interval timer presets",
  "roles.receptionist.label": "Receptionist",
  "roles.receptionist.desc": "TV and interval timer management",
  "roles.gymAdmin.label": "Gym Admin",
  "roles.gymAdmin.desc": "Full gym-level access",

  // ── Permission system: capability categories ──
  "capCategory.routes": "Routes",
  "capCategory.users": "Users",
  "capCategory.gym": "Gym profile",
  "capCategory.mapsAndConfig": "Maps & Configuration",
  "capCategory.tv": "TV Dashboard",
  "capCategory.stats": "Statistics",
  "capCategory.competitions": "Competitions",
  "capCategory.series": "Series",
  "capCategory.database": "Database",
  "capCategory.billing": "Billing",
  "capCategory.intervalTimer": "Interval Timer",

  // ── Permission system: capability labels & descriptions ──
  "caps.routes.create.label": "Create route",
  "caps.routes.create.desc": "Add a new route to the map",
  "caps.routes.edit.label": "Edit route",
  "caps.routes.edit.desc": "Modify existing routes",
  "caps.routes.archive.label": "Archive route",
  "caps.routes.archive.desc": "Archive routes (reversible soft delete)",
  "caps.routes.delete.label": "Delete route",
  "caps.routes.delete.desc": "Permanently delete routes (irreversible)",
  "caps.routes.viewTab.label": "Routes tab",
  "caps.routes.viewTab.desc": "Access the Routes management tab",

  "caps.users.view.label": "View members",
  "caps.users.view.desc": "View the list of gym members",
  "caps.users.viewProfile.label": "View member profile",
  "caps.users.viewProfile.desc": "Open a member's stats (ascents, points, climbing frequency) — e.g. by tapping a climber chip on the routes list",
  "caps.users.changeRole.label": "Change roles",
  "caps.users.changeRole.desc": "Modify member roles and capabilities",
  "caps.users.kick.label": "Remove members",
  "caps.users.kick.desc": "Remove members from the gym",
  "caps.users.ban.label": "Ban members",
  "caps.users.ban.desc": "Permanently ban members from the gym (reason and audit entry required)",
  "caps.users.viewAuditLog.label": "View audit log",
  "caps.users.viewAuditLog.desc": "View permission change and membership event history",

  "caps.gym.editProfile.label": "Edit gym profile",
  "caps.gym.editProfile.desc": "Name, description, address, GPS, contact, disciplines, widget",
  "caps.gym.editBranding.label": "Edit branding",
  "caps.gym.editBranding.desc": "Logo, cover image, accent color",
  "caps.gym.editVisibility.label": "Edit visibility",
  "caps.gym.editVisibility.desc": "Toggle public visibility in gym selector",
  "caps.gym.editGeneral.label": "Edit general settings",
  "caps.gym.editGeneral.desc": "New route badge days, opening hours, timezone",

  "caps.maps.manage.label": "Manage maps",
  "caps.maps.manage.desc": "Add, rename, upload images, delete floors",
  "caps.difficulty.manage.label": "Manage difficulty levels",
  "caps.difficulty.manage.desc": "Edit difficulty levels and grade ranges",
  "caps.holdColors.manage.label": "Manage hold colors",
  "caps.holdColors.manage.desc": "Edit hold color options",

  "caps.tv.manage.label": "TV settings",
  "caps.tv.manage.desc": "TV screens (approve, rename, switch mode, delete) plus slides, timing, theme, OLED protection",
  "caps.tv.regenerateToken.label": "Regenerate TV token",
  "caps.tv.regenerateToken.desc": "Generate new TV token (invalidates old link)",
  "caps.tv.forceReleaseTimer.label": "Force release timer",
  "caps.tv.forceReleaseTimer.desc": "Force release interval timer mobile control",

  "caps.stats.viewSetterTab.label": "Setters tab",
  "caps.stats.viewSetterTab.desc": "Access the Setters statistics tab",
  "caps.stats.viewAdvanced.label": "Advanced statistics",
  "caps.stats.viewAdvanced.desc": "Traffic, grade distribution, route insights, heatmap",

  "caps.competitions.manage.label": "Manage competitions",
  "caps.competitions.manage.desc": "Create, edit, activate, close, delete competitions",
  "caps.competitions.viewClimberTimeline.label": "Climber timelines",
  "caps.competitions.viewClimberTimeline.desc": "Any competitor's session timeline: which routes they went for, in what order, with how many attempts",

  "caps.series.manage.label": "Manage series",
  "caps.series.manage.desc": "Create, edit, close, delete competition series",

  "caps.db.export.label": "Export data",
  "caps.db.export.desc": "Full gym JSON data export",
  "caps.db.maintain.label": "Database maintenance",
  "caps.db.maintain.desc": "Purge operations on gym data",

  "caps.billing.view.label": "View billing",
  "caps.billing.view.desc": "Subscription status, MAU count, pricing",
  "caps.billing.changeCurrency.label": "Change currency",
  "caps.billing.changeCurrency.desc": "Switch billing currency (HUF/EUR/USD)",

  "caps.intervalTimer.managePresets.label": "Timer presets",
  "caps.intervalTimer.managePresets.desc": "Create, edit, delete gym presets",

  "capCategory.mcp": "AI connection",
  "capCategory.spraywall": "Spraywall",
  "caps.mcp.read.label": "AI connection: read",
  "caps.mcp.read.desc": "Use the Sendly MCP server's gym read tools through an AI assistant (each together with its own data permission), and show the admin panel's \"AI connection\" section",
  "caps.mcp.write.label": "AI connection: write",
  "caps.mcp.write.desc": "Use the Sendly MCP server's gym write tools through an AI assistant (together with the gym:write scope and the operation's own permission), and manage the \"Allow writes through AI\" switch",
  "caps.spraywall.manage.label": "Spraywall: manage walls",
  "caps.spraywall.manage.desc": "Create, edit and delete spray walls; photo, hold detection, holds and wall versions; placement on the map; the spray route creation permission settings; official lists",
  "caps.spraywall.moderate.label": "Spraywall: moderation",
  "caps.spraywall.moderate.desc": "Hide, delete and edit anyone's spray route; see private, friends-level and hidden spray routes; delete comments; benchmark marking (benchmark sends count toward climbers' level); handle reports",
  "spraywall.whatIsSendBody": "Send = climbing the whole route without a fall, from the start to the top.",
  "spraywall.gradeVote": "My grade vote",
  "spraywall.gradeVoteEasier": "Easier grades",
  "spraywall.gradeVoteHarder": "Harder grades",
  "spraywall.gradeVoteHint": "The dashed frame is the current consensus. Voting is optional and you can change it later; tap the selected one to take it back.",
  "spraywall.view.problems": "Routes",
  "spraywall.view.lists": "Lists",
  "spraywall.wallHeader.back": "Back to the walls",
  "spraywall.wallMenu.button": "More",
  "spraywall.wallMenu.versions": "Old wall versions",
  "spraywall.wallMenu.edit": "Edit wall",
  "spraywall.wallMenu.manage": "Manage walls",
  "spraywall.wallMenu.newWall": "New wall",
  "spraywall.strip.fullWall": "Full wall",
  "spraywall.versions.title": "Old wall versions",
  "spraywall.versions.body": "Earlier set-ups of the wall. You can browse their routes and delete your own log, but they cannot be logged.",
  "spraywall.versions.empty": "There is no old wall version yet.",
  "spraywall.versions.dates": "{from} – {to}",
  "spraywall.versions.routeCount": "{count} routes",
  "spraywall.versions.band": "Archive — browse only",
  "spraywall.versions.backToCurrent": "Back to the current wall",
  "spraywall.versions.crumb": "Old version: {label}",
  "spraywall.empty.noProblems": "No routes yet — be the first",
  "spraywall.empty.photoFailed": "The wall photo could not be loaded.",
  "spraywall.empty.retry": "Try again",
  "spraywall.empty.noWall": "No spray wall yet. Add a wall: photo, hold detection, publishing. Members will not see this tab until a wall is published.",
  "spraywall.empty.noLists": "No lists yet.",
  "spraywall.empty.noLog": "You have no log entries yet.",
  "spraywall.loading": "Loading Spraywall…",
  "spraywall.picker.newWall": "+ New wall",
  "spraywall.picker.manage": "Manage walls",
  "spraywall.picker.problemCount": "{count} active routes",
  "spraywall.picker.kicker": "Spray wall",
  "spraywall.picker.newThisWeek": "{count} new this week",
  "spraywall.picker.openReports": "{count} reported",
  "spraywall.picker.reportsLabel": "Reports",
  "spraywall.picker.reportsHint": "Open → Reports",
  "spraywall.picker.ownReportsHint": "On your routes — the staff will look at it",
  "spraywall.picker.needsReview": "{count} to review",
  "spraywall.picker.needsReviewLabel": "To review",
  "spraywall.picker.needsReviewHint": "Open → To review",
  "spraywall.picker.ownNeedsReviewHint": "On your routes — Open → Your routes",
  "spraywall.picker.you": "You",
  "spraywall.picker.youLine": "{sends} sends · {projects} projects",
  "spraywall.picker.lastClimbed": "Last climbed: {date}",
  "spraywall.picker.open": "Open",
  "spraywall.picker.manageLabel": "Manage",
  "spraywall.wall.angle": "Angle: {angle}°",
  "spraywall.wall.hidden": "Hidden",
  "spraywall.wall.notPublished": "Not published",
  "spraywall.wall.live": "Live",
  "spraywall.wall.notPublishedBody": "This wall has no published version yet, so members cannot see it. Open the editor and publish it.",
  "spraywall.wall.deleting": "Being deleted",
  "spraywall.wall.deletingBody": "The deletion of this wall was interrupted or is still running. You can start it again in the wall management view to finish it.",
  "spraywall.wall.edit": "Edit",
  "spraywall.manage.title": "Manage walls",
  "spraywall.manage.membersCanCreate": "Members can create routes too",
  "spraywall.manage.membersCanCreateHint": "Staff can always create. Can be overridden per wall.",
  "spraywall.manage.climbersVisible": "Members can see who climbed a route",
  "spraywall.manage.climbersVisibleHint": "When off, only the staff sees the \"Who climbed it\" list.",
  "spraywall.manage.moveUp": "Move up",
  "spraywall.manage.moveDown": "Move down",
  "spraywall.manage.active": "Active",
  "spraywall.manage.edit": "Edit",
  "spraywall.manage.delete": "Delete wall",
  "spraywall.manage.deleteResume": "Resume deletion",
  "spraywall.manage.deleteConfirmTitle": "Delete the wall?",
  "spraywall.manage.deleteConfirmBody": "\"{name}\" — all its versions, photos, routes, logs, ratings, comments, reports and the lists of the wall are deleted. Climbers' timelines and active days are kept (route names from a snapshot). This cannot be undone.",
  "spraywall.manage.deleting": "Deleting…",
  "spraywall.editor.titleNew": "New wall",
  "spraywall.editor.title": "Edit wall",
  "spraywall.editor.back": "Back",
  "spraywall.editor.basics": "Basics",
  "spraywall.editor.name": "Name",
  "spraywall.editor.description": "Description",
  "spraywall.editor.angle": "Angle (degrees)",
  "spraywall.editor.angleHint": "Information only, it affects nothing.",
  "spraywall.editor.active": "Active",
  "spraywall.editor.activeHint": "When off, members cannot see the wall.",
  "spraywall.editor.membersCanCreate": "Members can create routes",
  "spraywall.editor.membersCanCreate.gym": "Gym setting",
  "spraywall.editor.membersCanCreate.yes": "Yes",
  "spraywall.editor.membersCanCreate.no": "No",
  "spraywall.editor.create": "Create",
  "spraywall.editor.save": "Save",
  "spraywall.editor.saved": "Saved",
  "spraywall.editor.created": "Wall created",
  "spraywall.editor.saveFailed": "Saving failed. Please try again.",
  "spraywall.editor.photoHint": "The whole wall must fit on the photo, taken from the front in even light. Anything that does not fit is a separate wall.",
  "spraywall.editor.photoNoPeople": "Upload a photo that shows no people.",
  "spraywall.editor.uploadPhoto": "Choose photo",
  "spraywall.editor.uploading": "Uploading…",
  "spraywall.editor.uploadFailed": "The upload failed. Please try again.",
  "spraywall.editor.versions": "Versions",
  "spraywall.editor.versionDraft": "draft",
  "spraywall.editor.versionLive": "live",
  "spraywall.editor.versionStats": "{holds} holds · {problems} routes",
  "spraywall.editor.newVersion": "New version",
  "spraywall.editor.versionLabel": "Version name",
  "spraywall.editor.firstVersionLabel": "First version",
  "spraywall.editor.replaceDraftConfirm": "There is already a draft. Replace it?",
  "spraywall.editor.discardDraft": "Discard draft",
  "spraywall.editor.discardDraftConfirm": "The draft, its detection proposals and its photo are deleted. The live wall does not change.",
  "spraywall.editor.draftDiscarded": "Draft discarded",
  "spraywall.editor.publish": "Publish",
  "spraywall.editor.publishConfirm": "Publish the \"{label}\" version? Members will now see this photo and these holds; the routes of the previous live version are archived.",
  "spraywall.editor.published": "Version published",
  "spraywall.editor.publishHint": "Members cannot see this version until you publish it.",
  "spraywall.editor.holds": "Holds",
  "spraywall.editor.holdsCount": "{count} holds · {feetOnly} feet-only",
  "spraywall.editor.detect": "Detect holds",
  "spraywall.editor.detecting": "Detection running…",
  "spraywall.editor.detectFailed": "The detection failed.",
  "spraywall.editor.editHolds": "Edit holds",
  "spraywall.editor.noActiveModel": "No active detection model — + Circle can be used",
  "spraywall.editor.placement": "Place on the map",
  "spraywall.editor.placedOn": "Placed on: {map}",
  "spraywall.editor.notPlaced": "Not placed",
  "spraywall.editor.takeOffMap": "Take off the map",
  "spraywall.editor.placementBlocked": "Not available while a review is open — approve or discard it first.",
  "spraywall.editor.deleteWall": "Delete wall",
  "spraywall.editor.phoneWarning": "A tablet or computer is recommended for reviewing 300+ holds. On a phone you can fix single holds.",
  "spraywall.editor.discardReviewConfirm": "Leave the review? The detection and your decisions stay on this device for 24 hours.",
  "spraywall.progress.title": "Detecting holds",
  "spraywall.progress.starting": "Starting the detector…",
  "spraywall.progress.stepStart": "Start",
  "spraywall.progress.stepPhoto": "Loading the photo",
  "spraywall.progress.stepParts": "Finding holds",
  "spraywall.progress.partsCount": "{done} / {total} parts",
  "spraywall.progress.stepMerge": "Merging",
  "spraywall.progress.stepOutline": "Wall edge",
  "spraywall.progress.coldStart": "The first run takes about 10 s.",
  "spraywall.progress.etaUnknown": "Estimating…",
  "spraywall.progress.etaSoon": "a few seconds",
  "spraywall.progress.eta": "about {s} s left",
  "spraywall.progress.help": "You can leave: the detection runs on the server, and you can continue here when you come back.",
  "spraywall.resume.title": "A finished detection is waiting for review",
  "spraywall.resume.meta": "{ago} · {count} proposals · {model} model",
  "spraywall.resume.agoNow": "just created",
  "spraywall.resume.agoMinutes": "created {count} min ago",
  "spraywall.resume.agoHours": "created {count} h ago",
  "spraywall.resume.expiry": "It waits for you for 24 hours, then it has to be run again.",
  "spraywall.resume.open": "Review",
  "spraywall.resume.dismiss": "Dismiss",
  "spraywall.resume.dismissed": "Detection dismissed",
  "spraywall.resume.staleTitle": "The latest detection can no longer be used.",
  "spraywall.resume.staleBody": "The holds of this version have changed since.",
  "spraywall.resume.expiredTitle": "The latest detection has expired",
  "spraywall.resume.rerun": "New detection",
  "spraywall.review.toolSelect": "Edit",
  "spraywall.review.toolSmartPick": "+ Smart",
  "spraywall.review.toolManual": "+ Circle",
  "spraywall.review.reject": "Reject",
  "spraywall.review.restore": "Restore",
  "spraywall.review.feetOnly": "Feet only",
  "spraywall.review.delete": "Delete",
  "spraywall.review.nextCandidate": "Next candidate",
  "spraywall.review.doubtful": "Doubtful",
  "spraywall.review.counter": "{count} / 1500",
  "spraywall.review.tooMany": "A wall can have at most 1500 holds — remove some.",
  "spraywall.review.approve": "Approve",
  "spraywall.review.keptHint": "{count} hand-marked holds — the machine did not see them, so we kept them. Remove the ones that were taken off.",
  "spraywall.review.dropped": "taken off?",
  "spraywall.review.noHold": "I see no hold here — draw a manual circle.",
  "spraywall.review.noSmaller": "I found no smaller hold inside it — draw a manual circle.",
  "spraywall.review.alreadyHold": "This is already a hold.",
  "spraywall.review.picking": "Looking for the hold…",
  "spraywall.review.needsReviewTitle": "Routes going to review",
  "spraywall.review.needsReviewBody": "These routes will go to review:",
  "spraywall.review.restrictedProblem": "non-public / hidden route",
  "spraywall.review.deleteHoldConfirm": "Delete hold {id}?",
  "spraywall.review.missingNote": "The hold is marked \"missing\" and keeps its id; existing sends do not change. Ctrl+Z or a click on the hold restores it.",
  "spraywall.review.needsReviewHold": "{count} routes use this hold. They go to review:",
  "spraywall.review.needsReviewHoldOne": "One route uses this hold. It goes to review:",
  "spraywall.review.holdCrop": "Hold {id} on the photo",
  "spraywall.review.feetOnlyConfirm": "Make hold {id} feet-only?",
  "spraywall.focus.open": "Focus view",
  "spraywall.focus.exit": "Exit focus view",
  "spraywall.focus.fullscreen": "Full screen",
  "spraywall.focus.exitFullscreen": "Exit full screen",
  "spraywall.focus.saving": "Saving…",
  "spraywall.focus.saved": "✓ All saved",
  "spraywall.focus.idleTitle": "No hold selected",
  "spraywall.focus.idleMouse": "Click a hold on the photo.",
  "spraywall.focus.idleTouch": "Tap a hold on the photo.",
  "spraywall.focus.hintDrag": "on the selected hold: move it · elsewhere: pan the photo",
  "spraywall.focus.hintTwoFingers": "zoom, pan",
  "spraywall.focus.hintLongPress": "feet only",
  "spraywall.focus.hintPoint": "drag it · the circle adds a point · long press deletes",
  "spraywall.focus.hintDelete": "delete the hold or the point",
  "spraywall.focus.hintFeetOnly": "feet only",
  "spraywall.focus.hintUndo": "undo",
  "spraywall.focus.hintNudge": "fine nudge",
  "spraywall.focus.hintEscape": "leave the focus view",
  "spraywall.focus.keyDrag": "Drag",
  "spraywall.focus.keyTwoFingers": "Two fingers",
  "spraywall.focus.keyLongPress": "Long press",
  "spraywall.focus.keyPoint": "Point",
  "spraywall.shape.hint": "Drag the hold to move it. Drag the points to adjust the outline; the circle in the middle of an edge adds a point, a right-click (a long press on a touch screen) deletes a point.",
  "spraywall.outline.tool": "Wall edge",
  "spraywall.outline.points": "Outline: {count} / {max} points",
  "spraywall.outline.none": "No outline — the whole photo is shown.",
  "spraywall.outline.maxPoints": "You have reached 64 points — delete one before adding another.",
  "spraywall.outline.removeOutside": "Delete the holds outside ({count})",
  "spraywall.outline.removeOutsideConfirm": "Delete the {count} holds outside the outline?",
  "spraywall.outline.rejectOutside": "Reject the ones outside ({count})",
  "spraywall.tools.info": "What the tools do",
  "spraywall.tools.select": "Edit — tap a hold. Drag the selected hold to move it, drag its points to adjust it; the circle in the middle of an edge adds a point. Right-click (a long press on touch) on a point deletes it; on the hold: feet only.",
  "spraywall.tools.smartPick": "+ Smart — tap a hold the machine missed: it outlines it. A tap inside the selected hold searches for a smaller one in it.",
  "spraywall.tools.manual": "+ Circle — drag a circle around the hold: where you press is its centre.",
  "spraywall.tools.outline": "Wall edge — members do not see what lies outside. Drag the points, add one with an edge's circle, delete one with a right-click (a long press on touch).",
  "spraywall.tools.view": "Two fingers or the wheel zoom and pan; ⟲ resets the view.",
  "spraywall.tools.doubtful": "Doubtful — highlights only the proposals the machine is unsure about. The rest are dimmed and cannot be tapped.",
  "spraywall.outline.auto": "Automatic outline",
  "spraywall.outline.clear": "Clear outline",
  "spraywall.outline.autoFailed": "Could not compute an outline — it needs at least 5 holds in one group.",
  "spraywall.outline.tooSmall": "The outline is too small — it must cover at least 5% of the photo.",
  "spraywall.outline.outsideCount": "{count} proposals fell outside the wall — they start rejected.",
  "spraywall.outline.hint": "Members do not see the area outside the outline, and the detection brings the proposals there in rejected. Drag the points to the edges of the wall; the circle in the middle of an edge adds a point, a right-click (a long press on a touch screen) deletes a point. The photo does not change.",
  "spraywall.shape.zoomIn": "Zoom in on the hold to edit its points.",
  "spraywall.shape.maxPoints": "You have reached 16 points — delete one before adding another.",
  "spraywall.shape.minPoints": "At least 3 points are needed",
  "spraywall.shape.points": "Outline: {count} / {max} points",
  "spraywall.shape.unused": "No route uses it — it can be deleted without asking",
  "spraywall.shape.edited": "hand-corrected",
  "spraywall.undo.undo": "Undo",
  "spraywall.undo.redo": "Redo",
  "spraywall.undo.restoredRoutes": "Restored: {id} · {count} routes are fine again",
  "spraywall.restore.title": "{id} · missing",
  "spraywall.restore.body": "This hold was deleted, but {count} routes still use it:",
  "spraywall.restore.button": "Restore",
  "spraywall.restore.help": "After the restore the routes are fine again unless something else needs their review. You can adjust the outline afterwards. If the routes are fixed, the hold disappears by itself.",
  "spraywall.review.note": "Note",
  "spraywall.review.noteHint": "Everyone sees it — do not put a name in it.",
  "spraywall.review.noteSave": "Save note",
  "spraywall.review.rerun": "Run again",
  "spraywall.review.srcAuto": "automatic",
  "spraywall.review.srcPick": "smart",
  "spraywall.review.srcManual": "manual",
  "spraywall.review.missingHold": "missing",
  "spraywall.photo.coopHint": "Use two fingers to move and zoom the photo",
  "spraywall.photo.alt": "Photo of the {name} wall",
  "spraywall.footRule.marked": "Marked feet",
  "spraywall.footRule.marked.desc": "Only the holds marked as feet on the route may be used for feet, not the hand holds.",
  "spraywall.footRule.any": "Open feet",
  "spraywall.footRule.any.desc": "Any hold may be used for feet, including the wall's feet-only holds (shown faintly on the photo).",
  "spraywall.footRule.feetFollow": "Feet follow hands",
  "spraywall.footRule.feetFollow.desc": "The holds marked for hands may be used for feet too.",
  "spraywall.footRule.noFeet": "No feet",
  "spraywall.footRule.noFeet.desc": "Campus: the feet may not touch a hold.",
  "spraywall.startHolds.matched": "Matched",
  "spraywall.startHolds.matched.desc": "One start hold: both hands start on it.",
  "spraywall.startHolds.two": "Two holds",
  "spraywall.startHolds.two.desc": "Two start holds: each hand starts on its own.",
  "spraywall.list.filter.notClimbed": "Not climbed yet",
  "spraywall.list.filter.projects": "My projects",
  "spraywall.list.filter.benchmark": "Benchmark",
  "spraywall.list.filter.mine": "Mine",
  "spraywall.list.filter.hiddenReview": "Hidden / needs review",
  "spraywall.list.clearFilters": "Clear filters",
  "spraywall.empty.noFilterResult": "No route matches the filters.",
  "spraywall.list.hiddenReviewEmpty": "No hidden or under-review route.",
  "spraywall.list.loadFailed": "The routes could not be loaded.",
  "spraywall.list.sort": "Sort",
  "spraywall.list.sort.new": "Newest first",
  "spraywall.list.sort.rating": "Best rated",
  "spraywall.list.sort.mostClimbed": "Most climbed",
  "spraywall.list.sort.grade": "By difficulty",
  "spraywall.list.row.sends": "{count} sends",
  "spraywall.list.row.new": "new",
  "spraywall.list.row.sent": "Sent",
  "spraywall.list.row.project": "Project",
  "spraywall.list.row.private": "Private",
  "spraywall.list.row.friends": "Friends",
  "spraywall.list.row.benchmark": "Benchmark",
  "spraywall.list.row.benchmarkTitle": "Benchmark — counts toward your level",
  "spraywall.list.row.you": "You",
  "spraywall.filter.title": "Filters",
  "spraywall.filter.section.grade": "Difficulty",
  "spraywall.filter.section.status": "Status",
  "spraywall.filter.section.style": "Style",
  "spraywall.filter.section.list": "Official list",
  "spraywall.filter.searchPlaceholder": "Search by name",
  "spraywall.filter.count": "{count} routes",
  "spraywall.filter.filtered": "filtered",
  "spraywall.filter.chip.search": "\"{query}\"",
  "spraywall.badge.hidden": "Hidden",
  "spraywall.badge.needsReview": "Needs review",
  "spraywall.badge.archived": "Archived",
  "spraywall.badge.reported": "Reported",
  "spraywall.list.ownAttention": "Your routes ({count})",
  "spraywall.problem.unavailable": "The route is not available",
  "spraywall.problem.share": "Share",
  "spraywall.problem.linkCopied": "Link copied!",
  "spraywall.problem.noteHolds": "Hold notes",
  "spraywall.problem.editedByStaff": "Edited by staff",
  "spraywall.problem.formerMember": "Former member",
  "spraywall.problem.byline": "{name} · {date}",
  "spraywall.problem.authorGrade": "setter: {grade}",
  "spraywall.problem.footRule": "Foot rule",
  "spraywall.problem.votesTitle": "Grade votes",
  "spraywall.problem.votesSummary": "{count} votes · consensus: median",
  "spraywall.problem.noVotes": "No grade votes yet.",
  "spraywall.problem.counters": "{sends} sends · {flashes} flashes · {projects} projects",
  "spraywall.problem.back": "Back to the list",
  "spraywall.problem.prev": "Previous route",
  "spraywall.problem.next": "Next route",
  "spraywall.problem.fullscreen": "Full screen",
  "spraywall.problem.fullscreenExit": "Exit full screen",
  "spraywall.problem.position": "{wall} · {n} / {N}",
  "spraywall.problem.opinions": "Opinions",
  "spraywall.problem.opinionsSub": "{ratings} ratings · {comments} comments",
  "spraywall.problem.about": "About the route",
  "spraywall.problem.climbs": "Climbs",
  "spraywall.problem.fact.grade": "Grade",
  "spraywall.problem.fact.start": "Start",
  "spraywall.problem.fact.style": "Style",
  "spraywall.problem.fact.setUp": "Set",
  "spraywall.footer.own": "Your route",
  "spraywall.footer.staff": "Staff",
  "spraywall.problem.ratingCount": "({count} ratings)",
  "spraywall.problem.noRatings": "No ratings yet",
  "spraywall.problem.underReview": "Under review",
  "spraywall.problem.underReviewBody": "One of its holds is gone from the wall, so it cannot be logged now. The setter or the staff will fix it.",
  "spraywall.problem.archivedBody": "Archived wall version. You can browse the route and delete your own log, but it cannot be logged.",
  "spraywall.problem.authorHidden": "Hidden — only you and the staff can see it.",
  "spraywall.problem.authorReported": "This route has been reported ({count}). The staff will look at it and close it.",
  "spraywall.problem.moderatorReported": "{count} open report(s) on this route. Handle them with the “Reports” button above the wall's list.",
  "spraywall.problem.authorNeedsReview": "Needs review — one of its holds is gone from the wall. Fix it in the editor so it can be logged again.",
  "spraywall.problem.authorPrivate": "Private — only you and the staff can see it.",
  "spraywall.problem.authorFriends": "Friends — you, your friends and the staff can see it.",
  "spraywall.problem.delete": "Delete",
  "spraywall.problem.deleteConfirmTitle": "Delete the route?",
  "spraywall.problem.deleteConfirmBody": "The route \"{name}\" will be deleted permanently.",
  "spraywall.problem.photoAlt": "{name} — the route's holds",
  "spraywall.log.sent": "Send: {name}",
  "spraywall.log.flashed": "Flash: {name}",
  "spraywall.log.project": "Attempt logged: {name}",
  "spraywall.log.repeat": "Repeat: {name}",
  "spraywall.log.failed": "Could not save the log. Please try again.",
  "spraywall.editor.problemTitleNew": "New route",
  "spraywall.editor.problemTitleEdit": "Edit route",
  "spraywall.editor.clear": "Clear",
  "spraywall.editor.fullscreen": "Full screen",
  "spraywall.editor.step.holds": "Holds",
  "spraywall.editor.step.start": "Start",
  "spraywall.editor.step.top": "Top",
  "spraywall.editor.step.data": "Details",
  "spraywall.editor.step.prev": "Back",
  "spraywall.editor.step.next": "Next",
  "spraywall.editor.step.save": "Save",
  "spraywall.editor.instruction.holds": "Tap the holds: 1× hand, 2× foot, 3× remove.",
  "spraywall.editor.instruction.start": "Tap 1–2 hand holds: these become the starts.",
  "spraywall.editor.instruction.top": "Tap 1–2 hand holds: these become the tops.",
  "spraywall.editor.chip.hands": "{count} hands",
  "spraywall.editor.chip.feet": "{count} feet",
  "spraywall.editor.preview.title": "How it will look in the list",
  "spraywall.editor.preview.noName": "(no name yet)",
  "spraywall.editor.counter.starts": "Start: {count} / 2",
  "spraywall.editor.counter.tops": "Top: {count} / 2",
  "spraywall.editor.hint.minHands": "At least 2 hand holds are needed (a start and a top).",
  "spraywall.editor.hint.maxHolds": "At most 60 holds.",
  "spraywall.editor.hint.needStart": "Pick at least 1 start.",
  "spraywall.editor.hint.needHandLeft": "At least one hand hold must stay for the top.",
  "spraywall.editor.hint.needTop": "Pick at least 1 top.",
  "spraywall.editor.hint.needGrade": "Pick a grade.",
  "spraywall.editor.maxStart": "At most 2 starts.",
  "spraywall.editor.maxTop": "At most 2 tops.",
  "spraywall.editor.noFeetWithFeet": "A no-feet route cannot have foot holds — remove them in step 1.",
  "spraywall.editor.holdNote": "Note: {note}",
  "spraywall.editor.field.name": "Name",
  "spraywall.editor.field.nameShuffle": "Another name",
  "spraywall.editor.field.grade": "Grade",
  "spraywall.editor.field.footRule": "Foot rule",
  "spraywall.editor.field.tags": "Tags (up to 5)",
  "spraywall.editor.field.description": "Description",
  "spraywall.editor.privateLocked": "Someone has already climbed, rated, commented on or listed it — only the staff can narrow who sees it now.",
  "spraywall.editor.field.visibility": "Who can see it?",
  "spraywall.editor.visibility.public": "Everyone",
  "spraywall.editor.visibility.friends": "Friends",
  "spraywall.editor.visibility.private": "Only me",
  "spraywall.editor.privateNotAuthor": "Only the setter can change the visibility.",
  "spraywall.editor.locked": "The hold set is locked — {count} sends already rely on it. Name, description, grade and tags can be edited. A different hold set = a new route.",
  "spraywall.editor.fixable": "{count} hold(s) to fix (?) — gone from the wall. Replace with another or drop; the other holds stay closed and the sends are kept. With a complete hold set the route is active again.",
  "spraywall.editor.reviewNotice": "Route to review: {count} hold(s) need fixing — gone from the wall, or foot-only. Tap a highlighted hold: it is removed or becomes a foot. Then save, and the route is active again.",
  "spraywall.editor.reviewPending": "{count} hold(s) still to fix — saved like this, it stays under review.",
  "spraywall.editor.reviewFixed": "All holds fixed — after saving, the route is active again.",
  "spraywall.editor.staffEditNote": "After saving, the route view shows \"Edited by staff\".",
  "spraywall.editor.wallUpdated": "The wall was updated in the meantime",
  "spraywall.editor.discardTitle": "Discard your changes?",
  "spraywall.editor.discardBody": "Unsaved changes will be lost.",
  "spraywall.editor.discardConfirm": "Discard",
  "spraywall.journal.tile.sends": "Sends",
  "spraywall.journal.tile.flashes": "Flashes",
  "spraywall.journal.tile.days": "Spray days",
  "spraywall.journal.gradeDist": "Grade distribution",
  "spraywall.journal.gradeDistHint": "sends",
  "spraywall.journal.sum.sends": "{count} sends",
  "spraywall.journal.sum.repeats": "{count} repeats",
  "spraywall.journal.sum.projects": "{count} projects",
  "spraywall.journal.deletedProblem": "deleted route",
  "spraywall.journal.unavailableProblem": "unavailable route",
  "spraywall.journal.unavailable": "not available",
  "spraywall.journal.deleteWholeLog": "Delete the whole log of \"{name}\"",
  "spraywall.journal.loadFailed": "The log could not be loaded.",
  "stats.sprayBox.title": "Spraywall",
  "stats.sprayBox.openProjects": "Open projects ({count})",
  "stats.sprayBox.journalBtn": "Full spray journal · {count} entries",
  "stats.sprayBox.journalTitle": "Spray journal",
  "stats.sprayBox.journalSub": "All walls · {count} entries",
  "stats.sprayBox.perGym": "By gym",
  "stats.sprayBox.gymLine": "{sends} sends · {days} days",
  "stats.sprayBox.openGymLog": "Journal",
  "spraywall.rating.title": "How is this route?",
  "spraywall.rating.hint": "Tap a star.",
  "spraywall.rating.hintRated": "Your rating: {count}/5. Tap the same star to take it back.",
  "spraywall.rating.group": "Rating of the route",
  "spraywall.rating.star": "{count}/5 stars",
  "spraywall.rating.failed": "Could not save the rating. Try again.",
  "spraywall.climbers.title": "Who climbed it",
  "spraywall.climbers.you": "You",
  "spraywall.climbers.flash": "⚡ Flash",
  "spraywall.climbers.attempts": "{count} attempts",
  "spraywall.climbers.repeats": "{count} repeats",
  "spraywall.climbers.empty": "Nobody has sent it yet.",
  "spraywall.climbers.more": "Show more",
  "spraywall.climbers.unknown": "Unknown member",
  "spraywall.climbers.loadFailed": "Could not load the list.",
  "spraywall.comments.title": "Comments",
  "spraywall.comments.placeholder": "Write a comment…",
  "spraywall.comments.send": "Post",
  "spraywall.comments.empty": "No comments yet.",
  "spraywall.comments.more": "Show more",
  "spraywall.comments.delete": "Delete comment",
  "spraywall.comments.deleteConfirm": "Delete this comment?",
  "spraywall.comments.loadFailed": "Could not load the comments.",
  "spraywall.report.button": "Report",
  "spraywall.report.title": "Report a route",
  "spraywall.report.subtitle": "{name} · only the staff sees the report.",
  "spraywall.report.reasonLabel": "Reason for the report",
  "spraywall.report.reason.wrongProblem": "Wrong route",
  "spraywall.report.reason.wrongProblemHint": "wrong holds, a very different grade, spam",
  "spraywall.report.reason.offensive": "Offensive content",
  "spraywall.report.reason.other": "Other",
  "spraywall.report.notePlaceholder": "Note (optional)",
  "spraywall.report.submit": "Submit report",
  "spraywall.report.already": "You have already reported this. The staff will take a look. If they close it, you can report it again.",
  "spraywall.report.noteTooLong": "The note can be at most 300 characters.",
  "spraywall.moderation.edit": "Edit",
  "spraywall.moderation.benchmark": "Benchmark",
  "spraywall.moderation.hideProblem": "Hide the route",
  "spraywall.moderation.unhideProblem": "Show it again",
  "spraywall.moderation.deleteProblem": "Delete the route",
  "spraywall.moderation.deleteConfirm": "Delete this route? Its sends, comments, ratings and reports are deleted too. This cannot be undone.",
  "spraywall.moderation.unhideNeedsReview": "Restored, but one of its holds is missing, so it needs review",
  "spraywall.moderation.resolve": "Fine, close it",
  "spraywall.moderation.reports": "Reports ({count})",
  "spraywall.moderation.needsReview": "To review ({count})",
  "spraywall.moderation.reportsTitle": "Reports",
  "spraywall.moderation.reportCount": "{count} open",
  "spraywall.moderation.reportsEmpty": "There are no open reports.",
  "spraywall.moderation.reportsLoadFailed": "Could not load the reports.",
  "spraywall.moderation.deletedProblem": "Deleted route",
  "spraywall.moderation.openProblem": "Open the route",
  "spraywall.lists.official": "Official",
  "spraywall.lists.mine": "My lists",
  "spraywall.lists.gym": "The gym's lists",
  "spraywall.lists.friends": "Friends' lists",
  "spraywall.lists.new": "+ New list",
  "spraywall.lists.newTitle": "New list",
  "spraywall.lists.editTitle": "Edit list",
  "spraywall.lists.name": "Name",
  "spraywall.lists.namePlaceholder": "List name",
  "spraywall.lists.circuit": "circuit",
  "spraywall.lists.circuitLabel": "Circuit",
  "spraywall.lists.circuitHint": "Climbed in order, numbered, with a \"Next\" button",
  "spraywall.lists.problemCount": "{count} routes",
  "spraywall.lists.officialHint": "Belongs to the gym — any wall manager can edit it, shown first in the tab",
  "spraywall.lists.officialNotOwn": "You can only make your own list official.",
  "spraywall.lists.officialNoCreator": "An official list without a creator cannot be demoted, only deleted.",
  "spraywall.lists.officialIsGym": "An official list belongs to the gym, so everyone sees it.",
  "spraywall.lists.visibility": "Visibility",
  "spraywall.lists.visibilityPrivate": "Private",
  "spraywall.lists.visibilityGym": "Gym",
  "spraywall.lists.visibilityFriends": "Friends",
  "spraywall.lists.lastModified": "Last modified by {name} · {date}",
  "spraywall.lists.delete": "Delete list",
  "spraywall.lists.deleteConfirm": "Delete the list \"{name}\"? The routes are not deleted.",
  "spraywall.lists.saveFailed": "Could not save the list. Try again.",
  "spraywall.lists.removeFromList": "Remove from list",
  "spraywall.lists.moveUp": "Move up",
  "spraywall.lists.moveDown": "Move down",
  "spraywall.lists.unavailable": "Unavailable",
  "spraywall.lists.full": "The list is full (at most 100 routes).",
  "spraywall.lists.emptyList": "This list has no routes yet.",
  "spraywall.lists.addTitle": "Add to a list",
  "spraywall.lists.addHint": "A private route cannot be added to a list.",
  "spraywall.lists.circuitPosition": "{pos} / {total}",
  "spraywall.lists.prev": "← Previous",
  "spraywall.lists.next": "Next →",
  "spraywall.lists.log": "Log",
  "spraywall.lists.circuitSkipHint": "\"Next\" skips unavailable or archived items.",
  "spraywall.lists.circuitEmpty": "This circuit has no available routes.",
  "spraywall.tag.crimpy": "crimpy",
  "spraywall.tag.slopey": "slopey",
  "spraywall.tag.pinchy": "pinchy",
  "spraywall.tag.pockety": "pockety",
  "spraywall.tag.juggy": "juggy",
  "spraywall.tag.compression": "compression",
  "spraywall.tag.dynamic": "dynamic",
  "spraywall.tag.technical": "technical",
  "spraywall.tag.powerful": "powerful",
  "spraywall.tag.endurance": "endurance",
  "spraywall.role.start": "start",
  "spraywall.role.hand": "hand",
  "spraywall.role.foot": "foot",
  "spraywall.role.finish": "top",
  "spraywall.error.notMember": "You are not a member of this gym.",
  "spraywall.error.banned": "You are banned from this gym.",
  "spraywall.error.missingCap": "You do not have permission to do this.",
  "spraywall.error.addonInactive": "The Spraywall add-on is not active in this gym.",
  "spraywall.error.invalidArgument": "Invalid request. Refresh the page and try again.",
  "spraywall.error.invalidMode": "This detection mode cannot be used on this version now.",
  "spraywall.error.invalidPhotoPath": "The uploaded photo was not found or is invalid. Upload it again.",
  "spraywall.error.invalidPoint": "The selected point is outside the photo.",
  "spraywall.error.invalidLabel": "The version name must be 1–40 characters.",
  "spraywall.error.invalidSize": "The photo size is invalid.",
  "spraywall.error.invalidProposalIdx": "The proposals changed — run the detection again.",
  "spraywall.error.invalidHoldId": "This hold no longer exists. Reload the editor.",
  "spraywall.error.invalidPoly": "The hold outline is invalid.",
  "spraywall.error.invalidOutline": "The wall outline is invalid or too small.",
  "spraywall.error.invalidTransform": "The photo alignment value is invalid.",
  "spraywall.error.refreshOnly": "This option can only be used in a re-detection.",
  "spraywall.error.versionNotFound": "The version was not found — it may have been discarded.",
  "spraywall.error.wallNotFound": "The wall was not found.",
  "spraywall.error.runNotFound": "The detection run was not found. Run it again.",
  "spraywall.error.runExists": "This detection run has already started.",
  "spraywall.error.versionExists": "This version already exists with a different photo. Try again.",
  "spraywall.error.versionNotEditable": "This version can no longer be edited.",
  "spraywall.error.versionNotEmpty": "A first detection needs an empty version — use the re-detection.",
  "spraywall.error.versionNotDraft": "This is only possible with a draft version.",
  "spraywall.error.versionEmpty": "A version without holds cannot be published.",
  "spraywall.error.wallDeleting": "The wall is being deleted.",
  "spraywall.error.draftExists": "This wall already has a draft.",
  "spraywall.error.runInProgress": "Detection is still running — wait for it, or try again in a few minutes",
  "spraywall.error.staleRun": "The holds changed in the meantime — run the detection again.",
  "spraywall.error.alreadyApproved": "This detection was already approved.",
  "spraywall.error.notDone": "The detection is not finished yet.",
  "spraywall.error.runExpired": "The detection has expired (older than 24 hours). Run it again.",
  "spraywall.error.tooManyHolds": "A wall can have at most 1500 holds.",
  "spraywall.error.photoGone": "The temporary photo has expired, upload it again",
  "spraywall.error.versionNotLive": "This version is not the live version.",
  "spraywall.error.versionNotArchived": "Only an archived version can be made live again.",
  "spraywall.error.no-active-model": "No active detection model — + Circle can be used",
  "spraywall.error.tooManyProposals": "The detection returned too many proposals (over 2000). Try a better photo.",
  "spraywall.error.internal": "The detection stopped with an internal error. Try again.",
  "spraywall.error.staleEdit": "The holds changed in the meantime — reload the editor.",
  "spraywall.error.emptyEdit": "Nothing to save.",
  "spraywall.error.holdMissing": "This hold is already missing and cannot be edited.",
  "spraywall.error.holdNotRemoved": "This hold is not deleted, so it cannot be restored.",
  "spraywall.error.invalidNote": "The note must be 1–40 characters.",
  "spraywall.error.rateLimit": "10 detections have already run on this wall within an hour — try again later.",
  "spraywall.error.photoTooLarge": "The image is too large (over 5 MB) — choose a smaller resolution.",
  "spraywall.error.photoUnreadable": "Your browser cannot open this image (e.g. the HEIC format). Save it as JPG or PNG and upload that.",
  "spraywall.error.invalidName": "The name must be 1–60 characters.",
  "spraywall.error.invalidDescription": "The description can be at most 500 characters.",
  "spraywall.error.invalidGrade": "Invalid grade.",
  "spraywall.error.invalidTags": "You can pick at most 5 tags from the list.",
  "spraywall.error.invalidFootRule": "Invalid foot rule.",
  "spraywall.error.invalidVisibility": "Invalid visibility.",
  "spraywall.error.holdCount": "A route needs 2–60 holds.",
  "spraywall.error.unknownHold": "A selected hold no longer exists on the wall. Reload the editor.",
  "spraywall.error.startCount": "1–2 starts are needed.",
  "spraywall.error.finishCount": "1–2 tops are needed.",
  "spraywall.error.feetOnlyRole": "A feet-only hold can only have the foot role.",
  "spraywall.error.noFeetRule": "A no-feet route cannot have foot holds.",
  "spraywall.error.duplicateHold": "A hold can appear only once.",
  "spraywall.error.missingHold": "A selected hold is gone from the wall. Replace or drop it.",
  "spraywall.error.notAuthor": "Only the setter or the staff can do this.",
  "spraywall.error.visibilityNotAuthor": "Only the setter can change the visibility.",
  "spraywall.error.problemNotFound": "The route was not found.",
  "spraywall.error.cannotCreate": "You cannot create routes on this wall right now.",
  "spraywall.error.locked": "The hold set is locked because it has sends. The other fields can be edited.",
  "spraywall.error.hasActivity": "Someone has already climbed, rated, commented on or listed it — only the staff can narrow who sees it now.",
  "spraywall.error.hasSends": "It already has sends, so it cannot be deleted. Ask the staff.",
  "spraywall.error.saveRateLimit": "You saved the route too often. Please try again later.",
  "spraywall.error.invalidText": "A comment must be 1–500 characters.",
  "spraywall.error.invalidReason": "Pick a reason from the list.",
  "spraywall.error.invalidAction": "This moderation action is not known. Refresh the page.",
  "spraywall.error.commentNotFound": "The comment no longer exists.",
  "spraywall.error.problemNotOpen": "This route is not open right now — you cannot comment on it or report it.",
  "spraywall.error.ownProblem": "You cannot report your own route.",
  "spraywall.error.notHidden": "This route is not hidden.",
  "spraywall.error.privateProblem": "A private or friends-level route cannot be made a benchmark.",
  "spraywall.error.friendsDisabled": "The friends feature is turned off in this gym.",
  "spraywall.error.alreadyReported": "You have already reported this route.",
  "spraywall.error.unknown": "Something went wrong. Please try again.",
  // Spraywall wall refresh (step 7): photo swap, matching review, archive
  "spraywall.editor.photoSwap": "Photo swap",
  "spraywall.editor.photoSwapUpload": "New photo…",
  "spraywall.editor.photoSwapBody": "A few holds were added or taken off, or you just want a better picture. The routes stay.",
  "spraywall.editor.photoSwapHint": "Take the photo from the same spot as the previous one.",
  "spraywall.editor.photoSwapReviewNote": "A route whose hold disappears will need review.",
  "spraywall.editor.newVersionBody": "The wall was re-set. A new wall version starts with an empty route list.",
  "spraywall.editor.newVersionArchive": "The current {count} routes are archived when it goes live — still browsable, but no longer loggable.",
  "spraywall.editor.newVersionArchiveAll": "The current routes are archived when it goes live — still browsable, but no longer loggable.",
  "spraywall.editor.newVersionConfirmTitle": "Start a new version?",
  "spraywall.editor.newVersionConfirmBody": "The current {count} routes are archived when it goes live. Until then members see the current wall.",
  "spraywall.editor.newVersionConfirmBodyAll": "The current routes are archived when it goes live. Until then members see the current wall.",
  "spraywall.editor.choose": "Start",
  "spraywall.editor.continue": "Continue",
  "spraywall.editor.versionBlockStats": "{holds} holds · {feetOnly} feet-only · {problems} routes",
  "spraywall.editor.versionBlockStatsDraft": "{holds} holds · {feetOnly} feet-only · no routes yet",
  "spraywall.editor.headerDraft": "+ draft",
  "spraywall.editor.publishSwap": "{draft} → live · {live} → archived",
  "spraywall.editor.publishArchive": "The {count} routes of the \"{label}\" version are archived: still browsable, but no longer loggable.",
  "spraywall.editor.publishArchiveAll": "The routes of the \"{label}\" version are archived: still browsable, but no longer loggable.",
  "spraywall.editor.publishHintLive": "Until you publish, members see the current wall.",
  "spraywall.editor.republish": "Make live again",
  "spraywall.editor.republishConfirmTitle": "Make the \"{label}\" version live again?",
  "spraywall.editor.republishConfirmBody": "Members will see this photo and these holds again. Its {count} routes can be logged again.",
  "spraywall.editor.republishConfirmBodyAll": "Members will see this photo and these holds again. Its routes can be logged again.",
  "spraywall.editor.republishArchive": "The current \"{label}\" version is archived. Its {count} routes stay browsable, but can no longer be logged.",
  "spraywall.editor.republishArchiveAll": "The current \"{label}\" version is archived. Its routes stay browsable, but can no longer be logged.",
  "spraywall.editor.republishArchiveNone": "The current \"{label}\" version is archived; it has no routes yet.",
  "spraywall.editor.republished": "Version made live again",
  "spraywall.editor.compWarning": "A spray challenge or competition that uses this wall's routes is running: {names}. The archived routes can no longer be logged afterwards.",
  "spraywall.holdUsage.open": "Which routes use it?",
  "spraywall.holdUsage.title": "Which routes use it?",
  "spraywall.holdUsage.subtitle": "Tap a hold on the photo.",
  "spraywall.holdUsage.pick": "Pick a hold on the photo.",
  "spraywall.holdUsage.none": "No route uses this hold.",
  "spraywall.holdUsage.count": "{count} routes use this hold",
  "spraywall.holdUsage.missingCount": "Missing hold — still used by {count} routes",
  "spraywall.holdUsage.missingHint": "Once you fix these, the hold disappears from the wall by itself.",
  "spraywall.holdUsage.privateName": "Private route",
  "spraywall.holdUsage.loadFailed": "Could not load the routes.",
  "spraywall.editor.photoSwapDraftBlocked": "Not available while a draft exists — publish or discard the draft first.",
  "spraywall.editor.archivedVersions": "Archived versions ({count})",
  "spraywall.editor.versionArchived": "archived",
  "spraywall.editor.archivedAt": "Archived: {date}",
  "spraywall.review.photoSwapTitle": "Match the new photo",
  "spraywall.review.chipMatched": "Matched",
  "spraywall.review.chipNew": "New hold",
  "spraywall.review.chipDropped": "Taken off?",
  "spraywall.review.chipKept": "Hand-marked, kept",
  "spraywall.review.droppedNote": "A \"taken off?\" hold is deleted on approval — if a route uses it, it becomes \"missing\" and the route goes to review.",
  "spraywall.review.align": "Manual alignment (if the estimate is off)",
  "spraywall.review.alignFailed": "The automatic alignment failed — align the old holds to the new photo by hand, then press Match again.",
  "spraywall.review.alignShiftX": "Shift X",
  "spraywall.review.alignShiftY": "Shift Y",
  "spraywall.review.alignScale": "Zoom",
  "spraywall.review.alignRotate": "Rotation",
  "spraywall.review.alignReset": "Reset to the estimate",
  "spraywall.review.rematch": "Match again",
  "spraywall.review.photoSwapConfirmTitle": "Approve the photo swap?",
  "spraywall.review.photoSwapConfirmBody": "Members will now see the new photo. Sends and counters stay.",
  "spraywall.archive.noProblems": "There are no routes in this archived version.",
  "spraywall.archive.loadFailed": "The archived versions could not be loaded.",

  // ── Permission system: platform capability labels ──
  "caps.platform.gym.create.label": "Create gym",
  "caps.platform.gym.create.desc": "Create new gyms on the platform",
  "caps.platform.gym.activate.label": "Activate gym",
  "caps.platform.gym.activate.desc": "Toggle gym active/inactive status",
  "caps.platform.gym.delete.label": "Delete gym",
  "caps.platform.gym.delete.desc": "Permanently delete gyms",
  "caps.platform.gym.editPlan.label": "Edit plan",
  "caps.platform.gym.editPlan.desc": "Change gym plan (free/pro/competition)",
  "caps.platform.gym.editFeatures.label": "Edit features",
  "caps.platform.gym.editFeatures.desc": "Override feature flags",
  "caps.platform.gym.editBilling.label": "Edit billing",
  "caps.platform.gym.editBilling.desc": "MAU band, billing overrides",
  "caps.platform.gym.assignAdmin.label": "Assign gym admin",
  "caps.platform.gym.assignAdmin.desc": "Add gym admin by email",
  "caps.platform.gym.adminAny.label": "Admin any gym",
  "caps.platform.gym.adminAny.desc": "Full admin access to every gym",
  "caps.platform.stats.view.label": "View platform stats",
  "caps.platform.stats.view.desc": "Platform-wide statistics and charts",
  "caps.platform.pricing.edit.label": "Edit pricing",
  "caps.platform.pricing.edit.desc": "Tier prices and MAU bands",
  "caps.platform.settings.edit.label": "Edit platform settings",
  "caps.platform.settings.edit.desc": "Accent color, map toggle, grade scale",
  "caps.platform.user.editCaps.label": "Edit user caps",
  "caps.platform.user.editCaps.desc": "Edit platform-level permissions",
  "caps.platform.viewAuditLog.label": "View audit log",
  "caps.platform.viewAuditLog.desc": "View platform-level audit events (permission changes, future admin events)",
  "caps.platform.recap.manage.label": "Manage recaps",
  "caps.platform.recap.manage.desc": "Monthly recap configuration and test emails",
  "caps.platform.crashLogs.view.label": "View crash logs",
  "caps.platform.crashLogs.view.desc": "Crash logs and digest email config",
  "caps.platform.viewMarketData.label": "Market data",
  "caps.platform.viewMarketData.desc": "View monthly anonymized market snapshots",
  "caps.platform.users.view.label": "View users",
  "caps.platform.users.view.desc": "Browse the platform-wide user directory and open user profiles",
  "caps.platform.users.ban.label": "Ban users",
  "caps.platform.users.ban.desc": "Ban or unban a user across the whole platform",
  "caps.platform.mcp.use.label": "AI connection (platform)",
  "caps.platform.mcp.use.desc": "Use the Sendly MCP server's platform branch: read every gym regardless of add-on status, plus the platform tools",
  "caps.platform.gym.editAddons.label": "Manage add-ons",
  "caps.platform.gym.editAddons.desc": "Turn a paid add-on (e.g. MCP) on or off for a gym, and view the add-on log",

  // ── Permission system: UI strings ──
  "permissions.modalTitle": "Permissions",
  "permissions.requires": "Requires: {caps}",
  "permissions.cannotGrant": "You cannot grant a capability you don't have",
  "permissions.save": "Save",
  "permissions.cancel": "Cancel",

  // ── Audit log (unified: permission changes + membership events) ──
  "audit.title": "Audit log",
  "audit.filter.all": "All",
  "audit.filter.permission": "Permission",
  "audit.filter.membership": "Membership",
  "audit.filter.routes": "Routes",
  "audit.entry.permissionChange": "Permission change",
  "audit.entry.routeCreate": "Route created",
  "audit.entry.routeUpdate": "Route updated",
  "audit.entry.routeDelete": "Route deleted",
  "audit.entry.routeUnnamed": "(unnamed route)",
  "audit.entry.memberJoin": "Joined",
  "audit.entry.memberKick": "Removed",
  "audit.entry.memberBan": "Banned",
  "audit.entry.memberUnban": "Unbanned",
  "audit.entry.memberLeave": "Left",
  "audit.entry.memberAccountDeleted": "Account deleted",
  "audit.entry.selfDelete": "Account deleted",
  "audit.entry.nameReset": "Name reset",
  "audit.entry.nameUnban": "Removed from banlist",
  "audit.entry.platformBan": "Platform ban",
  "audit.entry.platformUnban": "Platform unban",
  "audit.entry.gymCreate": "Gym created (via AI)",
  "audit.entry.gymSetupUpdate": "Gym setup (via AI)",
  "audit.gymCreateAdmin": "Admin: {name}",
  "audit.gymSetupFields": "Changed: {fields}",
  "audit.entry.deletedData": "Deleted: {ascents} ascents, {ratings} ratings",
  // Competition / event existence events in the gym log
  "audit.filter.programs": "Competitions & events",
  "audit.entry.competitionCreate": "Competition created",
  "audit.entry.competitionDelete": "Competition deleted",
  "audit.entry.eventCreate": "Event created",
  "audit.entry.eventDelete": "Event deleted",
  "audit.entry.unnamedEntity": "(unnamed)",
  "audit.filter.spray": "Spraywall",
  "audit.entry.sprayProblemDelete": "Spray route deleted",
  "audit.entry.sprayProblemEdit": "Spray route edited",
  "audit.entry.sprayProblemHide": "Spray route hidden",
  "audit.entry.sprayProblemUnhide": "Spray route restored",
  "audit.entry.sprayBenchmark": "Made benchmark",
  "audit.entry.sprayUnbenchmark": "Benchmark removed",
  "audit.entry.sprayWallDelete": "Spray wall deleted",
  "audit.entry.sprayWallPublish": "Spray wall published",
  "audit.entry.sprayWallRepublish": "Old version made live again",
  "audit.entry.sprayWallDiscard": "Spray draft discarded",
  "audit.spray.problemMeta": "Author: {author} · Wall: {wall}",
  "audit.spray.problemMetaNoWall": "Author: {author}",
  "audit.spray.version": "Version: {label}",
  "audit.spray.field.footRule": "Foot rule",
  "audit.spray.field.tags": "Tags",
  "audit.spray.descriptionChanged": "Description changed",
  "audit.spray.holdsChanged": "Holds changed",
  // Route change diff chips: both the field name and the value are translated —
  // the log is read by gym admins, not by whoever named the Firestore fields.
  "audit.field.name": "Name",
  "audit.field.difficulty": "Difficulty",
  "audit.field.holdColor": "Hold color",
  "audit.field.active": "Status",
  "audit.field.discipline": "Discipline",
  "audit.value.active": "Active",
  "audit.value.archived": "Archived",

  // ── Organiser operations log (competition + event) ──
  "events.editor.tabLog": "Log",
  "events.editor.tabLog.short": "Log",
  "opsLog.tabJudge": "Judging",
  "opsLog.tabOps": "Organising",
  "opsLog.empty": "No entries yet.",
  "opsLog.emptyFiltered": "No entries match this filter.",
  "opsLog.denied": "You do not have access to this log.",
  "opsLog.loadMore": "Show more",
  "opsLog.filterAuthor": "All editors",
  "opsLog.systemActor": "System",
  "opsLog.unknownActor": "Unknown",
  "opsLog.bulkNote": "Affected {n} entrants in one operation",
  "opsLog.filter.all": "All",
  "opsLog.filter.money": "Payment",
  "opsLog.filter.role": "Role",
  "opsLog.filter.roster": "Registrations",
  "opsLog.filter.setup": "Setup",
  "opsLog.cascade.memberRemoved": "Because the member was removed",
  "opsLog.cascade.memberKicked": "The member was kicked from the gym",
  "opsLog.cascade.memberBanned": "The member was banned from the gym",
  "opsLog.cascade.platformBanned": "The user was banned platform-wide",
  "opsLog.cascade.memberLeft": "The member left the gym",
  "opsLog.cascade.accountDeleted": "The user deleted their account",
  "opsLog.type.comp.activate": "Activated",
  "opsLog.type.comp.close": "Closed",
  "opsLog.type.comp.reopen": "Close reverted",
  "opsLog.type.comp.settings": "Settings",
  "opsLog.type.comp.attemptsLock": "Recording locked",
  "opsLog.type.comp.tvToken": "TV token",
  "opsLog.type.group.create": "Group created",
  "opsLog.type.group.update": "Group changed",
  "opsLog.type.group.delete": "Group deleted",
  "opsLog.type.session.create": "Session created",
  "opsLog.type.session.update": "Session changed",
  "opsLog.type.session.delete": "Session deleted",
  "opsLog.type.session.start": "Session started",
  "opsLog.type.session.pause": "Session paused",
  "opsLog.type.session.resume": "Session resumed",
  "opsLog.type.session.finish": "Session finished",
  "opsLog.type.session.restart": "Session restarted",
  "opsLog.type.session.attemptsLock": "Session locked",
  "opsLog.type.session.finalsPhase": "Finals phase",
  "opsLog.type.reg.join": "Registered",
  "opsLog.type.reg.leave": "Registration removed",
  "opsLog.type.reg.role": "Role",
  "opsLog.type.reg.paid": "Payment",
  "opsLog.type.reg.move": "Reassigned",
  "opsLog.type.reg.submit": "Results submitted",
  "opsLog.type.reg.startNumber": "Start number",
  "opsLog.type.reg.edit": "Details changed",
  "opsLog.type.guest.create": "Guest added",
  "opsLog.type.guest.update": "Guest changed",
  "opsLog.type.guest.delete": "Guest removed",
  "opsLog.type.event.publish": "Published",
  "opsLog.type.event.close": "Closed",
  "opsLog.type.event.cancel": "Cancelled",
  "opsLog.type.event.settings": "Settings",
  "opsLog.type.event.organizers": "Organizers",
  "opsLog.type.part.join": "Signed up",
  "opsLog.type.part.cancel": "Cancelled",
  "opsLog.type.part.rejoin": "Signed up again",
  "opsLog.type.part.delete": "Participant removed",
  "opsLog.type.part.paid": "Payment",
  "opsLog.type.part.move": "Reassigned",
  "opsLog.type.part.edit": "Details changed",
  "opsLog.type.waitlist.join": "Waitlisted",
  "opsLog.type.waitlist.promote": "Promoted from waitlist",
  "opsLog.type.waitlist.decline": "Waitlist declined",
  "opsLog.type.waitlist.leave": "Left the waitlist",
  "opsLog.type.waitlist.edit": "Waitlist changed",
  "opsLog.field.name": "Name",
  "opsLog.field.status": "Status",
  "opsLog.field.type": "Type",
  "opsLog.field.settings": "Settings changed",
  "opsLog.field.rules": "Rules changed",
  "opsLog.field.description": "Short description changed",
  "opsLog.field.longDescription": "Long description changed",
  "opsLog.field.startDate": "Start",
  "opsLog.field.endDate": "End",
  "opsLog.field.registrationStart": "Registration opens",
  "opsLog.field.registrationDeadline": "Registration deadline",
  "opsLog.field.registrationClosed": "Registration closed",
  "opsLog.field.guestMode": "Guest mode",
  "opsLog.field.allowGuests": "Guests",
  "opsLog.field.proxyGuestLimit": "Guests per person",
  "opsLog.field.discipline": "Discipline",
  "opsLog.field.externalUrl": "External link",
  "opsLog.field.coverImageUrl": "Cover image",
  "opsLog.field.organizerContact": "Organiser contact changed",
  "opsLog.field.attemptsLocked": "Recording locked",
  "opsLog.field.tvToken": "TV token",
  "opsLog.field.participation": "Participation",
  "opsLog.field.excludedUsers": "Excluded climbers changed",
  "opsLog.field.excludedRoutes": "Excluded routes changed",
  "opsLog.field.entryFeePaid": "Paid",
  "opsLog.field.paidAmount": "Amount paid",
  "opsLog.field.paymentMethod": "Payment method",
  "opsLog.field.role": "Role",
  "opsLog.field.isOrganizer": "Organizer",
  "opsLog.field.groupId": "Group",
  "opsLog.field.sessionId": "Session",
  "opsLog.field.sessionIds": "Judge sessions changed",
  "opsLog.field.submissionStatus": "Submission",
  "opsLog.field.startNumber": "Start number",
  "opsLog.field.displayName": "Name",
  "opsLog.field.birthYear": "Birth year",
  "opsLog.field.club": "Club",
  "opsLog.field.notes": "Notes",
  "opsLog.field.mode": "Mode",
  "opsLog.field.order": "Order",
  "opsLog.field.color": "Colour",
  "opsLog.field.entryFeeAmount": "Entry fee",
  "opsLog.field.maxAttempts": "Max attempts",
  "opsLog.field.ageMin": "Min age",
  "opsLog.field.ageMax": "Max age",
  "opsLog.field.sortIndex": "Order",
  "opsLog.field.maxDifficulty": "Hardest level changed",
  "opsLog.field.groupIds": "Groups changed",
  "opsLog.field.startTime": "Start time",
  "opsLog.field.durationMinutes": "Duration (min)",
  "opsLog.field.maxParticipants": "Max participants",
  "opsLog.field.finals.phase": "Finals phase",
  "opsLog.field.capacity": "Capacity",
  "opsLog.field.waitlistEnabled": "Waitlist",
  "opsLog.field.autoPromoteFromWaitlist": "Auto promotion",
  "opsLog.field.entryFee": "Entry fee changed",
  "opsLog.field.schedule": "Schedule changed",
  "opsLog.field.coaches": "Coaches changed",
  "opsLog.field.organizers": "Organizers changed",
  "opsLog.field.groups": "Groups changed",
  "opsLog.field.ageGroups": "Age groups changed",
  "opsLog.field.fullyPaid": "Fully paid",
  "opsLog.field.paidAt": "Paid at",
  "opsLog.field.paymentNote": "Payment note changed",
  "opsLog.field.allergies": "Allergies changed",
  "opsLog.field.medicalNotes": "Medical notes changed",
  "opsLog.field.parentName": "Parent name changed",
  "opsLog.field.parentPhone": "Parent phone changed",
  "opsLog.field.parentEmail": "Parent email changed",
  "opsLog.field.parentalConsent": "Parental consent",
  "admin.auditLogEmpty": "No entries in the log yet.",
  "admin.auditLogError": "Could not load the log. You may not have permission to view it.",
  "admin.auditLogLoadMore": "Load more entries",
  "platformAdmin.auditLogTitle": "Platform audit log",
  "platformAdmin.auditLogEmpty": "No platform audit entries yet.",
  "platformAdmin.billingTitle": "Quarterly billing",
  "platformAdmin.billingSub": "Email + CSV summary at the start of each quarter",
  "platformAdmin.billingScheduleTitle": "Schedule",
  "platformAdmin.billingScheduleBody": "The quarterly billing summary is sent on the first day of each new quarter (January 1, April 1, July 1, October 1) at 08:00 Europe/Budapest, to the recipients listed below. The email contains a per-gym summary table plus a detailed CSV attachment (company, tax number, address, amount due).",
  "platformAdmin.billingRecipientsTitle": "Recipients",
  "platformAdmin.billingRecipientsHint": "The quarterly summary goes ONLY to the addresses listed here — platform admins do not receive it automatically. The label is shown on this screen only, never in the email. Up to {max} addresses.",
  "platformAdmin.billingRecipientsEmpty": "There are no recipients, so the quarterly summary will not be sent to anyone.",
  "platformAdmin.billingRecipientsEmailPlaceholder": "email@example.com",
  "platformAdmin.billingRecipientsLabelPlaceholder": "Label (e.g. accountant)",
  "platformAdmin.billingRecipientsAdd": "Add",
  "platformAdmin.billingRecipientsRemove": "Remove recipient",
  "platformAdmin.billingRecipientsInvalid": "Invalid email address.",
  "platformAdmin.billingRecipientsLimit": "The list can hold at most {max} recipients.",
  "platformAdmin.billingCoverageTitle": "Billing-data coverage",
  "platformAdmin.billingCoverageReady": "Billable",
  "platformAdmin.billingCoverageMissing": "Missing",
  "platformAdmin.billingCoverageTotal": "Total gyms",
  "platformAdmin.billingCoverageHint": "Gym admins can fill in their company name and tax number under Settings → Subscription & Billing. The quarterly email still sends — missing rows are marked with a red MISSING tag.",
  "platformAdmin.billingTestTitle": "Send a test email",
  "platformAdmin.billingTestHint": "Sends a test summary with the current data computed for the PREVIOUS quarter, to you only. Does not affect the next real quarterly run.",
  "platformAdmin.billingTestButton": "Send test email to me",
  "platformAdmin.billingTestSending": "Sending…",
  "platformAdmin.billingTestSent": "Test sent to {email} — {quarter}, {gyms} billable gyms ({missing} missing, {zero} skipped at 0).",
  "platformAdmin.billingHistoryTitle": "Past quarterly runs",
  "platformAdmin.billingHistoryEmpty": "No quarterly runs yet.",
  "platformAdmin.billingHistoryRecipients": "{sent}/{total} recipients",
  "platformAdmin.billingHistoryGyms": "{gyms} gyms · {missing} missing",
  "platformAdmin.billingHistoryNoRecipients": "No recipients",
  "platformAdmin.billingHistoryManual": "Manual CSV — nothing sent",
  "platformAdmin.selfDeleteCascade": "{gyms} gyms · {ascents} ascents, {ratings} ratings, {friendships} friendships deleted",
  "audit.roleChange": "role",
  "audit.blocked": "blocked",

  // ── Member ban (admin-side) ──
  "ban.modal.title": "Ban member",
  "ban.modal.titleKick": "Kick member",
  "ban.modal.titlePlatform": "Platform ban",
  "ban.modal.actionLabel": "Action",
  "ban.modal.optKick": "Kick",
  "ban.modal.optBan": "Ban",
  "ban.modal.kickDesc": "Removes them from the gym and deletes their ascents/ratings here — but they can rejoin anytime.",
  "ban.modal.banDesc": "Removes them AND blocks re-entry (they see the reason). Their ascents/ratings here are deleted.",
  "ban.modal.warning": "This action will delete all of the member's ascents and ratings in this gym. Competition results are unaffected. The member will not be able to rejoin the gym.",
  "ban.modal.warningKick": "This removes the member from this gym and deletes their ascents and ratings here. Competition results are unaffected. They can rejoin the gym later.",
  "ban.modal.warningPlatform": "This blocks the user from the ENTIRE Sendly platform AND deletes their ascents and ratings in every gym. Competition results are unaffected. Their gym memberships are kept — unbanning restores access, but NOT the deleted data.",
  "ban.modal.confirmButtonKick": "Kick member",
  "ban.modal.reasonLabel": "Reason (required)",
  "ban.modal.reasonUserWillSee": "The member will see this reason on their ban screen.",
  "ban.modal.reasonPlaceholder": "E.g. Code of conduct violation, payment dispute, ...",
  "ban.modal.reasonTooShort": "Reason must be at least 3 characters",
  "ban.modal.reasonTooLong": "Reason must be at most 500 characters",
  "ban.modal.confirmLabel": "Type \"{word}\" to confirm",
  "ban.modal.confirmButton": "Ban member",
  "ban.modal.error.lastAdmin": "Cannot ban the last admin in the gym",
  "ban.modal.error.self": "You cannot ban yourself",
  "ban.modal.error.platformAdmin": "Platform admin cannot be banned",
  "ban.modal.error.generic": "An error occurred during banning",
  "ban.unban.confirm": "Unban {name}? Previously deleted ascents and ratings will NOT be restored.",
  "ban.button.label": "Ban",
  "ban.unban.button.label": "Unban",
  "ban.badge": "Banned",
  "ban.tooltip": "Banned: {date}\nReason: {reason}",

  // ── Banned user full-screen view ──
  "banned.title": "You are banned from this gym",
  "banned.reasonLabel": "Reason",
  "banned.dateLabel": "Banned on",
  "banned.contactHint": "If you think this is a mistake, contact the gym:",
  "banned.backToSelector": "Back to gym selector",
  "banned.platformTitle": "You are banned from Sendly",
  "banned.platformSub": "Your access to the Sendly platform has been suspended.",
  "banned.logout": "Log out",

  // ── Events (camps, workshops) ──
  "capCategory.events": "Events",
  "caps.events.manage.label": "Manage events",
  "caps.events.manage.desc": "Create, edit, delete events and manage participants",
  "caps.events.checkIn.label": "Attendance",
  "caps.events.checkIn.desc": "Record participant attendance",

  "events.tab": "Events",
  "events.tab.short": "Events",
  "events.sectionHeading": "Camps & Workshops",
  "events.competitionsHeading": "Competitions",
  "events.empty": "No events",
  "events.create": "+ New event",
  "events.filter.active": "Active",
  "events.filter.closed": "Closed",
  "events.filter.draft": "Draft",
  "events.filter.all": "All",
  "events.edit": "Edit",
  "events.delete": "Delete",
  "events.deleteConfirm": "Delete event \"{name}\" and all related data?",
  "events.past": "Past events",
  "events.manage": "Edit",
  "events.closeEditor": "Close",
  "events.checkInOpen": "Attendance",
  "events.publish": "Publish",
  "events.unpublish": "Unpublish",
  "events.unpublishConfirm": "Unpublish \"{name}\"? It will go back to draft.",
  "events.noEvents": "No events",
  "events.overview": "Overview",
  "events.menu.export": "Export",
  "events.menu.other": "Other",
  "events.template.copy.btnLabel": "Copy as template",
  "events.template.copy.downloadLabel": "Download as .json",
  "events.template.copy.copied": "Event template copied to clipboard",
  "events.template.copy.failed": "Could not build the template.",
  "events.template.paste.btnLabel": "📋 Paste from template",
  "events.template.paste.title": "Create event from template",
  "events.template.paste.intro": "Paste a template copied from another event. Settings, groups and the program come across — participants, coaches, organizers and registration deadlines do not.",
  "events.template.paste.nameLabel": "Name of the new event",
  "events.template.paste.startDateLabel": "Start date",
  "events.template.paste.startDateHint": "The end date and the program days follow from it ({n}-day event).",
  "events.template.paste.previewTitle": "What the template contains",
  "events.template.paste.previewCounts": "{groups} groups · {days} program days · {slots} time slots",
  "events.template.paste.previewSource": "Source: {gym}",
  "events.template.paste.previewExcluded": "Not copied: participants, coaches (not in the time slots either), organizers, the organizer contact, the cover image, the registration and cancellation deadlines. The event is created as a draft.",
  "events.template.paste.warningsTitle": "Warnings ({n})",
  "events.template.paste.warnUnknownGroupKey": "A time slot on program day {day} references an unknown group: {key} — skipped.",
  "events.template.paste.warnDuplicateGroupKey": "Duplicate group key: {key} — the second one got a new key.",
  "events.template.paste.warnGroupNoName": "Group #{n} has no name — skipped.",
  "events.template.paste.warnGroupsTruncated": "{n} groups over the limit — skipped.",
  "events.template.paste.warnDaysTruncated": "{n} program days over the limit — skipped.",
  "events.template.paste.warnSlotsTruncated": "{n} time slots over the limit on program day {day} — skipped.",
  "events.template.paste.createBtn": "Create",
  "events.template.paste.importing": "Creating…",
  "events.template.paste.doneTitle": "Event created",
  "events.template.paste.doneNextSteps": "The event is a draft. Set the registration deadlines and assign coaches to the time slots before publishing.",
  "events.template.paste.openEditor": "Open",
  "events.template.paste.errorImportFailed": "Something went wrong while creating: {message}",
  "events.template.paste.error_empty": "The field is empty — paste a template.",
  "events.template.paste.error_notJson": "The pasted text is not valid JSON.",
  "events.template.paste.error_notTemplate": "The pasted text is not a Sendly event template.",
  "events.template.paste.error_wrongKind": "This is not an event template. Paste a competition template in the Competitions panel.",
  "events.template.paste.error_futureVersion": "This template was made with a newer version. Update the app.",
  "events.template.paste.error_noContent": "The template has no event name.",

  // Event card
  "events.card.cancelled": "Cancelled",
  "events.card.kicker": "Event",
  "events.card.row.registration": "Registration",
  "events.card.row.fee": "Registration fee",
  "events.card.row.spotsValue": "{current} / {max} places taken",
  "events.card.manageBar": "Organizer",
  "events.feeStatus.paid": "{paid} settled",
  "events.feeStatus.partial": "{paid} / {total}",
  "events.feeStatus.unpaid": "{amount} — payment due",
  "events.card.unlisted": "Unlisted",

  // Editor tabs
  "events.editor.title": "Edit event",
  "events.editor.titleNew": "Create new event",
  "events.editor.tabBasic": "Basic info",
  "events.editor.tabBasic.short": "Basic",
  "events.editor.tabGroups": "Groups",
  "events.editor.tabGroups.short": "Groups",
  "events.editor.tabSchedule": "Schedule",
  "events.editor.tabSchedule.short": "Sched.",
  "events.editor.tabCoaches": "Coaches",
  "events.editor.tabCoaches.short": "Coaches",
  "events.editor.tabOrganizers": "Organizers",
  "events.editor.tabOrganizers.short": "Organiz.",
  "events.editor.tabSettings": "Settings",
  "events.editor.tabSettings.short": "Settings",
  "events.editor.tabParticipants": "Participants",
  "events.editor.tabParticipants.short": "Particip.",
  "events.editor.tabWaitlist": "Waitlist",
  "events.editor.tabWaitlist.short": "Wait.",
  "events.editor.tabAttendance": "Attendance",
  "events.editor.tabAttendance.short": "Attend.",
  "events.editor.name": "Event name",
  "events.editor.description": "Short description",
  "events.editor.descriptionHint": "One or two sentences — always visible on cards",
  "events.editor.longDescription": "Full description",
  "events.editor.longDescriptionHint": "The complete announcement — shown collapsed by default on cards",
  "events.editor.coverImage": "Cover image",
  "events.editor.coverHint": "Recommended: 1200×400 px · Max 5 MB · JPG, PNG, WebP",
  "events.editor.startDate": "Start date",
  "events.editor.endDate": "End date",
  "events.editor.dailyStartTime": "Daily start",
  "events.editor.dailyEndTime": "Daily end",
  "events.editor.registrationStart": "Registration start",
  "events.editor.registrationDeadline": "Registration deadline",
  "events.editor.cancellationDeadline": "Cancellation deadline",
  "events.editor.saved": "Saved!",
  "events.editor.endBeforeStart": "End date cannot be before start date",
  "events.editor.nameRequired": "Name is required",

  // Groups tab
  "events.groups.add": "+ Group",
  "events.groupCount": "{n} groups",
  "events.groups.name": "Group name",
  "events.groups.max": "Max capacity",
  "events.groups.ageMin": "Age min.",
  "events.groups.ageMax": "Age max.",
  "events.groups.description": "Description (optional)",
  "events.groups.totalCapacity": "Total: {count} spots",
  "events.groups.empty": "No groups yet. Create at least one.",
  "events.groups.deleteConfirm": "Delete this group? {count} participants are in it — they will be left unassigned.",
  "events.groups.hasRegistrants": "({count} registered)",

  // Schedule tab
  "events.schedule.generateDays": "Generate days",
  "events.schedule.generateConfirm": "Create new day blocks based on the date range? (Existing days are preserved.)",
  "events.schedule.addSlot": "+ Time slot",
  "events.schedule.slotStart": "Start",
  "events.schedule.slotEnd": "End",
  "events.schedule.slotTitle": "Title",
  "events.schedule.slotDescription": "Description",
  "events.schedule.slotLocation": "Location",
  "events.schedule.slotCoaches": "Coaches",
  "events.schedule.slotGroups": "Groups",
  "events.schedule.allGroups": "Everyone",
  "events.schedule.empty": "No schedule yet. Set the dates and click 'Generate days'.",
  "events.schedule.dayTitle": "Day title",
  "events.schedule.dayDescription": "Day description",
  "events.schedule.deleteSlot": "Delete slot",
  "events.schedule.overlapWarning": "This slot overlaps with another",

  // Coaches tab
  "events.coaches.add": "+ Add coach",
  "events.coaches.role": "Role",
  "events.coaches.rolePlaceholder": "e.g. Head coach",
  "events.coaches.remove": "Remove",
  "events.coaches.empty": "No coaches assigned yet",
  "events.coaches.pickTitle": "Select coach",
  "events.organizers.add": "+ Add organizer",
  "events.organizers.remove": "Remove organizer",
  "events.organizers.empty": "No organizers assigned yet",
  "events.organizers.pickTitle": "Select organizer",

  // Settings tab
  "events.settings.entryFee": "Registration fee",
  "events.settings.entryFeeEnabled": "Registration fee enabled",
  "events.settings.entryFeeAmount": "Amount",
  "events.settings.entryFeeCurrency": "Currency",
  "events.settings.entryFeeNote": "Note",
  "events.settings.entryFeeNotePlaceholder": "e.g. Pay cash on site",
  "events.settings.allowGuests": "Allow guest participants (admin only)",
  "events.settings.allowProxy": "Allow proxy registration (parent registers child)",
  "events.settings.proxyLimit": "Proxy limit (per user)",
  "events.settings.requireMinorData": "Collect birth year and minor data",
  "events.settings.requireMinorDataHint": "Everyone gives a birth year and may note allergies. Parent details and consent are required only from someone under 18 in the year of the event.",
  "events.settings.waitlistEnabled": "Enable waitlist",
  "events.settings.autoPromoteFromWaitlist": "Auto-promote on cancellation",
  "events.settings.status": "Status",
  "events.settings.visibility": "Visibility",

  // Participants tab
  "events.participants.addUser": "+ Add participant",
  "events.participants.addGuest": "+ Add guest",
  "events.participants.empty": "No participants registered yet",
  "events.participants.alreadyRegistered": "Already registered",
  "events.participants.searchPlaceholder": "Search by name or email...",
  "events.participants.pickGroup": "Which group?",
  "events.participants.groupCapacity": "{used}/{max}",
  "events.participants.groupUnlimited": "{used}",
  "events.participants.groupFull": "Full",
  "events.participants.removeConfirm": "Remove this participant?",
  "events.participants.paymentPrompt": "Enter the paid amount:",
  "events.participants.guestBadge": "Guest",
  "events.participants.unassigned": "(No group)",
  "events.participants.remove": "Remove",

  // Guest modal
  "events.guest.name": "Name",
  "events.guest.save": "Add",

  // Waitlist tab
  "events.waitlist.empty": "No one on the waitlist",
  "events.waitlist.promote": "Promote",
  "events.waitlist.decline": "Decline",
  "events.waitlist.join": "Join waitlist",
  "events.waitlist.joined": "On waitlist",
  "events.waitlist.waitingSince": "Waiting since: {date}",
  "events.waitlist.promoteConfirm": "Promote {name} from the waitlist?",
  "events.waitlist.groupFullWarning": "This group is full. Promotion will exceed capacity.",
  "events.waitlist.declineConfirm": "Decline this waitlist entry?",

  // Attendance tab
  "events.attendance.present": "Present",
  "events.attendance.summary": "{present} / {total} present",
  "events.attendance.print": "Print attendance sheet",
  "events.attendance.noParticipants": "No participants for this day",
  "events.attendance.markAllPresent": "Mark all present",
  "events.attendance.markAllAbsent": "Mark all absent",
  "events.attendance.noDays": "Event has no days",
  "events.attendance.saveFailed": "Could not save attendance",
  "events.attendance.pdfBirth": "Birth",
  "events.attendance.pdfParent": "Parent",
  "events.attendance.pdfPhone": "Phone",
  "events.attendance.pdfSignature": "Signature",

  // Registration
  "events.register": "Register",
  "events.registerChild": "+ Add guest",
  "events.cancel": "Cancel registration",
  "events.changeRegistration": "Change",
  "events.updateRegistration": "Update registration",
  "events.cancelConfirmTitle": "Cancel registration",
  "events.cancelConfirm": "Are you sure you want to cancel?",
  "events.cancelDeadlinePassed": "Cancellation deadline has passed",
  "events.unregister.paidBlock": "You cannot withdraw because there is a paid amount on record. Please contact the event organizer!",
  "events.groupFull": "This group is full",
  "events.regClosedTitle": "Registration closed",
  "events.regNotYetOpenTitle": "Registration is not yet open",
  "events.selectGroup": "Select a group",
  "events.freeSlots": "{count} spots available",
  "events.fullSlots": "Full",

  // Proxy
  "events.proxy.title": "Register a guest",
  "events.proxy.childName": "Guest name",
  "events.proxy.childNameMissing": "Enter the guest's name",
  "events.proxy.count": "{current} / {max} guests registered",
  "events.proxy.limit": "Limit reached ({max})",
  "events.proxy.consent": "I consent to the guest's participation (as a parent if a minor)",
  "events.proxy.consentRequired": "Consent is required",
  "events.proxy.myChildren": "My registered guests:",

  // Minor data
  "events.minor.birthYear": "Birth year",
  "events.minor.birthYearInvalid": "Enter a valid birth year ({min}–{max})",
  "events.minor.birthYearMissing": "Birth year is required",
  "events.age.tooYoung": "Too young for this age category",
  "events.age.tooOld": "Too old for this age category",
  "events.age.birthYearRequired": "Birth year is required for this age category",
  "events.age.birthYearMissingHint": "Set your birth year in settings to register for age-restricted categories",
  "events.minor.parentNameMissing": "Parent name is required",
  "events.minor.parentPhoneMissing": "Parent phone is required",
  "events.minor.allergies": "Known allergies",
  "events.minor.medicalNotes": "Medical notes",
  "events.minor.parentName": "Parent name",
  "events.minor.parentPhone": "Parent phone",
  "events.minor.parentEmail": "Parent email",
  "events.minor.participantData": "Participant details",
  "events.minor.birthYearHint": "This decides whether a guardian's consent is needed (under 18).",
  "events.minor.guardianTitle": "Parent or guardian details",
  "events.minor.guardianHint": "The participant turns {age} in the year of the event, so these are required.",
  "events.contactLoadFailed": "The previously entered details could not be loaded, so saving is switched off for now.",

  // Billing
  "events.billing.exportExcel": "Billing list (Excel)",
  "events.billing.exportPdf": "Billing list (PDF)",
  "events.billing.pdfHeading": "Payers ({count})",
  "events.billing.pdfPayer": "Payer",
  "events.billing.pdfPhone": "Phone",
  "events.billing.pdfPersons": "Persons",
  "events.billing.notInvoice": "This document is not an invoice.",
  "events.billing.xlsPaidFlag": "Paid (X)",
  "events.billing.participantsPaid": "{paid} / {total} participants paid",
  "events.billing.disabled": "Registration fee is not enabled for this event",
  "events.billing.unregisteredParent": "Unregistered parent",
  "events.billing.parentOnlyNoEdit": "This parent has no own registration, so payment can't be recorded here. Mark payment on the Participants tab.",
  "events.billing.totalExpected": "Expected",
  "events.billing.totalPaid": "Paid",
  "events.billing.outstanding": "Outstanding",
  "events.billing.filter.all": "All",
  "events.billing.filter.unpaid": "Unpaid",
  "events.billing.filter.partial": "Partial",
  "events.billing.filter.paid": "Paid",
  "events.editor.tabEntryFees": "Registration fees",
  "events.editor.tabEntryFees.short": "Fees",

  // Status / visibility
  "events.status.draft": "Draft",
  "events.status.published": "Published",
  "events.status.closed": "Closed",
  "events.status.cancelled": "Cancelled",
  "events.visibility.public": "Public",
  "events.visibility.unlisted": "Unlisted (link only)",
  "events.visibility.unlistedHint": "This event won't appear in the event list. Participants can access it via direct link.",
  "events.visibility.copyLink": "Copy link",
  "events.visibility.linkCopied": "Link copied!",
  "events.publishConfirm": "Publish this event? It will become visible to members.",
  "events.closeConfirm": "Close this event?",

  // Program view (read-only for users)
  "events.programTitle": "Schedule",
  "events.coachesTitle": "Coaches",
  "events.groupsTitle": "Groups",

  // ── Sendly MCP server — consent page (/oauth/authorize) ──
  // docs/spec/20-mcp-server.md "Consent page"
  "oauth.consent.notice": "{app} ({domain}) is requesting access to your Sendly account. The app can read the data of the gyms listed below — and of any gym that grants you this access later. Sendly never shares personal data (name, e-mail address, contact details). Whatever you share with the app is handled by its own provider under its own terms — responsibility for that lies with the gym. You can end the connection any time: Settings → Connected AI apps.",
  "oauth.consent.noticeWrite": "With the write permission(s) you ticked, the app can record special opening hours, and create competition, challenge, series and event drafts.",
  "oauth.consent.gymWriteCheckbox": "Allow write access for this gym (special opening hours, creating drafts)",
  "oauth.consent.platformWriteCheckbox": "Allow write access at platform level, across every gym (per the framework agreement)",
  "oauth.consent.gymsLabel": "Which gyms this applies to",
  "oauth.consent.platformAccess": "Every gym (platform access)",
  "oauth.consent.noGyms": "There is no gym this access would apply to.",
  "oauth.consent.emptyState": "You don't currently have AI-connection rights in any gym, and no platform access either, so there's nothing to authorize.",
  "oauth.consent.deny": "Cancel",
  "oauth.consent.approve": "Authorize",
  "oauth.consent.approving": "Authorizing…",
  "oauth.consent.error": "Couldn't process the access request. Check the link, or try again.",
  "oauth.consent.errorTitle": "Something went wrong",
  "oauth.consent.unknownApp": "Unknown app",

  // ── Settings → Connected AI apps (mcpApps) ──
  // docs/spec/13-settings.md "Connected AI apps (`mcpApps`)"
  "settings.mcpApps.title": "Connected AI apps",
  "settings.mcpApps.sub": "OAuth connections and personal tokens",
  "settings.mcpApps.grantsTitle": "Connected apps",
  "settings.mcpApps.grantsDesc": "The AI assistants and tools you've given access to your Sendly account.",
  "settings.mcpApps.patTitle": "Personal access tokens",
  "settings.mcpApps.patDesc": "For scripts, n8n, Claude Code — expire after 90 days.",
  "settings.mcpApps.noGrants": "You don't have any connected apps yet.",
  "settings.mcpApps.loadError": "Couldn't load the list. Try again.",
  "settings.mcpApps.unnamed": "Unnamed app",
  "settings.mcpApps.created": "Created",
  "settings.mcpApps.lastUsed": "Last used",
  "settings.mcpApps.neverUsed": "never",
  "settings.mcpApps.revoke": "Revoke",
  "settings.mcpApps.revokeConfirm": "Revoke {name}'s access? The connection ends immediately.",
  "settings.mcpApps.revokeError": "Revoking access failed. Try again.",
  "settings.mcpApps.newToken": "Create new token",
  "settings.mcpApps.tokenLabel": "Name",
  "settings.mcpApps.tokenLabelPlaceholder": "e.g. n8n workflow",
  "settings.mcpApps.scopesLabel": "Scopes",
  "settings.mcpApps.creating": "Creating…",
  "settings.mcpApps.create": "Create",
  "settings.mcpApps.patError": "Couldn't create the token. Try again.",
  "settings.mcpApps.tokenReadyFor": "The “{label}” token is ready",
  "settings.mcpApps.copyToken": "Copy",
  "settings.mcpApps.tokenCopied": "Token copied to clipboard",
  "settings.mcpApps.tokenCopyFailed": "Copy failed",
  "settings.mcpApps.tokenWarning": "This is the last time you'll see this token — save it somewhere safe.",

  // Admin panel — "AI connection" (AdminMcpConnection.jsx), docs/spec/14b-admin-sections.md
  "admin.mcp.title": "AI connection",
  "admin.mcp.statusActive": "Active",
  "admin.mcp.statusInactive": "Not active",
  "admin.mcp.interested": "I'm interested",
  "admin.mcp.inactiveHint": "This is a paid add-on. Reach out if you'd like it turned on for your gym.",
  "admin.mcp.legalNote": "The add-on operates under Annex 1 of the framework contract. The gym is responsible for the data handed to the AI application; Sendly never releases personal data through this surface. Every call is logged for 90 days — the log is below. The Sendly operators' own access also shows up here.",
  "admin.mcp.serverUrlLabel": "Server URL",
  "admin.mcp.copy": "Copy",
  "admin.mcp.copied": "Copied",
  "admin.mcp.guideTitle": "How to connect",
  "admin.mcp.guideClaudeTitle": "Claude",
  "admin.mcp.guideChatgptTitle": "ChatGPT",
  "admin.mcp.guide.claude.step1": "Open Claude Settings → Connectors.",
  "admin.mcp.guide.claude.step2": "Click “Add custom connector” and paste in the server URL above.",
  "admin.mcp.guide.claude.step3": "On first use, Claude redirects you to the Sendly sign-in and consent screen — pick the gym and the permissions you want to grant.",
  "admin.mcp.guide.claude.step4": "After that you can ask questions about your gym's data in plain language.",
  "admin.mcp.guide.chatgpt.step1": "Open ChatGPT Settings → Connectors.",
  "admin.mcp.guide.chatgpt.step2": "Add a new connector and paste in the server URL above.",
  "admin.mcp.guide.chatgpt.step3": "Follow the consent flow — sign in with your Sendly account and grant the permissions you want.",
  "admin.mcp.guide.chatgpt.step4": "After that you can ask questions about your gym's data in plain language.",
  "admin.mcp.writeToggleLabel": "Allow writes through AI",
  "admin.mcp.writeToggleHint": "Extraordinary opening hours, and competition / challenge / series / event drafts created through AI. Reading is always allowed.",
  "admin.mcp.writeToggleError": "Couldn't save the switch. Try again.",
  "admin.mcp.holdersTitle": "Who has access",
  "admin.mcp.holdersEmpty": "No one has AI connection access.",
  "admin.mcp.holdersRead": "Read",
  "admin.mcp.holdersWrite": "Write",
  "admin.mcp.writeCapRequired": "Changing this switch needs the \"AI connection: write\" permission.",
  "admin.mcp.usageTitle": "Usage log (30 days)",
  "admin.mcp.usageEmpty": "No calls logged yet.",
  "admin.mcp.usageLoadError": "Couldn't load the log.",
  "admin.mcp.usage.time": "Time",
  "admin.mcp.usage.user": "User",
  "admin.mcp.usage.client": "Client",
  "admin.mcp.usage.tool": "Tool",
  "admin.mcp.usage.status": "Status",
  "admin.mcp.usage.rows": "Rows",
  "admin.mcp.usage.ok": "OK",
  "admin.mcp.usage.failed": "Failed",
  "admin.mcp.usage.refused": "Refused",
  "admin.mcp.usage.platformActor": "Sendly operator",
  "admin.mcp.usage.unknownUser": "Unknown",

  // Add-ons — AdminSubscription "Add-ons" card, docs/spec/15b-plan-enforcement.md
  "addons.title": "Add-ons",
  "addons.active": "Active",
  "addons.inactive": "Not active",
  "addons.interested": "I'm interested",
  "admin.spraywall.title": "Spraywall",
  "admin.spraywall.statusActive": "Active",
  "admin.spraywall.statusInactive": "Not active",
  "admin.spraywall.upsellBody": "Put your spray wall photo on the app: the machine detects the holds, and climbers set and log routes on it. A paid add-on.",
  "admin.spraywall.deleteViaPlatform": "Walls cannot be deleted while the add-on is off. Ask the platform to delete existing walls and photos.",
  "admin.spraywall.openReports": "Open reports: {count}",
  "admin.spraywall.trainingConsentOn": "Training consent: granted since {since} ({contractRef})",
  "admin.spraywall.trainingConsentOff": "Training consent: none",
  "addons.noAddons": "No add-ons available right now.",

  // Organiser + gym log AI marker, docs/spec/06-competitions-judging.md + 14b-admin-sections.md
  "opsLog.viaMcp": "via AI ({client})",
  "opsLog.viaMcpNoClient": "via AI",

  // Early-bird discount — competitions and events say the same sentence,
  // docs/plans/early-bird-pricing.md + docs/spec/07c-competitions-editor.md
  "earlyBird.title": "Early-bird discount",
  "earlyBird.toggle": "Early-bird discount",
  "earlyBird.toggleHint": "Anyone who registers at least this many days before the start pays less.",
  "earlyBird.daysBefore": "days before the start",
  "earlyBird.daysBeforeAria": "Days before the start, tier {n}",
  "earlyBird.discountPct": "% off",
  "earlyBird.discountPctAria": "Discount in percent, tier {n}",
  "earlyBird.addTier": "+ Tier",
  "earlyBird.removeTier": "Remove tier {n}",
  "earlyBird.chipsLabel": "Quick add",
  "earlyBird.chip.week1": "1 week",
  "earlyBird.chip.week2": "2 weeks",
  "earlyBird.chip.month1": "1 month (30 days)",
  "earlyBird.until": "until {date}",
  "earlyBird.thenFull": "then {amount}",
  "earlyBird.discountUntil": "−{pct}% · until {date}",
  "earlyBird.fullPriceStruck": "Full price: {amount}",
  "earlyBird.ladderLabel": "Price steps",
  "earlyBird.cardEarly": "early price until {date}",
  "earlyBird.badge": "Early −{pct}%",
  "earlyBird.badgeManual": "Early −{pct}% (manual)",
  "earlyBird.badgeFullManual": "Full price (manual)",
  "earlyBird.freeSettled": "No fee ✓",
  "earlyBird.override.menu": "Fee category…",
  "earlyBird.override.title": "Fee category",
  "earlyBird.override.auto": "Automatic",
  "earlyBird.override.full": "Full price",
  "earlyBird.override.tier": "−{pct}% ({days} days)",
  "earlyBird.override.tierOne": "−{pct}% (1 day)",
  "earlyBird.override.hint": "Automatic works from the moment the entry was originally made. A manual choice stays until you set it back.",
  "earlyBird.override.save": "Save",
  "earlyBird.override.error": "Could not save the fee category.",
  "earlyBird.warn.beforeRegOpen": "This tier will never apply: its cutoff is before registration opens.",
  "earlyBird.warn.noStartDate": "The dates show once the start date is set. Without a start date no tier applies.",
  "earlyBird.warn.existingEntries": "Those who have already registered keep their earlier prices.",
  "earlyBird.warn.invalidRows": "Invalid or repeated row: it is left out when you save. Days must be 1 to 365, the discount 1 to 100, and each day can appear once.",
  "earlyBird.summaryDiscount": "Total discount",
  "earlyBird.excelColumn": "Discount",
  "earlyBird.opsLogFull": "full price",
  "earlyBird.opsLogManual": "{value} (manual)",
  "opsLog.field.earlyBird": "Early-bird discount",
};

export default en;
